/**
 * PolygonProcessor.ts
 *
 * Implements a flexible pipeline for processing polygons extracted from SAM masks.
 * Based on Python prototype with optimizations for TypeScript/JavaScript.
 *
 * Key features:
 * - Room orientation detection using minimum area bounding box
 * - Orthogonal straightening to align walls perpendicular to room axes
 * - Pixel-aligned edge refinement using density masks
 * - Multi-pass processing pipeline with configurable passes
 */

import { BoxClipper } from './segmentation/BoxClipper';
import { Polygon, Point2D, Point3D, createPolygon } from './segmentation/types';
import { PolygonProcessingPipeline } from './PolygonProcessingPipeline';
import { PassConfig, PipelineConfig } from './BasePipeline';
import { adjustEdgesForLinePrompts as adjustEdgesForLinePromptsImpl } from './LinePromptEdgeAdjuster';

// Re-export types for backward compatibility
export { createPolygon };
export type { Point2D, Point3D, Polygon };
export type { PassConfig, PipelineConfig };

/**
 * Debug visualization data for pixel alignment
 */
export interface PixelAlignmentDebugData {
  sampleLines: { start: Point2D; end: Point2D }[]; // Perpendicular scan lines
  peakPoints: Point2D[]; // Detected peak positions (selected)
  peakPointWeights: number[]; // Weight for each peak point (0-1, based on B value)
  peakRegions: { start: Point2D; end: Point2D }[]; // Above-threshold regions along scan lines
  fittedLines: { start: Point2D; end: Point2D }[]; // Fitted lines through peaks
  sampledPositionPoints: Point2D[]; // Points sampled from position map at peak locations
  densityFallbackPoints: Point2D[]; // Points found via density map fallback (when position map has no data)
}

/**
 * Debug visualization data for line prompt edge adjustment.
 * Each category is toggled independently via separate booleans in the pipeline config UI.
 * All coordinates are in pixel space.
 */
export interface LinePromptDebugData {
  /** Perpendicular rays cast from sample points, with hit points at end */
  rays: { start: Point2D; end: Point2D }[];
  /** Points where rays hit polygon edges */
  rayHits: Point2D[];
  /** All candidate edges that passed the minimum hit threshold */
  candidateEdges: { start: Point2D; end: Point2D }[];
  /** All chains built from candidate edges (non-selected shown differently) */
  allChains: { edges: { start: Point2D; end: Point2D }[]; selected: boolean }[];
  /** The selected chain edges in their original position (before reduction) */
  selectedChainOriginal: { start: Point2D; end: Point2D }[];
  /** The collapsed single edge after interior vertex removal */
  collapsedEdge: { start: Point2D; end: Point2D } | null;
  /** The seed edge (closest to prompt line, always in chain) */
  seedEdge: { start: Point2D; end: Point2D } | null;
  /** Start outer edges: edges hit by perpendicular rays from the prompt line start point (up to 2) */
  startOuterEdges: { start: Point2D; end: Point2D }[];
  /** End outer edges: edges hit by perpendicular rays from the prompt line end point (up to 2) */
  endOuterEdges: { start: Point2D; end: Point2D }[];
}

/**
 * Per-step debug toggle flags for line prompt edge adjustment.
 */
export interface LinePromptDebugFlags {
  debugRays: boolean;
  debugCandidateEdges: boolean;
  debugChains: boolean;
  debugSelectedChain: boolean;
  debugCollapsedEdge: boolean;
  debugSeed: boolean;
  debugStartOuterEdges: boolean;
  debugEndOuterEdges: boolean;
}

/**
 * Main polygon processor with room-aware straightening and pixel alignment
 */
export class PolygonProcessor {
  // Configuration (in centimeters)
  private polygonSimplification = 0.5; // cm
  private useOrthogonalStraightening = true;
  private force90Degree = false;
  private straighteningMode: 'pixel_aligned' | 'floorsam' | 'none' = 'pixel_aligned';

  // Room orientation (detected from polygons)
  private roomRotation = 0;

  // Density mask for pixel alignment (optional, always in pixel space)
  private densityMask: ImageData | null = null;

  // Position map for sampling actual point positions (optional, same pixel space as density mask)
  // Non-zero RGBA indicates a point exists at that pixel
  private positionMap: ImageData | null = null;

  // Prompt points for edge adjustment (optional, in pixel space)
  private promptPoints: Array<{
    x: number;
    y: number;
    label: number;
    type?: 'point' | 'box' | 'vertex' | 'line';
    box?: {
      worldCorners: Point3D[];
    };
    vertex?: {
      radius: number; // Radius in pixels
    };
    line?: {
      start: { x: number; y: number };
      end: { x: number; y: number };
    };
  }> = [];

  // World space scale (pixels per meter for conversion)
  private pixelsPerMeter = 1.0; // Default: 1 pixel = 1 meter

  // Pixel alignment hyperparameters (searchRange in cm, others unitless)
  private pixelAlignmentSearchRange = 3.0; // cm
  private pixelAlignmentMinProbeRatio = 0.7; // 70% of probes must find matching B-value

  // Pipeline instance
  private pipeline: PolygonProcessingPipeline;

  // Debug visualization data
  public pixelAlignmentDebug: PixelAlignmentDebugData | null = null;
  public linePromptDebug: LinePromptDebugData | null = null;

  // Config file path for pipeline
  private configPath: string;

  constructor(config?: {
    polygonSimplification?: number;
    useOrthogonalStraightening?: boolean;
    force90Degree?: boolean;
    straighteningMode?: 'pixel_aligned' | 'floorsam' | 'none';
    pixelAlignmentSearchRange?: number;
    pixelAlignmentMinProbeRatio?: number;
    configPath?: string;
    /** If true, skip auto-loading pipeline config from file (useful when config is provided via other means) */
    skipAutoLoad?: boolean;
  }) {
    // Set config path before Object.assign to allow override
    this.configPath = config?.configPath ?? '/config/segmentation/poly-pipeline-config.json';

    if (config) {
      const { configPath: _, skipAutoLoad: __, ...rest } = config;
      Object.assign(this, rest);
    }

    // Initialize pipeline with processor registry
    this.pipeline = this.buildPipeline();
    this.initializeProcessorRegistry();

    // Load pipeline from config file asynchronously (unless skipped)
    if (!config?.skipAutoLoad) {
      this.pipeline.loadFromFile(this.configPath).catch(() => {
        console.warn(`[PolygonProcessor] Failed to load config file from ${this.configPath}, will use default pipeline if needed`);
      });
    }
  }

  /**
   * Initialize the processor registry with all available processors
   * Registers with both the pipeline and local registry
   */
  private initializeProcessorRegistry(): void {
    this.pipeline.registerPolygonProcessor(
      'simplification',
      (polygon: Polygon, kwargs?: Record<string, unknown>) => {
        const { tolerance, relative } = kwargs as { tolerance: number; relative: boolean };
        return this.simplifyPolygon(polygon, tolerance, relative);
      },
      {
        conditionFunc: (p, s) => s && p.length > 20,
        defaultKwargs: {
          tolerance: 0.5, // When relative=false: cm. When relative=true: fraction of bounding box diagonal (0.5 = 0.5%)
          relative: false, // true = tolerance is % of bounding box diagonal, false = absolute cm
        },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'orthogonal_straightening',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        this.applyOrthogonalStraightening(
          polygon,
          (kwargs?.force90Degree as boolean | undefined) ?? false,
          (kwargs?.smallEdgeThreshold as number | undefined) ?? 1.5,
          0, // recursionDepth starts at 0
          (kwargs?.angleThreshold as number | undefined) ?? 45.0,
        ),
      {
        conditionFunc: (p, s) => s && p.length > 3 && this.useOrthogonalStraightening,
        defaultKwargs: { force90Degree: false, smallEdgeThreshold: 1.5, angleThreshold: 45.0 },
      },
    );

    this.pipeline.registerPolygonProcessor('remove_sharp_angles', (polygon: Polygon, kwargs?: Record<string, unknown>) => this.removeSharpAngles(polygon, kwargs?.minAngleDegrees as number), {
      conditionFunc: (p, s) => s && p.length > 3,
      defaultKwargs: { minAngleDegrees: 10.0 },
    });

    this.pipeline.registerPolygonProcessor('prompt_edge_adjustment', (polygons: Polygon[], _kwargs?: Record<string, unknown>) => this.adjustEdgesForPrompts(polygons), {
      batchProcessor: true,
      processesChildren: true,
      defaultKwargs: {},
    });

    this.pipeline.registerPolygonProcessor('box_prompt_edge_adjustment', (polygons: Polygon[]) => this.adjustEdgesForBoxPrompts(polygons), {
      batchProcessor: true,
      processesChildren: true,
      defaultKwargs: {},
    });

    this.pipeline.registerPolygonProcessor(
      'line_prompt_edge_adjustment',
      (polygons: Polygon[], kwargs?: Record<string, unknown>) =>
        this.adjustEdgesForLinePrompts(polygons, (kwargs?.raySampleCount as number) ?? 20, (kwargs?.maxRayDistanceCm as number) ?? 50, (kwargs?.mergeThresholdCm as number) ?? 3, (kwargs?.pullModeRatio as number) ?? 0.7, {
          debugRays: (kwargs?.debugRays as boolean) ?? false,
          debugCandidateEdges: (kwargs?.debugCandidateEdges as boolean) ?? false,
          debugChains: (kwargs?.debugChains as boolean) ?? false,
          debugSelectedChain: (kwargs?.debugSelectedChain as boolean) ?? false,
          debugCollapsedEdge: (kwargs?.debugCollapsedEdge as boolean) ?? false,
          debugSeed: (kwargs?.debugSeed as boolean) ?? false,
          debugStartOuterEdges: (kwargs?.debugStartOuterEdges as boolean) ?? false,
          debugEndOuterEdges: (kwargs?.debugEndOuterEdges as boolean) ?? false,
        }),
      {
        batchProcessor: true,
        processesChildren: true,
        defaultKwargs: {
          raySampleCount: 20,
          maxRayDistanceCm: 50,
          mergeThresholdCm: 3,
          pullModeRatio: 0.7,
          debugRays: false,
          debugCandidateEdges: false,
          debugChains: false,
          debugSelectedChain: false,
          debugCollapsedEdge: false,
          debugSeed: false,
          debugStartOuterEdges: false,
          debugEndOuterEdges: false,
        },
      },
    );

    this.pipeline.registerPolygonProcessor('remove_points_in_boxes', (polygons: Polygon[]) => this.removePointsInBoxes(polygons), {
      batchProcessor: true,
      processesChildren: false,
      defaultKwargs: {},
    });

    this.pipeline.registerPolygonProcessor('vertex_prompt_enforcement', (polygons: Polygon[], kwargs?: { extraSearchCm?: number }) => this.enforceVertexPrompts(polygons, kwargs), {
      batchProcessor: true,
      processesChildren: true,
      defaultKwargs: { extraSearchCm: 5 },
    });

    this.pipeline.registerPolygonProcessor('prompt_boolean_operations', (polygons: Polygon[]) => this.applyBooleanOperationsForPrompts(polygons), {
      batchProcessor: true,
      processesChildren: false,
      defaultKwargs: {},
    });

    this.pipeline.registerPolygonProcessor(
      'pixel_alignment',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        this.applyPixelAlignment(
          polygon,
          kwargs?.paddingCm as number,
          kwargs?.searchRange as number,
          (kwargs?.enableDebug as boolean) ?? false,
          (kwargs?.rotate as boolean) ?? true,
          kwargs?.maxIterations as number,
          kwargs?.minProbeRatio as number,
          (kwargs?.relative as boolean) ?? false,
        ),
      {
        conditionFunc: (p, s) => s && p.length > 3,
        defaultKwargs: {
          paddingCm: 7.0,
          searchRange: 3.0, // When relative=false: cm. When relative=true: % of bounding box diagonal
          enableDebug: false,
          rotate: true,
          maxIterations: 50,
          minProbeRatio: 0.7,
          relative: false, // true = searchRange is % of bounding box diagonal, false = absolute cm
        },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'recalculate_room_rotation',
      (polygon: Polygon) => {
        if (polygon.length > 0) {
          // Collect all edges with their angles and lengths
          const allEdges: Array<{ angle: number; length: number }> = [];
          for (let i = 0; i < polygon.length; i++) {
            const p1 = polygon[i];
            const p2 = polygon[(i + 1) % polygon.length];
            const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
            const length = Math.sqrt(edge.x * edge.x + edge.y * edge.y);
            const angle = Math.atan2(edge.y, edge.x);
            allEdges.push({ angle, length });
          }

          if (allEdges.length > 0) {
            // Normalize angles to [0, 2π) range
            const normalizedEdges = allEdges.map((e) => ({
              angle: e.angle >= 0 ? e.angle : e.angle + 2 * Math.PI,
              length: e.length,
            }));

            // Create histogram of angles (bin size ~5 degrees)
            const binSize = (5 * Math.PI) / 180;
            const numBins = Math.ceil((2 * Math.PI) / binSize);
            const histogram = new Array(numBins).fill(0);

            for (const edge of normalizedEdges) {
              const binIndex = Math.floor(edge.angle / binSize) % numBins;
              histogram[binIndex] += edge.length;
            }

            // Find peaks in histogram (local maxima that are significantly higher than neighbors)
            const peaks: Array<{ binIndex: number; weight: number; angle: number }> = [];
            const minPeakHeight = Math.max(...histogram) * 0.2; // At least 20% of max

            for (let i = 0; i < numBins; i++) {
              const prev = histogram[(i - 1 + numBins) % numBins];
              const curr = histogram[i];
              const next = histogram[(i + 1) % numBins];

              if (curr > prev && curr > next && curr > minPeakHeight) {
                peaks.push({
                  binIndex: i,
                  weight: curr,
                  angle: (i + 0.5) * binSize,
                });
              }
            }

            // Check if we have 4 perpendicular peaks
            if (peaks.length >= 4) {
              // Sort by weight
              peaks.sort((a, b) => b.weight - a.weight);

              // Take top 4 peaks and check if they're roughly 90° apart
              const top4 = peaks.slice(0, 4);
              top4.sort((a, b) => a.angle - b.angle);

              // Check if they form perpendicular directions
              const angle1 = top4[0].angle;
              const angle2 = top4[1].angle;
              const angle3 = top4[2].angle;
              const angle4 = top4[3].angle;

              const diff1 = Math.abs(angle2 - angle1 - Math.PI / 2);
              const diff2 = Math.abs(angle3 - angle2 - Math.PI / 2);
              const diff3 = Math.abs(angle4 - angle3 - Math.PI / 2);
              const diff4 = Math.abs(angle1 + 2 * Math.PI - angle4 - Math.PI / 2);

              const maxDiff = Math.max(diff1, diff2, diff3, diff4);
              const perpendicularThreshold = (15 * Math.PI) / 180; // 15 degrees tolerance

              if (maxDiff < perpendicularThreshold) {
                // Found 4 perpendicular spikes - use the strongest one
                this.roomRotation = top4[0].angle;
                return polygon;
              }
            }

            // Fallback: group similar angles and use strongest
            const angleThreshold = (10 * Math.PI) / 180;
            const angleGroups: Array<{ representativeAngle: number; totalWeight: number }> = [];

            for (const edge of normalizedEdges) {
              let foundGroup = false;
              for (const group of angleGroups) {
                const angleDiff = Math.abs(edge.angle - group.representativeAngle);
                const angleDiffWrapped = Math.min(angleDiff, 2 * Math.PI - angleDiff);

                if (angleDiffWrapped <= angleThreshold) {
                  const totalWeight = group.totalWeight + edge.length;
                  group.representativeAngle = (group.representativeAngle * group.totalWeight + edge.angle * edge.length) / totalWeight;
                  group.totalWeight = totalWeight;
                  foundGroup = true;
                  break;
                }
              }

              if (!foundGroup) {
                angleGroups.push({
                  representativeAngle: edge.angle,
                  totalWeight: edge.length,
                });
              }
            }

            if (angleGroups.length > 0) {
              const strongestGroup = angleGroups.reduce((max, curr) => (curr.totalWeight > max.totalWeight ? curr : max));
              this.roomRotation = strongestGroup.representativeAngle;
            }
          }
        }
        return polygon;
      },
      { conditionFunc: (p, s) => s && p.length > 3 },
    );

    this.pipeline.registerPolygonProcessor('weld_close_points', (polygon: Polygon, kwargs?: Record<string, unknown>) => this.weldClosePoints(polygon, kwargs?.minDistance as number), {
      conditionFunc: (p, s) => s && p.length > 3,
      defaultKwargs: { minDistance: 5.0 },
    });

    this.pipeline.registerPolygonProcessor('fix_degenerate_edges', (polygon: Polygon, kwargs?: Record<string, unknown>) => this.fixDegenerateEdges(polygon, kwargs?.minEdgeLength as number), {
      conditionFunc: (p, s) => p.length > 3,
      defaultKwargs: { minEdgeLength: 1.0 },
    });

    this.pipeline.registerPolygonProcessor('remove_collinear_points', (polygon: Polygon, kwargs?: Record<string, unknown>) => this.removeCollinearPoints(polygon, kwargs?.angleThreshold as number, kwargs?.lengthScaleFactor as number), {
      conditionFunc: (p, s) => p.length > 3,
      defaultKwargs: { angleThreshold: 2.0, lengthScaleFactor: 0.1 },
    });

    this.pipeline.registerPolygonProcessor('snap_small_segments', (polygon: Polygon, kwargs?: Record<string, unknown>) => this.snapSmallSegments(polygon, kwargs?.minSegmentRatio as number, kwargs?.angleSnapThreshold as number), {
      conditionFunc: (p, s) => s && p.length > 3,
      defaultKwargs: { minSegmentRatio: 0.5, angleSnapThreshold: 10.0, maxEdgeLengthCm: 1000 },
    });

    this.pipeline.registerPolygonProcessor('remove_small_edges', (polygon: Polygon, kwargs?: Record<string, unknown>) => this.removeSmallEdges(polygon, kwargs?.minEdgeLength as number), {
      conditionFunc: (p, s) => s && p.length > 3,
      defaultKwargs: { minEdgeLength: 2.0 },
    });

    this.pipeline.registerPolygonProcessor(
      'grid_snap_vertices',
      (polygon: Polygon, kwargs?: Record<string, unknown>) => this.gridSnapVertices(polygon, kwargs?.gridSizeCm as number, kwargs?.snapToGrid as boolean, kwargs?.minimizeAreaDeviation as boolean, kwargs?.removeCollinear as boolean),
      {
        conditionFunc: (p, s) => s && p.length > 3,
        defaultKwargs: {
          gridSizeCm: 0.5, // Default 0.5cm grid cells
          snapToGrid: false, // Default: use original positions (no snapping)
          minimizeAreaDeviation: false, // Default: don't optimize for area preservation
          removeCollinear: true, // Default: remove collinear points
        },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'hexagon_snap_vertices',
      (polygon: Polygon, kwargs?: Record<string, unknown>) => this.hexagonSnapVertices(polygon, kwargs?.hexSizeCm as number, kwargs?.snapToCenter as boolean, kwargs?.removeCollinear as boolean),
      {
        conditionFunc: (p, s) => s && p.length > 3,
        defaultKwargs: {
          hexSizeCm: 0.5, // Default 0.5cm hexagon size (center-to-vertex distance)
          snapToCenter: false, // Default: use closest original point (no snapping to hex center)
          removeCollinear: true, // Default: remove collinear points
        },
      },
    );

    this.pipeline.registerPolygonProcessor('merge_polygons', (polygons: Polygon[]) => this.mergePolygons(polygons), {
      batchProcessor: true,
      defaultKwargs: {},
    });

    this.pipeline.registerPolygonProcessor(
      'remove_noise',
      (polygon: Polygon, kwargs?: Record<string, unknown>) => this.removeNoise(polygon, (kwargs?.windowSize as number) || 5, (kwargs?.deviationThreshold as number) || 1.2, (kwargs?.smoothingWeight as number) || 0.8),
      {
        conditionFunc: (p, s) => p.length > 5,
        defaultKwargs: { windowSize: 5, deviationThreshold: 1.2, smoothingWeight: 0.8 },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_small_features',
      (polygon: Polygon, kwargs?: Record<string, unknown>) => this.removeSmallFeatures(polygon, kwargs?.minAreaCm2 as number, kwargs?.maxIterations as number),
      {
        conditionFunc: (p, s) => p.length > 4,
        defaultKwargs: {
          minAreaCm2: 150, // Features enclosing less than this area are pruned
          maxIterations: 100, // Maximum vertices removed per polygon
        },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_unsupported_segments',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        this.removeUnsupportedSegments(
          polygon,
          kwargs?.searchRangeCm as number,
          kwargs?.minProbeRatio as number,
          kwargs?.maxSpanCm as number,
          kwargs?.paddingCm as number,
          kwargs?.maxExtendCm as number,
          kwargs?.minAnchorCm as number,
          kwargs?.isolatedAnchorCm as number,
          kwargs?.minIntensity as number,
          kwargs?.maxAreaChangeM2 as number,
          (kwargs?.debug as boolean) ?? false,
        ),
      {
        conditionFunc: (p, s) => s && p.length > 4,
        defaultKwargs: {
          searchRangeCm: 3.0, // Perpendicular probe scan distance (cm), same as pixel alignment
          minProbeRatio: 0.7, // Edges with fewer density hits than this are "not on a line"
          maxSpanCm: 300, // Widest anchor-to-anchor gap to rebuild
          paddingCm: 7.0, // Edge-end sampling padding (cm)
          maxExtendCm: 150, // Max extension of an anchor edge to the rebuilt corner
          minAnchorCm: 25, // Edges shorter than this never anchor a rebuild
          isolatedAnchorCm: 120, // Short on-line edges between unsupported runs get removed too
          minIntensity: 150, // Min mask brightness (0-255) for a probe hit — much stricter than alignment's 30; only strong lines count here
          maxAreaChangeM2: 2, // Max m² a single run's fixup may add/remove — bigger changes are left unfixed
          debug: false, // Log per-edge support and per-run decisions to the console
        },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_relative_detail',
      (polygon: Polygon, kwargs?: Record<string, unknown>) => this.removeRelativeDetail(polygon, kwargs?.deviationThreshold as number, kwargs?.minEdgeFraction as number, kwargs?.maxIterations as number),
      {
        conditionFunc: (p, s) => s && p.length > 4,
        defaultKwargs: {
          deviationThreshold: 0.002, // Deviation must be < 0.2% of perimeter to be removed
          minEdgeFraction: 0.005, // Edges shorter than 0.5% of perimeter are candidates for removal
          maxIterations: 50, // Maximum simplification passes
        },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_cavities',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        PolygonProcessor.removeCavities(polygon, {
          weldDistance: (kwargs?.weldDistance as number) || 3.0,
          areaThresholdM2: (kwargs?.areaThresholdM2 as number) || 1.0,
          maxAreaThresholdM2: (kwargs?.maxAreaThresholdM2 as number) || Infinity,
        }),
      {
        conditionFunc: (p, s) => p.length > 3,
        defaultKwargs: { weldDistance: 3.0, areaThresholdM2: 1.0 },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_short_edges_and_rectify',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        this.removeShortEdgesAndRectify(polygon, {
          edgeLengthThreshold: (kwargs?.edgeLengthThreshold as number) ?? 10,
          intersectionDistanceThreshold: (kwargs?.intersectionDistanceThreshold as number) ?? 10,
          angleThreshold: (kwargs?.angleThreshold as number) ?? 15,
        }),
      {
        conditionFunc: (p) => p.length > 3,
        defaultKwargs: { edgeLengthThreshold: 10, intersectionDistanceThreshold: 100, angleThreshold: 15 },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_non90_small_edges',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        this.removeNon90SmallEdges(polygon, {
          smallEdgeThreshold: (kwargs?.smallEdgeThreshold as number) ?? 20,
          largeEdgeRatio: (kwargs?.largeEdgeRatio as number) ?? 2.0,
          angleToleranceDegrees: (kwargs?.angleToleranceDegrees as number) ?? 15,
        }),
      {
        conditionFunc: (p) => p.length > 3,
        defaultKwargs: { smallEdgeThreshold: 20, largeEdgeRatio: 2.0, angleToleranceDegrees: 15 },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_single_point_deviations',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        this.removeSinglePointDeviations(polygon, {
          maxDeviationCm: (kwargs?.maxDeviationCm as number) ?? 3.0,
          minChainLength: (kwargs?.minChainLength as number) ?? 3,
        }),
      {
        conditionFunc: (p) => p.length > 3,
        defaultKwargs: { maxDeviationCm: 3.0, minChainLength: 3 },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_three_point_deviations',
      (polygon: Polygon, kwargs?: Record<string, unknown>) =>
        this.removeThreePointDeviations(polygon, {
          angleThresholdDegrees: (kwargs?.angleThresholdDegrees as number) ?? 10.0,
          distanceThresholdCm: (kwargs?.distanceThresholdCm as number) ?? 5.0,
        }),
      {
        conditionFunc: (p) => p.length > 5,
        defaultKwargs: { angleThresholdDegrees: 10.0, distanceThresholdCm: 5.0 },
      },
    );

    this.pipeline.registerPolygonProcessor(
      'remove_small_polygons',
      (polygons: Polygon[], kwargs?: Record<string, unknown>) =>
        this.removeSmallPolygons(polygons, {
          minAreaM2: (kwargs?.minAreaM2 as number) ?? 0.5,
        }),
      {
        batchProcessor: true,
        defaultKwargs: { minAreaM2: 0.5 },
      },
    );

    this.pipeline.registerPolygonProcessor('bounding_rectangle', (polygon: Polygon) => this.polygonToBoundingRectangle(polygon), {
      conditionFunc: (p) => p.length >= 3,
      defaultKwargs: {},
    });

    // Closest-fit (minimum-area) rectangle — a rotated rectangle that hugs the
    // polygon, vs the axis-aligned `bounding_rectangle` above.
    this.pipeline.registerPolygonProcessor('min_area_rectangle', (polygon: Polygon) => this.polygonToMinAreaRectangle(polygon), {
      conditionFunc: (p) => p.length >= 3,
      defaultKwargs: {},
    });
  }

  /**
   * Get the processing pipeline (creates it if needed)
   */
  getPipeline(): PolygonProcessingPipeline {
    if (!this.pipeline) {
      // Fallback to building default if config hasn't loaded yet
      this.pipeline = this.buildPipeline();
    }
    return this.pipeline;
  }

  /**
   * Load pipeline configuration from external file
   */
  /**
   * Set callback to be called when pipeline is loaded from config file
   */
  setOnPipelineLoaded(callback: () => void): void {
    this.pipeline.setOnPipelineLoaded(callback);
  }

  /**
   * Wait for pipeline config to be loaded from file.
   * Returns true if loaded successfully, false otherwise.
   */
  async waitForPipelineLoad(): Promise<boolean> {
    return this.pipeline.waitForLoad();
  }

  /**
   * Set a custom pipeline (useful for loading from config)
   */
  setPipeline(pipeline: PolygonProcessingPipeline): void {
    this.pipeline = pipeline;
  }

  /**
   * Load pipeline from JSON string
   */
  loadPipelineFromJSON(json: string): boolean {
    return this.pipeline.loadFromJSON(json);
  }

  /**
   * Load pipeline from configuration file
   */
  async loadPipelineFromFile(url?: string): Promise<boolean> {
    return this.pipeline.loadFromFile(url);
  }

  /**
   * Export current pipeline to JSON
   */
  exportPipelineToJSON(): string {
    return this.pipeline.exportToJSON();
  }

  /**
   * Get current pipeline configuration
   */
  getPipelineConfig(): PipelineConfig {
    return this.pipeline.getConfig();
  }

  /**
   * Get all available processor IDs
   */
  getAvailableProcessorIds(): string[] {
    return this.pipeline.getRegisteredProcessorIds();
  }

  /**
   * Build the processing pipeline with all passes
   */
  private buildPipeline(): PolygonProcessingPipeline {
    return new PolygonProcessingPipeline();
  }

  /**
   * Set prompt points for edge adjustment
   * @param points Array of {x, y, label, type?, box?, vertex?, line?} where label is 1 for positive, 0 for negative
   */
  setPromptPoints(
    points: Array<{
      x: number;
      y: number;
      label: number;
      type?: 'point' | 'box' | 'vertex' | 'line';
      box?: {
        worldCorners: Point3D[];
      };
      vertex?: {
        radius: number; // Radius in pixels
      };
      line?: {
        start: { x: number; y: number };
        end: { x: number; y: number };
      };
    }>,
  ) {
    this.promptPoints = points;
  }

  /**
   * Set the density mask image for pixel alignment
   */
  setDensityMask(imageData: ImageData) {
    this.densityMask = imageData;
  }

  /**
   * Set the position map for sampling actual point positions during pixel alignment.
   * Non-zero RGBA pixels indicate a point exists at that location.
   */
  setPositionMap(imageData: ImageData) {
    this.positionMap = imageData;
  }

  /**
   * Set the scale factor for converting between pixel and world space
   * @param pixelsPerMeter Scale factor (pixels per meter)
   */
  setPixelsPerMeter(pixelsPerMeter: number) {
    this.pixelsPerMeter = pixelsPerMeter;
  }

  /**
   * Convert centimeters to pixels based on current scale
   * @param cm Distance in centimeters
   * @returns Distance in pixels
   */
  private cmToPixels(cm: number): number {
    return (cm / 100) * this.pixelsPerMeter;
  }

  /**
   * Convert pixels to centimeters based on current scale
   * @param pixels Distance in pixels
   * @returns Distance in centimeters
   */
  private pixelsToCm(pixels: number): number {
    return (pixels / this.pixelsPerMeter) * 100;
  }

  /**
   * Convert a polygon from pixel space to world space (centimeters)
   * @param polygon Polygon in pixel coordinates
   * @returns Polygon in world coordinates (centimeters)
   */
  pixelPolygonToWorldPolygon(polygon: Polygon): Polygon {
    const worldPoints = polygon.points.map((p) => ({
      x: this.pixelsToCm(p.x),
      y: this.pixelsToCm(p.y),
    }));

    const worldPolygon = createPolygon(worldPoints, polygon.isHole);

    // Recursively convert children if they exist
    if (polygon.children && polygon.children.length > 0) {
      worldPolygon.children = polygon.children.map((child) => this.pixelPolygonToWorldPolygon(child));
    }

    return worldPolygon;
  }

  /**
   * Convert a polygon from world space (centimeters) to pixel space
   * @param polygon Polygon in world coordinates (centimeters)
   * @returns Polygon in pixel coordinates
   */
  private worldPolygonToPixelPolygon(polygon: Polygon): Polygon {
    const pixelPoints = polygon.points.map((p) => ({
      x: this.cmToPixels(p.x),
      y: this.cmToPixels(p.y),
    }));

    const pixelPolygon = createPolygon(pixelPoints, polygon.isHole);

    // Recursively convert children if they exist
    if (polygon.children && polygon.children.length > 0) {
      pixelPolygon.children = polygon.children.map((child) => this.worldPolygonToPixelPolygon(child));
    }

    return pixelPolygon;
  }

  /**
   * Assign hole polygons to their containing parent polygons
   * Uses boolean operations to handle partial overlaps by subtracting the hole from the polygon
   */

  /**
   * Test if a point is inside a polygon using ray casting algorithm
   */
  private pointInPolygon(point: Point2D, polygon: Polygon): boolean {
    let inside = false;
    const x = point.x;
    const y = point.y;

    for (let i = 0, j = polygon.points.length - 1; i < polygon.points.length; j = i++) {
      const xi = polygon.points[i].x;
      const yi = polygon.points[i].y;
      const xj = polygon.points[j].x;
      const yj = polygon.points[j].y;

      const intersect = (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  }

  /**
   * Check if a point prompt is inside any orange box (label=2)
   * @param prompt The prompt point to check (in pixel coordinates)
   * @returns true if the prompt is inside an orange box, false otherwise
   */
  private isPromptInOrangeBox(prompt: { x: number; y: number }): boolean {
    // Get all orange boxes (label=2)
    const orangeBoxes = this.promptPoints.filter((p) => p.type === 'box' && p.label === 2 && p.box?.worldCorners);

    if (orangeBoxes.length === 0) {
      return false;
    }

    // Check if the prompt point is inside any orange box
    for (const box of orangeBoxes) {
      if (!box.box?.worldCorners) continue;

      // Convert box corners to 2D points in pixel space
      const boxCorners = box.box.worldCorners.map((c) => ({ x: c.x, y: c.y }));

      // Create a polygon from the box corners for point-in-polygon test
      const boxPolygon = createPolygon(boxCorners, false);

      // Check if the prompt is inside this box
      if (this.pointInPolygon(prompt, boxPolygon)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Process polygons through the pipeline (straightening, simplification, etc.)
   * Input and output polygons are in pixel space.
   */
  async processPolygons(
    polygons: Polygon[],
    options: {
      simplify?: boolean;
      signal?: AbortSignal;
    } = {},
  ): Promise<Polygon[]> {
    const { simplify = true } = options;

    // Clear debug data at the start of each processing run
    this.pixelAlignmentDebug = null;
    this.linePromptDebug = null;

    if (!polygons || polygons.length === 0) {
      return [];
    }

    // Detect room orientation from polygons
    if (polygons.length > 0 && this.useOrthogonalStraightening) {
      this.roomRotation = this.detectRoomOrientation(polygons);
    }

    // Get the pipeline (builds it if needed, otherwise reuses cached)
    const pipeline = this.getPipeline();

    // Process polygons through pipeline - holes will be processed automatically as children
    const processedPolygons = await pipeline.process(polygons, { simplify, signal: options.signal });

    return processedPolygons;
  }

  /**
   * Detect room orientation using minimum area bounding box
   */
  private detectRoomOrientation(polygons: Polygon[]): number {
    if (!polygons || polygons.length === 0) return 0;

    // Use the largest polygon (assumed to be the main room)
    const largestPolygon = polygons.reduce((max, curr) => {
      const maxArea = this.polygonArea(max);
      const currArea = this.polygonArea(curr);
      return currArea > maxArea ? curr : max;
    });

    if (largestPolygon.length < 3) return 0;

    // Find minimum area bounding box using rotating calipers
    const boundingBox = this.minAreaRect(largestPolygon);

    // Determine the main axis (longer side)
    let angle = boundingBox.angle;
    if (boundingBox.width < boundingBox.height) {
      angle += Math.PI / 2;
    }

    // Normalize to [-π, π]
    angle = Math.atan2(Math.sin(angle), Math.cos(angle));

    return angle;
  }

  /**
   * Apply orthogonal straightening to make walls perpendicular
   * @param polygon Polygon in world space (cm)
   * @param smallEdgeThreshold Threshold in centimeters for small edges
   */
  private applyOrthogonalStraightening(polygon: Polygon, force90Degree = false, smallEdgeThreshold = 1.5, recursionDepth = 0, angleThreshold = 45.0): Polygon {
    if (polygon.length < 3 || recursionDepth > 30) return polygon;

    // Polygon is already in world space (cm), threshold is in cm, so use directly
    const angleThresholdRad = (angleThreshold * Math.PI) / 180; // Convert degrees to radians

    // Normalize room rotation to [-π, π]
    const roomRotation = Math.atan2(Math.sin(this.roomRotation), Math.cos(this.roomRotation));

    // Define four orthogonal directions relative to room orientation
    const targetAngles = [roomRotation, roomRotation + Math.PI / 2, roomRotation + Math.PI, roomRotation + (3 * Math.PI) / 2].map((a) => Math.atan2(Math.sin(a), Math.cos(a)));

    // Room-aligned axis directions
    const axisDirections = [
      { x: Math.cos(targetAngles[0]), y: Math.sin(targetAngles[0]) },
      { x: Math.cos(targetAngles[1]), y: Math.sin(targetAngles[1]) },
    ];

    // Iterative optimization approach
    const straightened = polygon.points.map((p) => ({ ...p }));
    const n = straightened.length;

    const maxIterations = 10;
    for (let iteration = 0; iteration < maxIterations; iteration++) {
      let maxCorrection = 0;

      for (let edgeIdx = 0; edgeIdx < n; edgeIdx++) {
        const v1Idx = edgeIdx;
        const v2Idx = (edgeIdx + 1) % n;
        const v1 = straightened[v1Idx];
        const v2 = straightened[v2Idx];

        const edge = { x: v2.x - v1.x, y: v2.y - v1.y };
        const edgeLength = Math.sqrt(edge.x * edge.x + edge.y * edge.y);

        if (edgeLength < smallEdgeThreshold) continue;

        const edgeAngle = Math.atan2(edge.y, edge.x);

        // Find closest target angle
        const angleDiffs = targetAngles.map((targetAngle) => Math.abs(Math.atan2(Math.sin(edgeAngle - targetAngle), Math.cos(edgeAngle - targetAngle))));
        const minDiffIdx = angleDiffs.indexOf(Math.min(...angleDiffs));
        const minDiff = angleDiffs[minDiffIdx];

        // Skip edges that deviate too much from the room rotation grid (unless forced or very small)
        if (minDiff > angleThresholdRad && edgeLength >= smallEdgeThreshold && !force90Degree) {
          continue; // Edge deviates too much from grid, ignore it
        }

        // Check if correction is actually needed (already close enough)
        if (minDiff <= 0.01) {
          continue; // Already aligned, no correction needed
        }

        maxCorrection = Math.max(maxCorrection, minDiff);
        const targetAngle = targetAngles[minDiffIdx];

        const targetDir = { x: Math.cos(targetAngle), y: Math.sin(targetAngle) };

        // Option 1: Move v2 along each axis, keeping v1 fixed
        let bestV2 = v2;
        let bestV2Dist = Infinity;

        for (const axisDir of axisDirections) {
          // Solve: v1 + t * target_dir = v2 + s * axis_dir
          // Rearrange: t * target_dir - s * axis_dir = v2 - v1
          // Matrix: [target_dir | -axis_dir] * [t, s]^T = b
          const b = { x: v2.x - v1.x, y: v2.y - v1.y };
          const det = targetDir.x * -axisDir.y - targetDir.y * -axisDir.x;

          if (Math.abs(det) > 1e-10) {
            // Solve for s using Cramer's rule (second column)
            const s = (targetDir.x * b.y - targetDir.y * b.x) / det;

            const moveDist = Math.abs(s);
            const candidateV2 = {
              x: v2.x + s * axisDir.x,
              y: v2.y + s * axisDir.y,
            };

            // Prefer smaller movements
            if (moveDist < bestV2Dist) {
              bestV2Dist = moveDist;
              bestV2 = candidateV2;
            }
          }
        }

        // Option 2: Move v1 along each axis, keeping v2 fixed
        let bestV1 = v1;
        let bestV1Dist = Infinity;

        for (const axisDir of axisDirections) {
          // Solve: v1 + s * axis_dir + t * target_dir = v2
          // Rearrange: s * axis_dir + t * target_dir = v2 - v1
          // Matrix: [axis_dir | target_dir] * [s, t]^T = b
          const b = { x: v2.x - v1.x, y: v2.y - v1.y };
          const det = axisDir.x * targetDir.y - axisDir.y * targetDir.x;

          if (Math.abs(det) > 1e-10) {
            // Solve for s using Cramer's rule (first column)
            const s = (b.x * targetDir.y - b.y * targetDir.x) / det;

            const moveDist = Math.abs(s);
            const candidateV1 = {
              x: v1.x + s * axisDir.x,
              y: v1.y + s * axisDir.y,
            };

            if (moveDist < bestV1Dist) {
              bestV1Dist = moveDist;
              bestV1 = candidateV1;
            }
          }
        }

        // Choose the option that requires less movement (with damping)
        const damping = 0.5;
        if (bestV1Dist < bestV2Dist) {
          straightened[v1Idx].x += damping * (bestV1.x - v1.x);
          straightened[v1Idx].y += damping * (bestV1.y - v1.y);
        } else {
          straightened[v2Idx].x += damping * (bestV2.x - v2.x);
          straightened[v2Idx].y += damping * (bestV2.y - v2.y);
        }
      }

      // Check convergence
      if (maxCorrection < 0.01) break;
    }

    // Remove small edges and weld collinear vertices
    const welded: Point2D[] = [straightened[0]];

    for (let i = 1; i < straightened.length; i++) {
      const prevIdx = i - 1;
      const currIdx = i;
      const nextIdx = (i + 1) % straightened.length;

      // Check edge length before current vertex
      const edgeBefore = {
        x: straightened[currIdx].x - straightened[prevIdx].x,
        y: straightened[currIdx].y - straightened[prevIdx].y,
      };
      const edgeBeforeLength = Math.sqrt(edgeBefore.x * edgeBefore.x + edgeBefore.y * edgeBefore.y);

      if (edgeBeforeLength < smallEdgeThreshold) continue;

      // Check if collinear
      const edgeAfter = {
        x: straightened[nextIdx].x - straightened[currIdx].x,
        y: straightened[nextIdx].y - straightened[currIdx].y,
      };

      const angleBefore = Math.atan2(edgeBefore.y, edgeBefore.x);
      const angleAfter = Math.atan2(edgeAfter.y, edgeAfter.x);
      const angleDiff = Math.abs(Math.atan2(Math.sin(angleAfter - angleBefore), Math.cos(angleAfter - angleBefore)));

      if (angleDiff > 0.1) {
        welded.push(straightened[currIdx]);
      }
    }

    // Check last edge
    if (welded.length > 2) {
      const edgeLast = {
        x: welded[0].x - welded[welded.length - 1].x,
        y: welded[0].y - welded[welded.length - 1].y,
      };
      const edgeLastLength = Math.sqrt(edgeLast.x * edgeLast.x + edgeLast.y * edgeLast.y);

      if (edgeLastLength < smallEdgeThreshold) {
        welded.pop();
      } else {
        const edgeFirst = {
          x: welded[1].x - welded[0].x,
          y: welded[1].y - welded[0].y,
        };
        const angleLast = Math.atan2(edgeLast.y, edgeLast.x);
        const angleFirst = Math.atan2(edgeFirst.y, edgeFirst.x);
        const angleDiff = Math.abs(Math.atan2(Math.sin(angleFirst - angleLast), Math.cos(angleFirst - angleLast)));

        if (angleDiff <= 0.01) {
          welded.shift();
        }
      }
    }

    const result = welded.length >= 3 ? welded : polygon.points;

    // Recursively process if vertices were removed
    if (result.length < polygon.length) {
      return this.applyOrthogonalStraightening(createPolygon(result, polygon.isHole), force90Degree, smallEdgeThreshold, recursionDepth + 1, angleThreshold);
    }

    return createPolygon(result, polygon.isHole);
  }

  /**
   * Align polygon edges to best match the density mask pixels
   * Runs recursively until convergence
   * @param polygon Polygon in world space (cm)
   * @param paddingCm Edge padding in cm (fixed distance, not ratio)
   * @param searchRange Search range in cm (converted to pixels internally)
   * @param rotate If true, edges can rotate to fit peaks; if false, edges only translate perpendicular
   */
  private applyPixelAlignment(polygon: Polygon, paddingCm = 1.0, searchRange?: number, enableDebug = false, rotate = true, maxIterations = 50, minProbeRatio?: number, relative = false): Polygon {
    if (!this.positionMap) {
      console.warn('   ⚠️  No position map available for pixel alignment');
      return polygon;
    }

    // When relative, interpret searchRange as % of bounding box diagonal and convert to cm
    let effectiveSearchRange = searchRange;
    if (relative && searchRange !== undefined) {
      const bbox = this.getPolygonBounds(polygon);
      const diagonal = Math.sqrt((bbox.maxX - bbox.minX) ** 2 + (bbox.maxY - bbox.minY) ** 2);
      effectiveSearchRange = diagonal * (searchRange / 100);
    }

    // Convert polygon from world space (cm) to pixel space for position map operations
    let currentInPixelSpace = this.worldPolygonToPixelPolygon(polygon);

    const convergenceThresholdCm = 0.1; // Stop if average movement < 0.1 cm
    const convergenceThresholdPixels = this.cmToPixels(convergenceThresholdCm);
    let iteration = 0;

    // Track original area to detect destructive changes
    const originalArea = this.calculatePolygonArea(currentInPixelSpace);

    while (iteration < maxIterations) {
      const aligned = this.applyPixelAlignmentSinglePass(currentInPixelSpace, paddingCm, effectiveSearchRange, enableDebug, rotate, minProbeRatio);

      // Validate: check that the polygon wasn't destroyed
      const alignedArea = this.calculatePolygonArea(aligned);

      // If winding flipped (sign changed) or area shrunk below 50%, revert
      if ((originalArea > 0 && alignedArea <= 0) || (originalArea < 0 && alignedArea >= 0) || Math.abs(alignedArea) < Math.abs(originalArea) * 0.5) {
        break;
      }

      // Calculate average movement (in pixels)
      let totalMovement = 0;
      for (let i = 0; i < currentInPixelSpace.length; i++) {
        const dx = aligned[i].x - currentInPixelSpace[i].x;
        const dy = aligned[i].y - currentInPixelSpace[i].y;
        totalMovement += Math.sqrt(dx * dx + dy * dy);
      }
      const avgMovement = totalMovement / currentInPixelSpace.length;

      iteration++;
      currentInPixelSpace = aligned;

      // Check for convergence
      if (avgMovement < convergenceThresholdPixels) {
        break;
      }
    }

    // Convert result back to world space (cm) and return
    const resultInWorldSpace = this.pixelPolygonToWorldPolygon(currentInPixelSpace);
    return resultInWorldSpace;
  }

  /**
   * Single pass of pixel alignment using the position map.
   *
   * Instead of sampling a density map for intensity peaks, this probes the position map directly.
   * The position map encodes world-space XYZ as RGB (R=X, G=Y, B=Z, normalized 0-1).
   *
   * Algorithm per edge:
   * 1. Scan perpendicular to the edge at sample points
   * 2. Collect all non-empty position map pixels along each scan line
   * 3. Find the best B value (closest to 0.5 = center of the Z range) across all samples
   * 4. Collect all pixels matching that B value (same height plane = same wall surface)
   * 5. Use the pixel positions of matching points to fit a line
   *
   * @param polygon Polygon in pixel space
   * @param paddingCm Edge padding in cm (fixed distance, not ratio)
   * @param searchRange Search range in cm (will be converted to pixels)
   * @param rotate If true, edges can rotate to fit peaks; if false, edges only translate perpendicular
   */
  private applyPixelAlignmentSinglePass(polygon: Polygon, paddingCm = 1.0, searchRange?: number, enableDebug = false, rotate = true, minProbeRatio?: number): Polygon {
    if (!this.positionMap) return polygon;

    const map = this.positionMap;

    // Convert cm parameters to pixels
    const searchRangeCm = searchRange ?? this.pixelAlignmentSearchRange;
    const effectiveSearchRange = this.cmToPixels(searchRangeCm);
    const effectivePaddingPixels = this.cmToPixels(paddingCm);

    // Initialize or accumulate debug data if enabled
    // Note: We accumulate across multiple pixel alignment passes rather than overwriting
    if (enableDebug) {
      if (!this.pixelAlignmentDebug) {
        this.pixelAlignmentDebug = {
          sampleLines: [],
          peakPoints: [],
          peakPointWeights: [],
          peakRegions: [],
          fittedLines: [],
          sampledPositionPoints: [],
          densityFallbackPoints: [],
        };
      }
    }
    // Don't set to null if disabled - this allows debug from earlier passes to persist

    // Calculate edge offsets and fitted directions
    const nEdges = polygon.length;
    const edgeOffsets = new Array(nEdges).fill(0);
    const edgeHasPeaks = new Array(nEdges).fill(false);
    const edgePerpDirs: Point2D[] = [];
    const edgeFittedDirections: (Point2D | null)[] = new Array(nEdges).fill(null);
    const edgeFittedPoints: (Point2D | null)[] = new Array(nEdges).fill(null);

    for (let i = 0; i < nEdges; i++) {
      const p1 = polygon[i];
      const p2 = polygon[(i + 1) % nEdges];

      const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
      const edgeLength = Math.sqrt(edge.x * edge.x + edge.y * edge.y);

      if (edgeLength < 1) {
        edgePerpDirs.push({ x: 0, y: 0 });
        continue;
      }

      const edgeDir = { x: edge.x / edgeLength, y: edge.y / edgeLength };
      const perpDir = { x: -edgeDir.y, y: edgeDir.x }; // 90° rotation (outward normal)
      edgePerpDirs.push(perpDir);

      // Use fixed padding in pixels (converted from cm), but cap at 15% of edge length
      const maxPaddingPx = edgeLength * 0.15;
      const edgePaddingPx = Math.min(effectivePaddingPixels, maxPaddingPx);

      // Sample points along the edge with padding
      const samplingLength = edgeLength - edgePaddingPx;
      if (samplingLength <= 0) {
        edgeOffsets[i] = 0;
        continue;
      }

      const numSamples = Math.max(5, Math.floor(samplingLength));

      // Phase 1: Use density map to find peak positions for each probe
      // Then sample B color at those positions and vote on dominant B value(s)
      const probeSamplePoints: Point2D[] = [];
      const probeResults: Array<{
        densityPeak: Point2D | null;
        bValue: number | null;
        positionData: { r: number; g: number; b: number; a: number } | null;
      }> = [];

      for (let j = 0; j < numSamples; j++) {
        const sampleDistance = edgePaddingPx / 2.0 + (j / (numSamples - 1)) * samplingLength;
        const t = sampleDistance / edgeLength;
        const samplePoint = {
          x: p1.x + edge.x * t,
          y: p1.y + edge.y * t,
        };
        probeSamplePoints.push(samplePoint);

        // DEBUG: Add sample scan line
        if (enableDebug) {
          this.pixelAlignmentDebug?.sampleLines.push({
            start: {
              x: samplePoint.x + perpDir.x * -effectiveSearchRange,
              y: samplePoint.y + perpDir.y * -effectiveSearchRange,
            },
            end: {
              x: samplePoint.x + perpDir.x * effectiveSearchRange,
              y: samplePoint.y + perpDir.y * effectiveSearchRange,
            },
          });
        }

        // Find density peak first (visual center of the line)
        let densityPeak: Point2D | null = null;
        if (this.densityMask) {
          densityPeak = this.findDensityPeakAlongScanLine(samplePoint, perpDir, effectiveSearchRange, this.densityMask);
        }

        // Sample B color (height) at the density peak position from position map
        let bValue: number | null = null;
        let positionData: { r: number; g: number; b: number; a: number } | null = null;

        if (densityPeak) {
          const idx = (densityPeak.y * map.width + densityPeak.x) * 4;
          const r = map.data[idx];
          const g = map.data[idx + 1];
          const b = map.data[idx + 2];
          const a = map.data[idx + 3];

          // Only use if position map has data at this location
          if (r !== 0 || g !== 0 || b !== 0 || a !== 0) {
            bValue = b;
            positionData = { r, g, b, a };
          }
        }

        probeResults.push({ densityPeak, bValue, positionData });
      }

      // Phase 2: Collect all probes with density peaks and assign weights based on B value.
      // B is normalized eye-Z depth (low B = close to camera, high B = far). For
      // a top-down floor capture, far = floor (where walls meet floor) — that's
      // the layer we want to weight up. For wall-perpendicular captures, far =
      // behind-wall, which we also want to weight up because surface points
      // sit at mid-depth and we want the structural layer.
      const sampledPoints: Point2D[] = []; // pixel positions (for line fitting)
      const decodedPositions: Point2D[] = []; // 12-bit decoded positions
      const pointWeights: number[] = []; // weight for each point based on B value
      const isFromDensityFallback: boolean[] = []; // track which points came from density fallback
      let probesWithMatch = 0;

      for (let probeIdx = 0; probeIdx < probeResults.length; probeIdx++) {
        const result = probeResults[probeIdx];

        // Skip probes without a density peak
        if (!result.densityPeak) {
          continue;
        }

        probesWithMatch++;
        sampledPoints.push(result.densityPeak);
        isFromDensityFallback.push(true); // All points come from density now

        // Higher B = higher weight (B=255 → weight=1, B=0 → ~0.1).
        const baseWeight = 0.1;
        const bWeight = result.bValue !== null ? baseWeight + ((1 - baseWeight) * result.bValue) / 256 : 0.5;
        pointWeights.push(bWeight);

        if (result.positionData && this.densityMask) {
          // Decode 12-bit eye-X / eye-Y from the position map, mapping back
          // to image-space pixels (y-axis flipped because the renderer
          // flips the buffer vertically while the encoded normY runs
          // bottom→top in eye space).
          const xHigh = result.positionData.r;
          const yHigh = result.positionData.g;
          const xLow = (result.positionData.a >> 4) & 0x0f;
          const yLow = result.positionData.a & 0x0f;
          const x12 = (xHigh << 4) | xLow; // 0-4095
          const y12 = (yHigh << 4) | yLow; // 0-4095
          const xPixel = (x12 / 4095) * this.densityMask.width;
          const yPixel = (1 - y12 / 4095) * this.densityMask.height;
          decodedPositions.push({ x: xPixel, y: yPixel });
        } else if (result.positionData) {
          // Fallback to position map dimensions if no density mask. Same
          // y-flip as the densityMask branch above.
          const xHigh = result.positionData.r;
          const yHigh = result.positionData.g;
          const xLow = (result.positionData.a >> 4) & 0x0f;
          const yLow = result.positionData.a & 0x0f;
          const x12 = (xHigh << 4) | xLow;
          const y12 = (yHigh << 4) | yLow;
          const xPixel = (x12 / 4095) * map.width;
          const yPixel = (1 - y12 / 4095) * map.height;
          decodedPositions.push({ x: xPixel, y: yPixel });
        } else {
          // No position map data - use pixel coordinates as proxy
          decodedPositions.push({ x: result.densityPeak.x, y: result.densityPeak.y });
        }
      }

      const effectiveMinProbeRatio = minProbeRatio ?? this.pixelAlignmentMinProbeRatio;
      if (probesWithMatch < numSamples * effectiveMinProbeRatio) {
        edgeOffsets[i] = 0;
        continue;
      }

      if (enableDebug) {
        for (let idx = 0; idx < decodedPositions.length; idx++) {
          // Use decoded positions (from position map) for debug visualization
          const pt = decodedPositions[idx];
          const weight = pointWeights[idx];
          this.pixelAlignmentDebug?.peakPoints.push(pt);
          this.pixelAlignmentDebug?.peakPointWeights.push(weight);
          if (isFromDensityFallback[idx]) {
            this.pixelAlignmentDebug?.densityFallbackPoints.push(pt);
          } else {
            this.pixelAlignmentDebug?.sampledPositionPoints.push(pt);
          }
        }
      }

      // Phase 4: Fit a line through the decoded 12-bit positions and compute edge offset.
      // The decoded positions (from position map) give accurate world-space XY coordinates
      // in a 0-4095 range that maps to the image/mask pixel space.
      let bestOffset = 0;

      if (decodedPositions.length > 2) {
        edgeHasPeaks[i] = true;

        if (rotate) {
          const edgeMidpoint = {
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
          };
          // Use decoded positions from position map for more accurate line
          // fitting. The edge midpoint anchors the fit: on double-layer
          // walls the near-tie layer closer to the CURRENT edge wins, so
          // alignment refines the observed line instead of re-segmenting
          // onto a parallel layer behind it.
          const lineFit = this.fitLineRANSAC(decodedPositions, 2.0, 100, 0.3, edgeMidpoint);

          if (lineFit) {
            const toLine = {
              x: lineFit.point.x - edgeMidpoint.x,
              y: lineFit.point.y - edgeMidpoint.y,
            };
            bestOffset = toLine.x * perpDir.x + toLine.y * perpDir.y;

            edgeFittedDirections[i] = lineFit.direction;
            edgeFittedPoints[i] = lineFit.point;
          }
        } else {
          let totalOffset = 0;
          let validPoints = 0;
          const edgeMidpoint = {
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
          };

          // Use decoded positions from position map
          for (const pt of decodedPositions) {
            const toPt = {
              x: pt.x - edgeMidpoint.x,
              y: pt.y - edgeMidpoint.y,
            };
            totalOffset += toPt.x * perpDir.x + toPt.y * perpDir.y;
            validPoints++;
          }

          if (validPoints > 0) {
            bestOffset = totalOffset / validPoints;
            edgeFittedDirections[i] = edgeDir;
            edgeFittedPoints[i] = {
              x: edgeMidpoint.x + perpDir.x * bestOffset,
              y: edgeMidpoint.y + perpDir.y * bestOffset,
            };
          }
        }

        // DEBUG: Add fitted line
        if (enableDebug && edgeFittedPoints[i] && edgeFittedDirections[i]) {
          const fittedPoint = edgeFittedPoints[i] as Point2D;
          const fittedDir = edgeFittedDirections[i] as Point2D;
          const lineExtent = edgeLength * 0.6;
          const fittedLineStart = {
            x: fittedPoint.x - (fittedDir.x * lineExtent) / 2,
            y: fittedPoint.y - (fittedDir.y * lineExtent) / 2,
          };
          const fittedLineEnd = {
            x: fittedPoint.x + (fittedDir.x * lineExtent) / 2,
            y: fittedPoint.y + (fittedDir.y * lineExtent) / 2,
          };
          this.pixelAlignmentDebug?.fittedLines.push({ start: fittedLineStart, end: fittedLineEnd });
        }
      }
      // Clamp offset to search range — anything beyond means bad data
      bestOffset = Math.max(-effectiveSearchRange, Math.min(effectiveSearchRange, bestOffset));
      edgeOffsets[i] = bestOffset;
    }

    // Build effective fitted lines for all edges
    const effectiveFittedLines: ({ point: Point2D; direction: Point2D } | null)[] = new Array(nEdges).fill(null);

    for (let i = 0; i < nEdges; i++) {
      if (edgeFittedDirections[i] && edgeFittedPoints[i]) {
        effectiveFittedLines[i] = {
          point: edgeFittedPoints[i] as Point2D,
          direction: edgeFittedDirections[i] as Point2D,
        };
      } else if (edgeOffsets[i] !== 0) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % nEdges];
        const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
        const edgeLen = Math.sqrt(edge.x * edge.x + edge.y * edge.y);
        if (edgeLen > 0) {
          effectiveFittedLines[i] = {
            point: {
              x: (p1.x + p2.x) / 2 + edgePerpDirs[i].x * edgeOffsets[i],
              y: (p1.y + p2.y) / 2 + edgePerpDirs[i].y * edgeOffsets[i],
            },
            direction: { x: edge.x / edgeLen, y: edge.y / edgeLen },
          };
        }
      }
    }

    // Build original edge directions for edges without fitted lines
    const originalEdgeLines: { point: Point2D; direction: Point2D }[] = [];
    for (let i = 0; i < nEdges; i++) {
      const p1 = polygon[i];
      const p2 = polygon[(i + 1) % nEdges];
      const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
      const edgeLen = Math.sqrt(edge.x * edge.x + edge.y * edge.y);
      originalEdgeLines.push({
        point: p1,
        direction: edgeLen > 0 ? { x: edge.x / edgeLen, y: edge.y / edgeLen } : { x: 1, y: 0 },
      });
    }

    // Reconstruct polygon by intersecting adjacent edge lines
    const maxIntersectionDistance = effectiveSearchRange * 1.5;

    const newPolygon = polygon.points.map((p, vertexIdx) => {
      const prevEdgeIdx = (vertexIdx - 1 + nEdges) % nEdges;
      const nextEdgeIdx = vertexIdx;

      const fittedA = effectiveFittedLines[prevEdgeIdx];
      const fittedB = effectiveFittedLines[nextEdgeIdx];

      if (!fittedA && !fittedB) {
        return { ...p };
      }

      const lineA = fittedA ?? originalEdgeLines[prevEdgeIdx];
      const lineB = fittedB ?? originalEdgeLines[nextEdgeIdx];

      const intersection = this.infiniteLineIntersection(lineA.point, lineA.direction, lineB.point, lineB.direction);
      if (intersection) {
        const dx = intersection.x - p.x;
        const dy = intersection.y - p.y;
        if (dx * dx + dy * dy <= maxIntersectionDistance * maxIntersectionDistance) {
          return intersection;
        }
      }

      if (fittedA && fittedB) {
        const toA = { x: p.x - fittedA.point.x, y: p.y - fittedA.point.y };
        const alongA = toA.x * fittedA.direction.x + toA.y * fittedA.direction.y;
        const projA = {
          x: fittedA.point.x + alongA * fittedA.direction.x,
          y: fittedA.point.y + alongA * fittedA.direction.y,
        };
        const toB = { x: p.x - fittedB.point.x, y: p.y - fittedB.point.y };
        const alongB = toB.x * fittedB.direction.x + toB.y * fittedB.direction.y;
        const projB = {
          x: fittedB.point.x + alongB * fittedB.direction.x,
          y: fittedB.point.y + alongB * fittedB.direction.y,
        };
        return { x: (projA.x + projB.x) / 2, y: (projA.y + projB.y) / 2 };
      }

      const fitted = fittedA ?? fittedB;
      if (fitted) {
        const toPoint = { x: p.x - fitted.point.x, y: p.y - fitted.point.y };
        const along = toPoint.x * fitted.direction.x + toPoint.y * fitted.direction.y;
        return {
          x: fitted.point.x + along * fitted.direction.x,
          y: fitted.point.y + along * fitted.direction.y,
        };
      }

      return { ...p };
    });

    return createPolygon(newPolygon, polygon.isHole);
  }

  // ==================== Helper Methods ====================

  /**
   * Find the density peak along a scan line in the density mask.
   * Used as fallback when position map has no data for a probe.
   * Scans perpendicular to the edge and finds the pixel with highest intensity.
   *
   * @param samplePoint The starting point on the edge
   * @param perpDir The perpendicular direction to scan along
   * @param searchRange How far to scan in each direction
   * @param densityMask The density mask image data
   * @returns The pixel position of the density peak, or null if no significant peak found
   */
  private findDensityPeakAlongScanLine(samplePoint: Point2D, perpDir: Point2D, searchRange: number, densityMask: ImageData): Point2D | null {
    const intensityThreshold = 30; // Minimum intensity to consider as part of a line/feature
    const smoothingRadius = 2; // Gaussian-like smoothing window radius

    // Build intensity profile along the scan line
    const intensityProfile: Array<{
      x: number;
      y: number;
      offset: number;
      intensity: number;
    }> = [];

    for (let offset = -searchRange; offset <= searchRange; offset++) {
      const scanX = samplePoint.x + perpDir.x * offset;
      const scanY = samplePoint.y + perpDir.y * offset;
      const pixelX = Math.round(scanX);
      const pixelY = Math.round(scanY);

      if (pixelX < 0 || pixelX >= densityMask.width || pixelY < 0 || pixelY >= densityMask.height) {
        intensityProfile.push({ x: pixelX, y: pixelY, offset, intensity: 0 });
        continue;
      }

      const idx = (pixelY * densityMask.width + pixelX) * 4;
      const r = densityMask.data[idx];
      const g = densityMask.data[idx + 1];
      const b = densityMask.data[idx + 2];
      const intensity = (r + g + b) / 3;

      intensityProfile.push({ x: pixelX, y: pixelY, offset, intensity });
    }

    if (intensityProfile.length === 0) {
      return null;
    }

    // Smooth the intensity profile using a weighted moving average (triangle kernel)
    const smoothedIntensities: number[] = [];
    for (let i = 0; i < intensityProfile.length; i++) {
      let weightedSum = 0;
      let totalWeight = 0;

      for (let j = -smoothingRadius; j <= smoothingRadius; j++) {
        const idx = i + j;
        if (idx >= 0 && idx < intensityProfile.length) {
          // Triangle kernel weight: higher weight for closer samples
          const weight = smoothingRadius + 1 - Math.abs(j);
          weightedSum += intensityProfile[idx].intensity * weight;
          totalWeight += weight;
        }
      }

      smoothedIntensities.push(totalWeight > 0 ? weightedSum / totalWeight : 0);
    }

    // Find the global maximum on the smoothed profile (above threshold)
    let maxSmoothedIntensity = 0;
    let maxIdx = -1;

    for (let i = 0; i < smoothedIntensities.length; i++) {
      if (smoothedIntensities[i] > maxSmoothedIntensity && smoothedIntensities[i] >= intensityThreshold) {
        maxSmoothedIntensity = smoothedIntensities[i];
        maxIdx = i;
      }
    }

    if (maxIdx === -1) {
      return null;
    }

    // Find all peaks (local maxima) on the smoothed profile
    const peaks: Array<{ idx: number; intensity: number }> = [];
    for (let i = 1; i < smoothedIntensities.length - 1; i++) {
      if (smoothedIntensities[i] >= intensityThreshold && smoothedIntensities[i] >= smoothedIntensities[i - 1] && smoothedIntensities[i] >= smoothedIntensities[i + 1]) {
        peaks.push({ idx: i, intensity: smoothedIntensities[i] });
      }
    }

    // If no local maxima found, use global max
    if (peaks.length === 0) {
      peaks.push({ idx: maxIdx, intensity: maxSmoothedIntensity });
    }

    // Select best peak: prefer highest intensity, use distance to edge (offset=0) as tiebreaker
    const significanceThreshold = 0.6;
    const maxPeakIntensity = Math.max(...peaks.map((p) => p.intensity));
    const strongPeaks = peaks.filter((p) => p.intensity >= maxPeakIntensity * significanceThreshold);

    let selectedPeakIdx: number;
    if (strongPeaks.length === 1) {
      selectedPeakIdx = strongPeaks[0].idx;
    } else {
      // Multiple strong peaks - pick the one closest to the original edge position (offset=0)
      // The center of the profile corresponds to offset=0
      const centerIdx = Math.floor(intensityProfile.length / 2);
      selectedPeakIdx = strongPeaks.reduce((closest, peak) => (Math.abs(peak.idx - centerIdx) < Math.abs(closest.idx - centerIdx) ? peak : closest)).idx;
    }

    // Return the pixel position at the selected peak
    const selectedSample = intensityProfile[selectedPeakIdx];
    return { x: selectedSample.x, y: selectedSample.y };
  }

  /**
   * Dilate a binary mask by expanding white regions by the specified number of pixels
   */
  private dilateMask(grid: number[][], width: number, height: number, iterations: number): void {
    for (let iter = 0; iter < iterations; iter++) {
      const temp = new Array(height);
      for (let y = 0; y < height; y++) {
        temp[y] = new Array(width);
        for (let x = 0; x < width; x++) {
          temp[y][x] = grid[y][x];
        }
      }

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (grid[y][x] === 1) {
            // Expand to 8-connected neighbors
            for (let dy = -1; dy <= 1; dy++) {
              for (let dx = -1; dx <= 1; dx++) {
                const ny = y + dy;
                const nx = x + dx;
                if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                  temp[ny][nx] = 1;
                }
              }
            }
          }
        }
      }

      // Copy temp back to grid
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          grid[y][x] = temp[y][x];
        }
      }
    }
  }

  /**
   * Find contours in binary mask using marching squares (async with yield points)
   */
  private async findContours(mask: ImageData, signal?: AbortSignal): Promise<Polygon[]> {
    const width = mask.width;
    const height = mask.height;
    const contours: Polygon[] = [];

    // Use Uint8Array for faster access and less memory (1 byte per pixel instead of 8 bytes for number)
    const grid = new Uint8Array(width * height);
    const maskData = mask.data;

    // Create flat binary grid - much faster than nested arrays
    for (let i = 0, len = width * height; i < len; i++) {
      grid[i] = maskData[i * 4] > 128 ? 1 : 0;
    }

    // Check if aborted
    if (signal?.aborted) {
      return [];
    }

    // Use flat Uint16Array for labels (supports up to 65535 regions, uses half the memory of Int32)
    const labels = new Uint16Array(width * height);

    let currentLabel = 0;
    const labelBounds = new Map<number, { minX: number; minY: number; maxX: number; maxY: number }>();

    // Flood fill to label each connected region
    for (let y = 0; y < height; y++) {
      if (y > 0 && y % 50 === 0) {
        // Check if aborted during flood fill
        if (signal?.aborted) {
          return [];
        }
      }

      const rowOffset = y * width;
      for (let x = 0; x < width; x++) {
        const idx = rowOffset + x;
        if (grid[idx] === 1 && labels[idx] === 0) {
          currentLabel++;
          const stack = [{ x, y }];
          let minX = x,
            maxX = x,
            minY = y,
            maxY = y;

          while (stack.length > 0) {
            const popped = stack.pop();
            if (!popped) continue;
            const { x: cx, y: cy } = popped;
            if (cx < 0 || cx >= width || cy < 0 || cy >= height) continue;

            const cidx = cy * width + cx;
            if (grid[cidx] !== 1 || labels[cidx] !== 0) continue;

            labels[cidx] = currentLabel;
            minX = Math.min(minX, cx);
            maxX = Math.max(maxX, cx);
            minY = Math.min(minY, cy);
            maxY = Math.max(maxY, cy);

            // 8-connected neighbors (matches boundary tracing connectivity)
            stack.push({ x: cx + 1, y: cy }); // E
            stack.push({ x: cx - 1, y: cy }); // W
            stack.push({ x: cx, y: cy + 1 }); // S
            stack.push({ x: cx, y: cy - 1 }); // N
            stack.push({ x: cx + 1, y: cy + 1 }); // SE
            stack.push({ x: cx - 1, y: cy - 1 }); // NW
            stack.push({ x: cx + 1, y: cy - 1 }); // NE
            stack.push({ x: cx - 1, y: cy + 1 }); // SW
          }

          labelBounds.set(currentLabel, { minX, minY, maxX, maxY });
        }
      }
    }

    // Check if aborted
    if (signal?.aborted) {
      return [];
    }

    // For each labeled region, find the topmost-leftmost boundary pixel and trace only outer contour
    for (let label = 1; label <= currentLabel; label++) {
      if (label > 1 && label % 10 === 0) {
        // Check if aborted during contour tracing
        if (signal?.aborted) {
          return [];
        }
      }

      const bounds = labelBounds.get(label);
      if (!bounds) continue;

      // Find topmost-leftmost boundary pixel for this region (guaranteed to be on outer boundary)
      let startX = -1,
        startY = -1;
      outerLoop: for (let y = bounds.minY; y <= bounds.maxY; y++) {
        const rowOffset = y * width;
        for (let x = bounds.minX; x <= bounds.maxX; x++) {
          const idx = rowOffset + x;
          if (labels[idx] === label) {
            // Check if it's a boundary pixel (has black neighbor or is on edge)
            const isBoundary =
              y === 0 ||
              y === height - 1 ||
              x === 0 ||
              x === width - 1 ||
              (y > 0 && grid[(y - 1) * width + x] === 0) ||
              (y < height - 1 && grid[(y + 1) * width + x] === 0) ||
              (x > 0 && grid[rowOffset + x - 1] === 0) ||
              (x < width - 1 && grid[rowOffset + x + 1] === 0);

            if (isBoundary) {
              startX = x;
              startY = y;
              break outerLoop;
            }
          }
        }
      }

      if (startX >= 0 && startY >= 0) {
        // Trace only the outer contour for this region
        const contour = this.traceContour(grid, startX, startY, width, height);
        if (contour.length > 2) {
          // Check if this contour is a duplicate by comparing with existing contours
          const isDuplicate = contours.some((existing) => {
            if (existing.points.length !== contour.points.length) return false;
            // Check if all points match (same contour)
            return existing.points.every((pt, i) => pt.x === contour.points[i].x && pt.y === contour.points[i].y);
          });

          if (!isDuplicate) {
            contours.push(contour);
          } else {
            console.warn(`   ⚠️  Skipping duplicate contour for label ${label}`);
          }
        }
      }
    }

    return contours;
  }

  /**
   * Calculate polygon area using the Shoelace formula (signed area)
   * Positive area = counterclockwise orientation
   * Negative area = clockwise orientation
   */
  private calculatePolygonArea(polygon: Polygon): number {
    if (polygon.length < 3) return 0;

    let area = 0;
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      area += polygon[i].x * polygon[j].y;
      area -= polygon[j].x * polygon[i].y;
    }
    return area / 2;
  }

  /**
   * Trace a single contour using Moore neighborhood boundary following
   */
  private traceContour(grid: Uint8Array, startX: number, startY: number, width: number, height: number): Polygon {
    const contour: Point2D[] = [];

    // Moore neighborhood (8-connected): N, NE, E, SE, S, SW, W, NW
    const directions = [
      { x: 0, y: -1 }, // N
      { x: 1, y: -1 }, // NE
      { x: 1, y: 0 }, // E
      { x: 1, y: 1 }, // SE
      { x: 0, y: 1 }, // S
      { x: -1, y: 1 }, // SW
      { x: -1, y: 0 }, // W
      { x: -1, y: -1 }, // NW
    ];

    let cx = startX;
    let cy = startY;
    let dir = 0; // Current search direction index

    // Find initial direction (first black pixel or out-of-bounds)
    let foundStart = false;
    for (let i = 0; i < 8; i++) {
      const nx = cx + directions[i].x;
      const ny = cy + directions[i].y;
      // Consider out-of-bounds as black (background)
      const isBlack = nx < 0 || nx >= width || ny < 0 || ny >= height || grid[ny * width + nx] === 0;
      if (isBlack) {
        dir = i;
        foundStart = true;
        break;
      }
    }

    if (!foundStart) return createPolygon([{ x: startX, y: startY }], false);

    // MEMORY SAFETY: Limit max contour points to prevent OOM
    // For huge masks, we need much more aggressive limits
    const MAX_CONTOUR_POINTS = 50000; // Hard limit to prevent memory explosion
    const maxSteps = Math.min(width * height, MAX_CONTOUR_POINTS * 2); // Allow some tracing but cap it
    let steps = 0;

    let lastDir = dir;
    let lastAddedX = cx;
    let lastAddedY = cy;

    do {
      // MEMORY SAFETY: Stop if contour is getting too large
      if (contour.length >= MAX_CONTOUR_POINTS) {
        console.warn(`   ⚠️  Contour trace limit reached (${MAX_CONTOUR_POINTS} points), stopping early`);
        break;
      }

      // Only add point if direction changed (skip collinear points)
      // This dramatically reduces point count while preserving shape
      if (contour.length === 0 || dir !== lastDir) {
        // Add previous position when direction changes
        if (contour.length > 0 && (lastAddedX !== cx || lastAddedY !== cy)) {
          contour.push({ x: lastAddedX, y: lastAddedY });
        } else if (contour.length === 0) {
          contour.push({ x: cx, y: cy });
        }
        lastAddedX = cx;
        lastAddedY = cy;
        lastDir = dir;
      }

      // Search for next boundary pixel (clockwise from current direction)
      let found = false;
      for (let i = 0; i < 8; i++) {
        const searchDir = (dir + i) % 8;
        const nx = cx + directions[searchDir].x;
        const ny = cy + directions[searchDir].y;

        if (nx >= 0 && nx < width && ny >= 0 && ny < height && grid[ny * width + nx] === 1) {
          // Found next white pixel
          lastAddedX = cx;
          lastAddedY = cy;
          cx = nx;
          cy = ny;
          // Update direction (turn counter-clockwise from current search direction)
          dir = (searchDir + 6) % 8; // Start next search from ~90° counter-clockwise
          found = true;
          break;
        }
      }

      if (!found) break;

      steps++;
    } while ((cx !== startX || cy !== startY) && steps < maxSteps);

    // Add final point if it's different
    if (lastAddedX !== cx || lastAddedY !== cy) {
      contour.push({ x: cx, y: cy });
    }

    return createPolygon(contour, false);
  }

  /**
   * Flip coordinates (y-axis flip for image coordinate systems)
   */

  /**
   * Weld points that are too close together by choosing the weld position that minimizes area change
   * @param polygon Polygon in world space (cm)
   * @param minDistance Minimum distance in centimeters
   */
  private weldClosePoints(polygon: Polygon, minDistance: number): Polygon {
    if (polygon.length < 3) return polygon;

    const maxIterations = 100;
    let current = polygon;
    let iteration = 0;

    while (iteration < maxIterations) {
      const points = current.points.slice();
      const originalArea = Math.abs(this.calculatePolygonArea(current));
      let anyWelded = false;

      // Find pairs of points that need welding
      for (let i = 0; i < points.length; i++) {
        const curr = points[i];
        const nextIdx = (i + 1) % points.length;
        const next = points[nextIdx];
        const dist = Math.sqrt((next.x - curr.x) ** 2 + (next.y - curr.y) ** 2);

        if (dist < minDistance) {
          // These points need to be welded
          // Determine which point sits at a sharper corner angle so we preserve it.
          // Compute the interior angle at each of the two candidates using their
          // respective neighbours in the polygon.
          const n = points.length;
          const anglAt = (idx: number): number => {
            const p = points[(idx - 1 + n) % n];
            const c = points[idx];
            const q = points[(idx + 1) % n];
            const v1x = p.x - c.x,
              v1y = p.y - c.y;
            const v2x = q.x - c.x,
              v2y = q.y - c.y;
            const len1 = Math.sqrt(v1x * v1x + v1y * v1y);
            const len2 = Math.sqrt(v2x * v2x + v2y * v2y);
            if (len1 < 1e-10 || len2 < 1e-10) return Math.PI; // degenerate → treat as straight
            const cos = (v1x * v2x + v1y * v2y) / (len1 * len2);
            return Math.acos(Math.max(-1, Math.min(1, cos)));
          };

          const angleCurr = anglAt(i);
          const angleNext = anglAt(nextIdx);

          // The point with the smaller angle is at a sharper corner — keep it.
          // (collinear = π, 90° corner = π/2, so smaller = sharper)
          const keepIdx = angleCurr <= angleNext ? i : nextIdx;
          const removeIdx = keepIdx === i ? nextIdx : i;

          let weldPosition: { x: number; y: number };

          // For degenerate points (essentially on top of each other), keep the sharper corner
          if (dist < 0.001) {
            weldPosition = points[keepIdx];
          } else {
            // Try welding to curr position, next position, and midpoint
            // Choose the one that minimizes area change
            const midpoint = { x: (curr.x + next.x) / 2, y: (curr.y + next.y) / 2 };
            const candidates = [
              // Prefer the sharper-corner point, then midpoint, then the other point
              { pos: points[keepIdx], name: 'keep', priority: 0 },
              { pos: midpoint, name: 'midpoint', priority: 1 },
              { pos: points[removeIdx], name: 'remove', priority: 2 },
            ];

            let bestCandidate = candidates[0];
            let minAreaChange = Infinity;

            for (const candidate of candidates) {
              // Create test polygon with this weld position
              const testPoints = points.slice();
              testPoints[keepIdx] = candidate.pos;
              // Remove the other point
              testPoints.splice(removeIdx, 1);

              if (testPoints.length >= 3) {
                const testPolygon = createPolygon(testPoints, polygon.isHole);
                const testArea = Math.abs(this.calculatePolygonArea(testPolygon));
                const areaChange = Math.abs(testArea - originalArea);

                // Use epsilon comparison for near-identical area changes
                const epsilon = 0.0001; // Very small threshold for area comparison
                if (areaChange < minAreaChange - epsilon || (Math.abs(areaChange - minAreaChange) < epsilon && candidate.priority < bestCandidate.priority)) {
                  minAreaChange = areaChange;
                  bestCandidate = candidate;
                }
              }
            }

            weldPosition = bestCandidate.pos;
          }

          // Apply the weld — keep the sharper-corner point, remove the other
          points[keepIdx] = weldPosition;
          points.splice(removeIdx, 1);
          anyWelded = true;
          break; // Restart from beginning after welding
        }
      }

      if (anyWelded && points.length >= 3) {
        current = createPolygon(points, polygon.isHole);
        iteration++;
      } else {
        break;
      }
    }

    return current.length >= 3 ? current : polygon;
  }

  /**
   * Fix degenerate edges in a polygon
   * - Remove duplicate consecutive points
   * - Collapse very short edges
   * - Detect and fix self-intersections
   * Runs recursively until no more edges are removed
   * @param polygon Polygon in world space (cm)
   * @param minEdgeLength Minimum edge length in centimeters
   */
  private fixDegenerateEdges(polygon: Polygon, minEdgeLength = 1.0): Polygon {
    if (polygon.length < 3) return polygon;

    const maxIterations = 100;
    let current = polygon;
    let iteration = 0;

    while (iteration < maxIterations) {
      const fixed: Point2D[] = [];
      let removedCount = 0;

      for (let i = 0; i < current.length; i++) {
        const curr = current[i];
        const next = current[(i + 1) % current.length];

        // Calculate edge length
        const dx = next.x - curr.x;
        const dy = next.y - curr.y;
        const edgeLength = Math.sqrt(dx * dx + dy * dy);

        // Skip this point if the edge to the next point is too short
        if (edgeLength < minEdgeLength) {
          removedCount++;
          continue;
        }

        // Check for duplicate points (exact match)
        if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
          removedCount++;
          continue;
        }

        // Check for overlapping connected edges (hairpin/fold-back):
        // If edges prev→curr and curr→next are collinear and pointing in opposite
        // directions, remove curr (the common vertex) so prev connects directly to next.
        const prev = current[(i - 1 + current.length) % current.length];
        const prevDx = curr.x - prev.x;
        const prevDy = curr.y - prev.y;
        const prevLen = Math.sqrt(prevDx * prevDx + prevDy * prevDy);
        if (prevLen > 1e-6 && edgeLength > 1e-6) {
          // Dot product of normalized edge directions
          const dot = (prevDx / prevLen) * (dx / edgeLength) + (prevDy / prevLen) * (dy / edgeLength);
          // Cross product magnitude for collinearity check
          const cross = Math.abs((prevDx / prevLen) * (dy / edgeLength) - (prevDy / prevLen) * (dx / edgeLength));
          // Edges are overlapping if nearly collinear (cross ≈ 0) and pointing in opposite directions (dot ≈ -1)
          if (dot < -0.95 && cross < 0.1) {
            removedCount++;
            continue;
          }
        }

        fixed.push(curr);
      }

      // Additional check: ensure first and last points aren't too close
      if (fixed.length >= 3) {
        const first = fixed[0];
        const last = fixed[fixed.length - 1];
        const dist = Math.sqrt((last.x - first.x) ** 2 + (last.y - first.y) ** 2);
        if (dist < minEdgeLength) {
          fixed.pop();
          removedCount++;
        }
      }

      // Detect and fix self-intersections (simple case: consecutive edges crossing)
      const fixedSelfIntersections = this.fixSelfIntersections(fixed);

      if (removedCount > 0 && fixedSelfIntersections.length >= 3 && fixedSelfIntersections.length < current.length) {
        // Some edges were removed, continue
        current = createPolygon(fixedSelfIntersections, polygon.isHole);
        iteration++;
      } else {
        // No change or invalid result, stop
        if (fixedSelfIntersections.length >= 3) {
          current = createPolygon(fixedSelfIntersections, polygon.isHole);
        }
        break;
      }
    }

    return current.length >= 3 ? current : polygon;
  }

  /**
   * Fix simple self-intersections where consecutive edges cross each other
   */
  private fixSelfIntersections(points: Point2D[]): Point2D[] {
    if (points.length < 4) return points;

    const fixed: Point2D[] = [...points];
    let changed = true;
    let iterations = 0;
    const maxIterations = 10;

    while (changed && iterations < maxIterations) {
      changed = false;
      iterations++;

      // Check each edge against non-adjacent edges
      for (let i = 0; i < fixed.length; i++) {
        const p1 = fixed[i];
        const p2 = fixed[(i + 1) % fixed.length];

        // Check against edges at least 2 positions away
        for (let j = i + 2; j < fixed.length; j++) {
          // Don't check the last edge against the first edge (they share a vertex)
          if (i === 0 && j === fixed.length - 1) continue;

          const p3 = fixed[j];
          const p4 = fixed[(j + 1) % fixed.length];

          // Check if segments intersect
          const intersection = this.segmentIntersection(p1, p2, p3, p4);
          if (intersection) {
            // Found intersection - swap the order of points between the edges
            const segment1 = fixed.slice(i + 1, j + 1);
            segment1.reverse();

            fixed.splice(i + 1, j - i, ...segment1);
            changed = true;
            break;
          }
        }

        if (changed) break;
      }
    }

    return fixed;
  }

  /**
   * Check if two line segments intersect and return the intersection point
   */
  private segmentIntersection(p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D): Point2D | null {
    const d1x = p2.x - p1.x;
    const d1y = p2.y - p1.y;
    const d2x = p4.x - p3.x;
    const d2y = p4.y - p3.y;

    const denominator = d1x * d2y - d1y * d2x;

    // Parallel or coincident lines
    if (Math.abs(denominator) < 1e-10) return null;

    const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denominator;
    const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / denominator;

    // Check if intersection is within both segments (excluding endpoints)
    if (t > 0.001 && t < 0.999 && u > 0.001 && u < 0.999) {
      return {
        x: p1.x + t * d1x,
        y: p1.y + t * d1y,
      };
    }

    return null;
  }

  /**
   * Remove vertices with sharp angles (less than minimum angle threshold)
   * Runs recursively until no more vertices are removed
   */
  private removeSharpAngles(polygon: Polygon, minAngleDegrees: number): Polygon {
    if (polygon.length < 3) return polygon;

    const minAngleRad = (minAngleDegrees * Math.PI) / 180;
    const maxIterations = 100;
    let current = polygon;
    let iteration = 0;

    while (iteration < maxIterations) {
      const filtered: Point2D[] = [];

      for (let i = 0; i < current.length; i++) {
        const prev = current[(i - 1 + current.length) % current.length];
        const curr = current[i];
        const next = current[(i + 1) % current.length];

        // Calculate vectors from current point
        const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
        const v2 = { x: next.x - curr.x, y: next.y - curr.y };

        // Calculate angle between vectors
        const v1Len = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
        const v2Len = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

        if (v1Len < 1e-10 || v2Len < 1e-10) {
          // Can't calculate angle for degenerate edges, keep the vertex
          filtered.push(curr);
          continue;
        }

        // Dot product and angle
        const dotProduct = v1.x * v2.x + v1.y * v2.y;
        const cosAngle = dotProduct / (v1Len * v2Len);
        const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle))); // Clamp to [-1, 1] for numerical stability

        // Keep vertex if angle is greater than threshold
        if (angle >= minAngleRad) {
          filtered.push(curr);
        }
      }

      if (filtered.length >= 3 && filtered.length < current.length) {
        // Some vertices were removed, continue
        current = createPolygon(filtered, polygon.isHole);
        iteration++;
      } else {
        // No change or invalid result, stop
        break;
      }
    }

    return current.length >= 3 ? current : polygon;
  }

  /**
   * Remove noise by detecting and removing rapid small-scale direction changes
   * Recursively processes until no more noise is detected
   */
  private removeNoise(polygon: Polygon, windowSize = 5, deviationThreshold = 1.2, smoothingWeight = 0.8): Polygon {
    if (polygon.length < 5) return polygon;

    // Calculate polygon scale (characteristic length)
    const bbox = this.getPolygonBounds(polygon);
    const scale = Math.sqrt((bbox.maxX - bbox.minX) ** 2 + (bbox.maxY - bbox.minY) ** 2);
    const minFeatureSize = scale * 0.05; // Features smaller than 5% of diagonal are considered noise

    const maxIterations = 100;
    let current = polygon;
    let iteration = 0;

    while (iteration < maxIterations) {
      const points = current.points;
      const n = points.length;

      // First pass: identify noisy vertices and long edges
      const isNoisy = new Array(n).fill(false);
      const isLongEdge = new Array(n).fill(false);
      const longEdgeThreshold = minFeatureSize * 1.2; // Edges longer than 1.2x min feature size are "long"

      for (let i = 0; i < n; i++) {
        const prev = points[(i - 1 + n) % n];
        const curr = points[i];
        const next = points[(i + 1) % n];

        // Calculate edge vectors
        const v1 = { x: curr.x - prev.x, y: curr.y - prev.y };
        const v2 = { x: next.x - curr.x, y: next.y - curr.y };
        const v1Len = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
        const v2Len = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

        // Mark vertices that are endpoints of long edges
        if (v1Len >= longEdgeThreshold) {
          isLongEdge[i] = true;
          isLongEdge[(i - 1 + n) % n] = true;
        }
        if (v2Len >= longEdgeThreshold) {
          isLongEdge[i] = true;
          isLongEdge[(i + 1) % n] = true;
        }

        if (v1Len < 1e-10 || v2Len < 1e-10) {
          continue;
        }

        // Calculate angle change
        const dotProduct = v1.x * v2.x + v1.y * v2.y;
        const cosAngle = dotProduct / (v1Len * v2Len);
        const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle)));

        // Calculate perpendicular distance from prev-next line
        const directDist = Math.sqrt((next.x - prev.x) ** 2 + (next.y - prev.y) ** 2);
        let perpDist = 0;
        if (directDist > 1e-10) {
          const A = next.y - prev.y;
          const B = prev.x - next.x;
          const C = next.x * prev.y - prev.x * next.y;
          perpDist = Math.abs(A * curr.x + B * curr.y + C) / Math.sqrt(A * A + B * B);
        }

        // Check if this is a small-scale rapid direction change
        // Sharp angles (close to 0°) on small edges are noise
        // 90° angles are legitimate corners and should be preserved
        const isSmallEdge = v1Len < minFeatureSize || v2Len < minFeatureSize;
        const isVerySharpAngle = angle < Math.PI / 4; // Less than 45 degrees - definitely noise
        const isSomewhatSharpAngle = angle < Math.PI / 2.5; // Less than 72 degrees - noise if small
        const isSmallDeviation = perpDist < minFeatureSize * 2.0;

        // More aggressive on very sharp angles, less on moderate angles
        if (isSmallEdge && isVerySharpAngle && isSmallDeviation) {
          isNoisy[i] = true;
        } else if (isSmallEdge && isSomewhatSharpAngle && isSmallDeviation && (v1Len < minFeatureSize * 0.5 || v2Len < minFeatureSize * 0.5)) {
          // Only mark as noise if edges are very small (< 50% of min feature size)
          isNoisy[i] = true;
        }
      }

      // Second pass: only smooth vertices that are part of a cluster (2+ consecutive noisy vertices)
      // and don't smooth vertices that are part of long edges
      const filtered: Point2D[] = [];
      let changesMade = false;

      for (let i = 0; i < n; i++) {
        const curr = points[i];

        // Never smooth vertices that are part of long edges
        if (isLongEdge[i]) {
          filtered.push(curr);
          continue;
        }

        if (isNoisy[i]) {
          // Check if this vertex is part of a cluster (has noisy neighbors)
          const prevIdx = (i - 1 + n) % n;
          const nextIdx = (i + 1) % n;
          const nextNextIdx = (i + 2) % n;
          const prevPrevIdx = (i - 2 + n) % n;

          const hasNoisyNeighbor = isNoisy[prevIdx] || isNoisy[nextIdx] || isNoisy[nextNextIdx] || isNoisy[prevPrevIdx];

          if (hasNoisyNeighbor) {
            // Part of a cluster - smooth it
            const prev = points[(i - 1 + n) % n];
            const next = points[(i + 1) % n];
            filtered.push({
              x: ((prev.x + next.x) / 2) * smoothingWeight + curr.x * (1 - smoothingWeight),
              y: ((prev.y + next.y) / 2) * smoothingWeight + curr.y * (1 - smoothingWeight),
            });
            changesMade = true;
          } else {
            // Isolated noisy vertex - keep it (might be a legitimate feature)
            filtered.push(curr);
          }
        } else {
          filtered.push(curr);
        }
      }

      if (changesMade && filtered.length >= 3) {
        current = createPolygon(filtered, polygon.isHole);
        iteration++;
      } else {
        break;
      }
    }

    return current.length >= 3 ? current : polygon;
  }

  /**
   * Simplify polygon using Douglas-Peucker algorithm
   * @param polygon Polygon in world space (cm)
   * @param tolerance When relative=false: tolerance in centimeters.
   *                  When relative=true: tolerance as percentage of bounding box diagonal (e.g. 0.5 = 0.5%)
   * @param relative If true, tolerance is interpreted as % of bounding box diagonal
   */
  private simplifyPolygon(polygon: Polygon, tolerance: number, relative = false): Polygon {
    if (polygon.length < 3) return polygon;

    let epsilon = tolerance;
    if (relative) {
      const bbox = this.getPolygonBounds(polygon);
      const diagonal = Math.sqrt((bbox.maxX - bbox.minX) ** 2 + (bbox.maxY - bbox.minY) ** 2);
      epsilon = diagonal * (tolerance / 100);
    }

    const douglasPeucker = (points: Point2D[]): Point2D[] => {
      if (points.length < 3) return points;

      let maxDist = 0;
      let maxIndex = 0;

      const start = points[0];
      const end = points[points.length - 1];

      for (let i = 1; i < points.length - 1; i++) {
        const dist = this.pointToLineDistance(points[i], start, end);
        if (dist > maxDist) {
          maxDist = dist;
          maxIndex = i;
        }
      }

      if (maxDist > epsilon) {
        const left = douglasPeucker(points.slice(0, maxIndex + 1));
        const right = douglasPeucker(points.slice(maxIndex));
        return [...left.slice(0, -1), ...right];
      } else {
        return [start, end];
      }
    };

    // For closed polygons, naive Douglas-Peucker from points[0] to points[last]
    // collapses everything when start ≈ end (the "line" has zero length so all
    // distances are measured from a single point — a small polygon at 4% tolerance
    // easily falls within that radius and is destroyed).
    //
    // Fix: find the two farthest-apart vertices and split the ring there.
    // Run Douglas-Peucker on each half independently, then stitch together.
    const pts = polygon.points;
    const n = pts.length;

    // Find the pair of vertices with maximum distance (polygon diameter)
    let splitA = 0;
    let splitB = 0;
    let maxPairDist = 0;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = pts[j].x - pts[i].x;
        const dy = pts[j].y - pts[i].y;
        const d = dx * dx + dy * dy;
        if (d > maxPairDist) {
          maxPairDist = d;
          splitA = i;
          splitB = j;
        }
      }
    }

    // Build two open halves: A→B and B→A (wrapping around)
    const halfAB = pts.slice(splitA, splitB + 1);
    const halfBA = [...pts.slice(splitB), ...pts.slice(0, splitA + 1)];

    const simplifiedAB = douglasPeucker(halfAB);
    const simplifiedBA = douglasPeucker(halfBA);

    // Stitch: drop duplicate join points
    const result = [...simplifiedAB.slice(0, -1), ...simplifiedBA.slice(0, -1)];

    if (result.length < 3) return polygon;
    return createPolygon(result, polygon.isHole);
  }

  /**
   * Calculate point-to-line distance
   */
  private pointToLineDistance(point: Point2D, lineStart: Point2D, lineEnd: Point2D): number {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);
    }

    const t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSq;
    const tClamped = Math.max(0, Math.min(1, t));

    const projX = lineStart.x + tClamped * dx;
    const projY = lineStart.y + tClamped * dy;

    return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
  }

  /**
   * Alias for pointToLineDistance (edge is a line segment)
   */
  private pointToEdgeDistance(point: Point2D, edgeStart: Point2D, edgeEnd: Point2D): number {
    return this.pointToLineDistance(point, edgeStart, edgeEnd);
  }

  /**
   * Find the closest point on an edge (line segment) to a given point
   */
  private closestPointOnEdge(point: Point2D, edgeStart: Point2D, edgeEnd: Point2D): Point2D {
    const dx = edgeEnd.x - edgeStart.x;
    const dy = edgeEnd.y - edgeStart.y;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      return { x: edgeStart.x, y: edgeStart.y };
    }

    const t = ((point.x - edgeStart.x) * dx + (point.y - edgeStart.y) * dy) / lengthSq;
    const tClamped = Math.max(0, Math.min(1, t));

    return {
      x: edgeStart.x + tClamped * dx,
      y: edgeStart.y + tClamped * dy,
    };
  }

  /**
   * Check if an edge (line segment) intersects with an axis-aligned box
   */
  private edgeIntersectsBox(edgeStart: Point2D, edgeEnd: Point2D, boxMinX: number, boxMinY: number, boxMaxX: number, boxMaxY: number): boolean {
    // Check if either endpoint is inside the box
    if ((edgeStart.x >= boxMinX && edgeStart.x <= boxMaxX && edgeStart.y >= boxMinY && edgeStart.y <= boxMaxY) || (edgeEnd.x >= boxMinX && edgeEnd.x <= boxMaxX && edgeEnd.y >= boxMinY && edgeEnd.y <= boxMaxY)) {
      return true;
    }

    // Check if edge intersects any of the 4 box edges using line segment intersection
    const boxEdges = [
      [
        { x: boxMinX, y: boxMinY },
        { x: boxMaxX, y: boxMinY },
      ], // bottom
      [
        { x: boxMaxX, y: boxMinY },
        { x: boxMaxX, y: boxMaxY },
      ], // right
      [
        { x: boxMaxX, y: boxMaxY },
        { x: boxMinX, y: boxMaxY },
      ], // top
      [
        { x: boxMinX, y: boxMaxY },
        { x: boxMinX, y: boxMinY },
      ], // left
    ];

    for (const [boxEdgeStart, boxEdgeEnd] of boxEdges) {
      if (this.lineSegmentsIntersect(edgeStart, edgeEnd, boxEdgeStart, boxEdgeEnd)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if two line segments intersect
   */
  private lineSegmentsIntersect(p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D): boolean {
    // Use cross product to determine orientation
    const d1 = this.crossProduct(p3, p4, p1);
    const d2 = this.crossProduct(p3, p4, p2);
    const d3 = this.crossProduct(p1, p2, p3);
    const d4 = this.crossProduct(p1, p2, p4);

    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
      return true;
    }

    // Check collinear cases
    if (d1 === 0 && this.onSegment(p3, p1, p4)) return true;
    if (d2 === 0 && this.onSegment(p3, p2, p4)) return true;
    if (d3 === 0 && this.onSegment(p1, p3, p2)) return true;
    if (d4 === 0 && this.onSegment(p1, p4, p2)) return true;

    return false;
  }

  /**
   * Check if point q lies on segment pr (when collinear)
   */
  private onSegment(p: Point2D, q: Point2D, r: Point2D): boolean {
    return q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) && q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);
  }

  /**
   * Calculate polygon area (shoelace formula)
   */
  private polygonArea(polygon: Polygon): number {
    let area = 0;
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      area += polygon[i].x * polygon[j].y;
      area -= polygon[j].x * polygon[i].y;
    }
    return Math.abs(area) / 2;
  }

  /**
   * Find minimum area bounding rectangle using rotating calipers
   */
  private minAreaRect(polygon: Polygon): { angle: number; width: number; height: number; center: Point2D } {
    // Use rotating calipers algorithm to find minimum area bounding box
    // This is more robust than PCA for rectangular rooms (matches Python cv2.minAreaRect)

    if (polygon.length < 3) {
      return { angle: 0, width: 0, height: 0, center: { x: 0, y: 0 } };
    }

    // Compute convex hull first (rotating calipers works on convex polygons)
    const hull = this.convexHull(polygon);

    if (hull.length < 3) {
      return { angle: 0, width: 0, height: 0, center: { x: 0, y: 0 } };
    }

    let minArea = Infinity;
    let bestAngle = 0;
    let bestWidth = 0;
    let bestHeight = 0;
    let bestCenter = { x: 0, y: 0 };

    // Try each edge of the convex hull as a potential side of the bounding box
    for (let i = 0; i < hull.length; i++) {
      const p1 = hull[i];
      const p2 = hull[(i + 1) % hull.length];

      // Calculate edge direction
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const edgeAngle = Math.atan2(dy, dx);

      // Rotate all points so edge aligns with x-axis
      const cos = Math.cos(-edgeAngle);
      const sin = Math.sin(-edgeAngle);
      const rotatedPoints = hull.points.map((p) => ({
        x: p.x * cos - p.y * sin,
        y: p.x * sin + p.y * cos,
      }));

      // Find bounding box of rotated points
      const bounds = this.getPolygonBounds(createPolygon(rotatedPoints, false));
      const width = bounds.maxX - bounds.minX;
      const height = bounds.maxY - bounds.minY;
      const area = width * height;

      if (area < minArea) {
        minArea = area;
        bestAngle = edgeAngle;
        bestWidth = width;
        bestHeight = height;

        // Calculate center in original coordinate system
        const centerRotated = {
          x: (bounds.minX + bounds.maxX) / 2,
          y: (bounds.minY + bounds.maxY) / 2,
        };
        bestCenter = {
          x: centerRotated.x * Math.cos(edgeAngle) - centerRotated.y * Math.sin(edgeAngle),
          y: centerRotated.x * Math.sin(edgeAngle) + centerRotated.y * Math.cos(edgeAngle),
        };
      }
    }

    return { angle: bestAngle, width: bestWidth, height: bestHeight, center: bestCenter };
  }

  /**
   * Convert a polygon to its axis-aligned bounding rectangle
   * Returns a 4-point polygon representing the bounding rectangle (always horizontal, not rotated)
   */
  private polygonToBoundingRectangle(polygon: Polygon): Polygon {
    if (polygon.length < 3) {
      return polygon;
    }

    // Get axis-aligned bounding box
    const bounds = this.getPolygonBounds(polygon);

    // Generate the 4 corners of the axis-aligned rectangle
    const corners: Point2D[] = [
      { x: bounds.minX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.minX, y: bounds.maxY },
    ];

    return createPolygon(corners, polygon.isHole);
  }

  /**
   * Convert a polygon to its closest-fit (minimum-area) rectangle.
   * Unlike the axis-aligned bounding rectangle, this rectangle is rotated to hug
   * the polygon as tightly as possible (rotating-calipers min-area rect), which
   * is the right fit for rooms/structures that aren't axis-aligned.
   * Returns a 4-point rotated rectangle polygon.
   */
  private polygonToMinAreaRectangle(polygon: Polygon): Polygon {
    if (polygon.length < 3) {
      return polygon;
    }

    const { angle, width, height, center } = this.minAreaRect(polygon);
    if (width === 0 || height === 0) {
      return polygon;
    }

    // Build the 4 corners in the rectangle's local frame, then rotate by `angle`
    // and translate to `center` (matching minAreaRect's center convention).
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const hw = width / 2;
    const hh = height / 2;
    const local: Point2D[] = [
      { x: -hw, y: -hh },
      { x: hw, y: -hh },
      { x: hw, y: hh },
      { x: -hw, y: hh },
    ];
    const corners: Point2D[] = local.map((p) => ({
      x: center.x + p.x * cos - p.y * sin,
      y: center.y + p.x * sin + p.y * cos,
    }));

    return createPolygon(corners, polygon.isHole);
  }

  /**
   * Compute convex hull using Graham scan algorithm
   */
  private convexHull(points: Polygon): Polygon {
    if (points.length < 3) return createPolygon(points.points, points.isHole);

    // Find the point with lowest y-coordinate (and leftmost if tied)
    let start = points[0];
    for (const p of points) {
      if (p.y < start.y || (p.y === start.y && p.x < start.x)) {
        start = p;
      }
    }

    // Sort points by polar angle with respect to start point
    const sorted = points.points.slice().sort((a, b) => {
      if (a === start) return -1;
      if (b === start) return 1;

      const angleA = Math.atan2(a.y - start.y, a.x - start.x);
      const angleB = Math.atan2(b.y - start.y, b.x - start.x);

      if (angleA !== angleB) return angleA - angleB;

      // If angles are equal, sort by distance
      const distA = (a.x - start.x) ** 2 + (a.y - start.y) ** 2;
      const distB = (b.x - start.x) ** 2 + (b.y - start.y) ** 2;
      return distA - distB;
    });

    // Build hull using Graham scan
    const hull: Point2D[] = [sorted[0], sorted[1]];

    for (let i = 2; i < sorted.length; i++) {
      let top = hull[hull.length - 1];
      let nextTop = hull[hull.length - 2];

      // Remove points that make clockwise turn
      while (hull.length >= 2 && this.crossProduct(nextTop, top, sorted[i]) <= 0) {
        hull.pop();
        top = hull[hull.length - 1];
        nextTop = hull[hull.length - 2];
      }

      hull.push(sorted[i]);
    }

    return createPolygon(hull, points.isHole);
  }

  /**
   * Calculate cross product of vectors (p2-p1) and (p3-p1)
   * Positive = counter-clockwise, Negative = clockwise
   */
  private crossProduct(p1: Point2D, p2: Point2D, p3: Point2D): number {
    return (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
  }

  /**
   * Calculate polygon centroid
   */
  private polygonCentroid(polygon: Polygon): Point2D {
    let cx = 0,
      cy = 0;
    for (const p of polygon) {
      cx += p.x;
      cy += p.y;
    }
    return { x: cx / polygon.length, y: cy / polygon.length };
  }

  /**
   * Get polygon bounding box
   */
  private getPolygonBounds(polygon: Polygon): { minX: number; maxX: number; minY: number; maxY: number } {
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

    for (const p of polygon) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }

    return { minX, maxX, minY, maxY };
  }

  /**
   * Fit a line through points using least squares (PCA approach)
   */
  private fitLine(points: Point2D[]): { point: Point2D; direction: Point2D } {
    const center = this.polygonCentroid(createPolygon(points, false));
    const centered = points.map((p) => ({ x: p.x - center.x, y: p.y - center.y }));

    // Covariance matrix
    let cxx = 0,
      cyy = 0,
      cxy = 0;
    for (const p of centered) {
      cxx += p.x * p.x;
      cyy += p.y * p.y;
      cxy += p.x * p.y;
    }

    // Eigenvector of largest eigenvalue gives principal direction
    const trace = cxx + cyy;
    const det = cxx * cyy - cxy * cxy;
    const lambda = trace / 2 + Math.sqrt((trace * trace) / 4 - det);

    const direction = { x: cxy, y: lambda - cxx };
    const dirLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y);

    return {
      point: center,
      direction: { x: direction.x / dirLength, y: direction.y / dirLength },
    };
  }

  /**
   * Fit a line through points using RANSAC to robustly handle outliers.
   *
   * DETERMINISTIC: the sampling PRNG is seeded from the point set itself,
   * so the same points always produce the same fit. On a double-layer wall
   * (a crisp line with a fuzzy outer coat) the samples straddle both
   * layers and a true-random RANSAC snaps an edge to a different layer on
   * every recompute — the polygon visibly changed between identical runs,
   * and downstream code had to avoid recomputing shapes the user had
   * already seen.
   *
   * @param points Points to fit
   * @param distanceThreshold Maximum distance from line for a point to be considered an inlier (pixels)
   * @param maxIterations Maximum number of RANSAC iterations
   * @param minInlierRatio Minimum ratio of inliers required for a valid fit (0-1)
   * @param anchor Optional preference point (the edge being aligned): among
   *   models whose inlier count is within `anchorTieRatio` of the best, the
   *   one whose line passes closest to the anchor wins. On double-layer
   *   walls both layers reach similar inlier counts; alignment is meant to
   *   REFINE the observed edge, not jump it to a parallel layer behind.
   * @param anchorTieRatio How close (fraction of best inlier count) a model
   *   must be to compete on anchor distance
   */
  private fitLineRANSAC(
    points: Point2D[],
    distanceThreshold = 2.0,
    maxIterations = 100,
    minInlierRatio = 0.6,
    anchor?: Point2D,
    anchorTieRatio = 0.8,
  ): { point: Point2D; direction: Point2D; inlierCount: number } | null {
    if (points.length < 2) return null;

    const minInliers = Math.max(2, Math.ceil(points.length * minInlierRatio));

    // Seed from the data (coordinates quantized to 0.01px), then mulberry32.
    let seed = points.length >>> 0;
    for (const p of points) {
      seed = (seed ^ (Math.round(p.x * 100) * 73856093) ^ (Math.round(p.y * 100) * 19349663)) >>> 0;
      seed = Math.imul(seed, 2654435761) >>> 0;
    }
    const random = (): number => {
      seed = (seed + 0x6d2b79f5) >>> 0;
      let t = seed;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    const candidates: Array<{ point: Point2D; direction: Point2D; inlierCount: number; anchorDist: number }> = [];
    let bestCount = 0;

    // RANSAC iterations
    for (let iter = 0; iter < maxIterations; iter++) {
      // Sample 2 points (deterministic PRNG, see above)
      const idx1 = Math.floor(random() * points.length);
      let idx2 = Math.floor(random() * points.length);
      while (idx2 === idx1) {
        idx2 = Math.floor(random() * points.length);
      }

      const p1 = points[idx1];
      const p2 = points[idx2];

      // Create line model from these two points
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length < 1e-6) continue; // Degenerate case

      const direction = { x: dx / length, y: dy / length };
      const point = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

      // Count inliers (points within distance threshold)
      let inlierCount = 0;
      for (const pt of points) {
        const distance = this.pointToInfiniteLineDistance(pt, point, direction);
        if (distance <= distanceThreshold) {
          inlierCount++;
        }
      }

      if (inlierCount >= 2) {
        candidates.push({
          point,
          direction,
          inlierCount,
          anchorDist: anchor ? this.pointToInfiniteLineDistance(anchor, point, direction) : 0,
        });
        bestCount = Math.max(bestCount, inlierCount);
      }

      // Early termination once the fit is essentially unanimous — but not
      // when an anchor competes, where near-tie models must all be seen.
      if (!anchor && bestCount >= points.length * 0.95) {
        break;
      }
    }

    // Check if we found a valid model
    if (bestCount < minInliers) {
      return null;
    }

    // Winner: most inliers — except that with an anchor, any near-tie
    // model (inliers within anchorTieRatio of the best) closer to the
    // anchor line wins instead.
    let winner = candidates[0];
    for (const candidate of candidates) {
      if (candidate.inlierCount > winner.inlierCount) winner = candidate;
    }
    if (anchor) {
      for (const candidate of candidates) {
        if (candidate.inlierCount >= bestCount * anchorTieRatio && candidate.anchorDist < winner.anchorDist - 1e-9) {
          winner = candidate;
        }
      }
    }

    // Refine the winner using all its inliers with least squares
    const inliers: Point2D[] = [];
    for (const pt of points) {
      if (this.pointToInfiniteLineDistance(pt, winner.point, winner.direction) <= distanceThreshold) {
        inliers.push(pt);
      }
    }
    const refinedModel = this.fitLine(inliers);

    return {
      point: refinedModel.point,
      direction: refinedModel.direction,
      inlierCount: inliers.length,
    };
  }

  /**
   * Calculate perpendicular distance from point to an infinite line
   * @param point The point
   * @param linePoint A point on the line
   * @param lineDirection Unit direction vector of the line
   */
  private pointToInfiniteLineDistance(point: Point2D, linePoint: Point2D, lineDirection: Point2D): number {
    // Vector from line point to test point
    const dx = point.x - linePoint.x;
    const dy = point.y - linePoint.y;

    // Project onto line direction
    const alongLine = dx * lineDirection.x + dy * lineDirection.y;

    // Perpendicular component
    const perpX = dx - alongLine * lineDirection.x;
    const perpY = dy - alongLine * lineDirection.y;

    return Math.sqrt(perpX * perpX + perpY * perpY);
  }

  /**
   * Adjust polygon edges based on prompt points (batch processor)
   * - Positive prompts: Only adjust the closest edge across ALL polygons
   * - Negative prompts: Adjust nearest edge in each polygon containing the point
   * - Handles nested holes automatically
   * @param polygons Polygons in world space (cm)
   */
  private adjustEdgesForPrompts(polygons: Polygon[]): Polygon[] {
    if (this.promptPoints.length === 0 || polygons.length === 0) {
      return polygons;
    }

    // Clone all polygons with their children
    const adjustedPolygons: Polygon[] = polygons.map((p) => {
      const cloned = createPolygon(p.points.slice(), p.isHole);
      if (p.children && p.children.length > 0) {
        cloned.children = p.children.map((child) => createPolygon(child.points.slice(), child.isHole));
      }
      return cloned;
    });

    // Flatten polygons and holes into a single list for processing
    const flatPolygons: { polygon: Polygon; parentIndex: number; isChild: boolean; childIndex: number }[] = [];
    for (let i = 0; i < adjustedPolygons.length; i++) {
      flatPolygons.push({ polygon: adjustedPolygons[i], parentIndex: i, isChild: false, childIndex: -1 });
      const children = adjustedPolygons[i].children;
      if (children) {
        for (let j = 0; j < children.length; j++) {
          flatPolygons.push({
            polygon: children[j],
            parentIndex: i,
            isChild: true,
            childIndex: j,
          });
        }
      }
    }

    // Filter out box and vertex prompts - they are handled separately
    // Only process regular point prompts for edge adjustment
    // Skip label=2 (remove_points) prompts - they only remove vertices, not adjust edges
    // Skip prompts that are inside orange boxes (label=2)
    const expandedPrompts: Array<{ x: number; y: number; label: number }> = [];
    for (const prompt of this.promptPoints) {
      // Skip box prompts - they are handled by applyBooleanOperationsForPrompts
      if (prompt.type === 'box') {
        continue;
      }

      // Skip line prompts - not processed yet
      if (prompt.type === 'line') {
        continue;
      }

      // Skip vertex prompts - they are handled by enforceVertexPrompts
      if (prompt.type === 'vertex') {
        continue;
      }

      // Skip remove_points prompts (label=2)
      if (prompt.label === 2) {
        continue;
      }

      // Skip prompts that are inside orange boxes
      if (this.isPromptInOrangeBox({ x: prompt.x, y: prompt.y })) {
        continue;
      }

      // Regular point prompt
      expandedPrompts.push({
        x: this.pixelsToCm(prompt.x),
        y: this.pixelsToCm(prompt.y),
        label: prompt.label,
      });
    }

    // Process each expanded prompt
    for (const prompt of expandedPrompts) {
      const promptPointInWorldSpace = {
        x: prompt.x,
        y: prompt.y,
      };
      const isPositive = prompt.label === 1;

      if (isPositive) {
        // Positive prompt: Check if inside any polygon or hole
        let isInsideAny = false;
        let isInHole = false;
        for (const item of flatPolygons) {
          if (this.isPointInPolygon(promptPointInWorldSpace, item.polygon)) {
            isInsideAny = true;
            // Check if it's in a hole
            if (item.polygon.isHole) {
              isInHole = true;
            }
            break;
          }
        }

        // Positive prompts in holes should be ignored (holes ignore positive prompts)
        if (isInHole) {
          continue;
        }

        // Only adjust if point is outside all polygons
        if (!isInsideAny) {
          // Find the single closest edge across ALL non-hole polygons
          let globalMinDistance = Infinity;
          let closestFlatIndex = -1;
          let closestEdgeIndex = -1;
          let closestPoint: Point2D | null = null;

          for (let flatIdx = 0; flatIdx < flatPolygons.length; flatIdx++) {
            const item = flatPolygons[flatIdx];
            // Skip holes for positive prompts
            if (item.polygon.isHole) continue;

            const { edgeIndex, closestPoint: edgeClosest, distance } = this.findNearestEdge(promptPointInWorldSpace, item.polygon);

            if (distance < globalMinDistance) {
              globalMinDistance = distance;
              closestFlatIndex = flatIdx;
              closestEdgeIndex = edgeIndex;
              closestPoint = edgeClosest;
            }
          }

          // Adjust only the closest polygon's closest edge
          if (closestFlatIndex >= 0 && closestPoint) {
            const closestItem = flatPolygons[closestFlatIndex];

            const adjustedPolygon = closestItem.polygon;
            const edgeStart = adjustedPolygon.points[closestEdgeIndex];
            const edgeEnd = adjustedPolygon.points[(closestEdgeIndex + 1) % adjustedPolygon.length];

            const edgeVector = { x: edgeEnd.x - edgeStart.x, y: edgeEnd.y - edgeStart.y };
            const edgeLength = Math.sqrt(edgeVector.x ** 2 + edgeVector.y ** 2);
            const edgeNormal = { x: -edgeVector.y / edgeLength, y: edgeVector.x / edgeLength };

            // Determine push direction (towards point for positive)
            const toPoint = {
              x: promptPointInWorldSpace.x - closestPoint.x,
              y: promptPointInWorldSpace.y - closestPoint.y,
            };
            const dotProduct = toPoint.x * edgeNormal.x + toPoint.y * edgeNormal.y;
            const pushDirection = dotProduct > 0 ? 1 : -1;

            // Calculate push distance - place edge exactly on the point
            const pushDistance = globalMinDistance;

            // Push edge vertices perpendicular to edge
            const offset = {
              x: edgeNormal.x * pushDistance * pushDirection,
              y: edgeNormal.y * pushDistance * pushDirection,
            };

            adjustedPolygon.points[closestEdgeIndex] = {
              x: edgeStart.x + offset.x,
              y: edgeStart.y + offset.y,
            };

            adjustedPolygon.points[(closestEdgeIndex + 1) % adjustedPolygon.length] = {
              x: edgeEnd.x + offset.x,
              y: edgeEnd.y + offset.y,
            };
          }
        }
      } else {
        // Negative prompt: First check if it's in any hole
        let isInAnyHole = false;
        for (const item of flatPolygons) {
          if (item.polygon.isHole && this.isPointInPolygon(promptPointInWorldSpace, item.polygon)) {
            isInAnyHole = true;
            break;
          }
        }

        // Negative prompts in holes should be ignored (holes represent empty space already)
        if (isInAnyHole) {
          continue;
        }

        // Cast rays to find equidistant edges
        for (let flatIdx = 0; flatIdx < flatPolygons.length; flatIdx++) {
          const item = flatPolygons[flatIdx];
          const adjustedPolygon = item.polygon;

          // Only adjust if point is inside this polygon
          if (!this.isPointInPolygon(promptPointInWorldSpace, adjustedPolygon)) {
            continue;
          }

          // Skip holes (already checked above)
          if (adjustedPolygon.isHole) {
            continue;
          }

          // Collect all edges: polygon edges + hole edges
          const allEdges: { polygon: Polygon; edgeIndex: number; isHole: boolean }[] = [];

          // Add main polygon edges
          for (let i = 0; i < adjustedPolygon.length; i++) {
            allEdges.push({ polygon: adjustedPolygon, edgeIndex: i, isHole: false });
          }

          // Add hole edges from children
          const parentPolygon = adjustedPolygons[item.parentIndex];
          if (parentPolygon.children && parentPolygon.children.length > 0) {
            for (const hole of parentPolygon.children) {
              for (let i = 0; i < hole.length; i++) {
                allEdges.push({ polygon: hole, edgeIndex: i, isHole: true });
              }
            }
          }

          // Cast rays in multiple directions to find equidistant edges
          const rayCount = 16;
          const distanceMarginCm = 2.0; // cm
          const distanceMargin = distanceMarginCm; // Already in world space (cm)
          const edgeHits = new Map<string, number[]>(); // "polygonType:edgeIndex" -> distances

          for (let rayIdx = 0; rayIdx < rayCount; rayIdx++) {
            const angle = (rayIdx / rayCount) * Math.PI * 2;
            const direction = { x: Math.cos(angle), y: Math.sin(angle) };

            // Find all edge intersections for this ray
            const hits: { edgeKey: string; polygon: Polygon; edgeIndex: number; distance: number; isHole: boolean }[] = [];

            for (const edge of allEdges) {
              const edgeStart = edge.polygon.points[edge.edgeIndex];
              const edgeEnd = edge.polygon.points[(edge.edgeIndex + 1) % edge.polygon.length];

              const hit = this.rayIntersectsEdge(promptPointInWorldSpace, direction, edgeStart, edgeEnd);
              if (hit) {
                const edgeKey = `${edge.isHole ? 'hole' : 'main'}:${edge.edgeIndex}`;
                hits.push({
                  edgeKey,
                  polygon: edge.polygon,
                  edgeIndex: edge.edgeIndex,
                  distance: hit.distance,
                  isHole: edge.isHole,
                });
              }
            }

            // Sort by distance and check for equidistant edges
            if (hits.length >= 2) {
              hits.sort((a, b) => a.distance - b.distance);
              const dist1 = hits[0].distance;
              const dist2 = hits[1].distance;

              if (Math.abs(dist1 - dist2) <= distanceMargin) {
                // Record these edges as potential candidates
                if (!edgeHits.has(hits[0].edgeKey)) edgeHits.set(hits[0].edgeKey, []);
                if (!edgeHits.has(hits[1].edgeKey)) edgeHits.set(hits[1].edgeKey, []);
                edgeHits.get(hits[0].edgeKey)?.push(dist1);
                edgeHits.get(hits[1].edgeKey)?.push(dist2);
              }
            }
          }

          // Find edges that are consistently equidistant (hit by multiple rays)
          const threshold = rayCount * 0.25; // At least 25% of rays
          const equidistantEdges: { edgeKey: string; avgDistance: number }[] = [];

          for (const [edgeKey, distances] of edgeHits) {
            if (distances.length >= threshold) {
              const avgDistance = distances.reduce((a, b) => a + b) / distances.length;
              equidistantEdges.push({ edgeKey, avgDistance });
            }
          }

          // Simplified approach: Find the single nearest edge among all edges
          let globalMinDistance = Infinity;
          let nearestEdge: { polygon: Polygon; edgeIndex: number; closestPoint: Point2D; isHole: boolean } | null = null;

          for (const edge of allEdges) {
            const { edgeIndex, closestPoint, distance } = this.findNearestEdge(promptPointInWorldSpace, edge.polygon);

            if (distance < globalMinDistance) {
              globalMinDistance = distance;
              nearestEdge = {
                polygon: edge.polygon,
                edgeIndex,
                closestPoint,
                isHole: edge.isHole,
              };
            }
          }

          if (nearestEdge) {
            const edgeStart = nearestEdge.polygon.points[nearestEdge.edgeIndex];
            const edgeEnd = nearestEdge.polygon.points[(nearestEdge.edgeIndex + 1) % nearestEdge.polygon.length];

            const edgeVector = { x: edgeEnd.x - edgeStart.x, y: edgeEnd.y - edgeStart.y };
            const edgeLength = Math.sqrt(edgeVector.x ** 2 + edgeVector.y ** 2);
            const edgeNormal = { x: -edgeVector.y / edgeLength, y: edgeVector.x / edgeLength };

            // Determine push direction (away from point for negative prompt)
            const toPoint = {
              x: promptPointInWorldSpace.x - nearestEdge.closestPoint.x,
              y: promptPointInWorldSpace.y - nearestEdge.closestPoint.y,
            };
            const dotProduct = toPoint.x * edgeNormal.x + toPoint.y * edgeNormal.y;
            const pushDirection = dotProduct > 0 ? 1 : -1; // Push in opposite direction of toPoint vector

            const pushDistance = globalMinDistance;

            const offset = {
              x: edgeNormal.x * pushDistance * pushDirection,
              y: edgeNormal.y * pushDistance * pushDirection,
            };

            nearestEdge.polygon.points[nearestEdge.edgeIndex] = {
              x: edgeStart.x + offset.x,
              y: edgeStart.y + offset.y,
            };

            nearestEdge.polygon.points[(nearestEdge.edgeIndex + 1) % nearestEdge.polygon.length] = {
              x: edgeEnd.x + offset.x,
              y: edgeEnd.y + offset.y,
            };
          }
        }
      }
    }

    return adjustedPolygons;
  }

  /**
   * Adjust polygon edges based on box prompt corners (batch processor)
   * Each corner of the box acts as a point prompt, but only for the closest polygon to the box
   * - Positive boxes: Push edges outward to include corners that are outside
   * - Negative boxes: Push edges inward to exclude corners that are inside
   * @param polygons Polygons in world space (cm)
   */
  private adjustEdgesForBoxPrompts(polygons: Polygon[]): Polygon[] {
    if (this.promptPoints.length === 0 || polygons.length === 0) {
      return polygons;
    }

    // Filter only box prompts, excluding label=2 (remove_points)
    const boxPrompts = this.promptPoints.filter((p) => p.type === 'box' && p.box?.worldCorners && p.label !== 2);
    if (boxPrompts.length === 0) {
      return polygons;
    }

    // Clone all polygons with their children
    const adjustedPolygons: Polygon[] = polygons.map((p) => {
      const cloned = createPolygon(p.points.slice(), p.isHole);
      if (p.children && p.children.length > 0) {
        cloned.children = p.children.map((child) => createPolygon(child.points.slice(), child.isHole));
      }
      return cloned;
    });

    // Flatten polygons and holes into a single list for processing
    const flatPolygons: { polygon: Polygon; parentIndex: number; isChild: boolean; childIndex: number }[] = [];
    for (let i = 0; i < adjustedPolygons.length; i++) {
      flatPolygons.push({ polygon: adjustedPolygons[i], parentIndex: i, isChild: false, childIndex: -1 });
      const children = adjustedPolygons[i].children;
      if (children) {
        for (let j = 0; j < children.length; j++) {
          flatPolygons.push({
            polygon: children[j],
            parentIndex: i,
            isChild: true,
            childIndex: j,
          });
        }
      }
    }

    // Process each box prompt
    for (const prompt of boxPrompts) {
      if (!prompt.box?.worldCorners) continue;

      const isPositive = prompt.label === 1;

      // Convert box corners to world space (cm)
      const boxCorners = prompt.box.worldCorners.map((c) => ({
        x: this.pixelsToCm(c.x),
        y: this.pixelsToCm(c.y),
      }));

      // Calculate box center
      const boxCenter = {
        x: boxCorners.reduce((sum, c) => sum + c.x, 0) / boxCorners.length,
        y: boxCorners.reduce((sum, c) => sum + c.y, 0) / boxCorners.length,
      };

      // Find the closest polygon to the box center (only non-hole polygons)
      let closestPolygonItem: { polygon: Polygon; parentIndex: number; isChild: boolean; childIndex: number } | null = null;
      let minDistanceToPolygon = Infinity;

      for (const item of flatPolygons) {
        if (item.polygon.isHole) continue;

        // Calculate distance from box center to polygon (use nearest edge distance)
        const { distance } = this.findNearestEdge(boxCenter, item.polygon);
        if (distance < minDistanceToPolygon) {
          minDistanceToPolygon = distance;
          closestPolygonItem = item;
        }
      }

      if (!closestPolygonItem) continue;

      const targetPolygon = closestPolygonItem.polygon;

      // Process each corner of the box as a point prompt
      for (const corner of boxCorners) {
        // Check if corner is inside or outside the polygon
        const isInsidePolygon = this.isPointInPolygon(corner, targetPolygon);

        if (isPositive && !isInsidePolygon) {
          // Positive box corner outside polygon: push nearest edge outward to include the corner
          const { edgeIndex, closestPoint, distance } = this.findNearestEdge(corner, targetPolygon);

          if (distance > 0) {
            const edgeStart = targetPolygon.points[edgeIndex];
            const edgeEnd = targetPolygon.points[(edgeIndex + 1) % targetPolygon.length];

            const edgeVector = { x: edgeEnd.x - edgeStart.x, y: edgeEnd.y - edgeStart.y };
            const edgeLength = Math.sqrt(edgeVector.x ** 2 + edgeVector.y ** 2);
            const edgeNormal = { x: -edgeVector.y / edgeLength, y: edgeVector.x / edgeLength };

            // Determine push direction (towards corner point)
            const toPoint = {
              x: corner.x - closestPoint.x,
              y: corner.y - closestPoint.y,
            };
            const dotProduct = toPoint.x * edgeNormal.x + toPoint.y * edgeNormal.y;
            const pushDirection = dotProduct > 0 ? 1 : -1;

            const offset = {
              x: edgeNormal.x * distance * pushDirection,
              y: edgeNormal.y * distance * pushDirection,
            };

            targetPolygon.points[edgeIndex] = {
              x: edgeStart.x + offset.x,
              y: edgeStart.y + offset.y,
            };

            targetPolygon.points[(edgeIndex + 1) % targetPolygon.length] = {
              x: edgeEnd.x + offset.x,
              y: edgeEnd.y + offset.y,
            };
          }
        } else if (!isPositive && isInsidePolygon) {
          // Negative box corner inside polygon: find edge to push
          // Priority: edges that intersect with box > closest edge
          const boxMinX = Math.min(...boxCorners.map((c) => c.x));
          const boxMaxX = Math.max(...boxCorners.map((c) => c.x));
          const boxMinY = Math.min(...boxCorners.map((c) => c.y));
          const boxMaxY = Math.max(...boxCorners.map((c) => c.y));

          // Find intersecting edges first, then fallback to closest
          let bestEdgeIndex = -1;
          let bestClosestPoint: Point2D | null = null;
          let bestDistance = Infinity;
          let foundIntersecting = false;

          for (let i = 0; i < targetPolygon.points.length; i++) {
            const edgeStart = targetPolygon.points[i];
            const edgeEnd = targetPolygon.points[(i + 1) % targetPolygon.length];
            const edgeIntersects = this.edgeIntersectsBox(edgeStart, edgeEnd, boxMinX, boxMinY, boxMaxX, boxMaxY);

            const dist = this.pointToEdgeDistance(corner, edgeStart, edgeEnd);

            // If we already found an intersecting edge, only consider other intersecting edges
            if (foundIntersecting && !edgeIntersects) continue;

            // If this is first intersecting edge, reset and use it
            if (edgeIntersects && !foundIntersecting) {
              foundIntersecting = true;
              bestEdgeIndex = i;
              bestDistance = dist;
              bestClosestPoint = this.closestPointOnEdge(corner, edgeStart, edgeEnd);
            } else if (dist < bestDistance) {
              bestEdgeIndex = i;
              bestDistance = dist;
              bestClosestPoint = this.closestPointOnEdge(corner, edgeStart, edgeEnd);
            }
          }

          if (bestEdgeIndex >= 0 && bestClosestPoint && bestDistance > 0) {
            const edgeStart = targetPolygon.points[bestEdgeIndex];
            const edgeEnd = targetPolygon.points[(bestEdgeIndex + 1) % targetPolygon.length];

            const edgeVector = { x: edgeEnd.x - edgeStart.x, y: edgeEnd.y - edgeStart.y };
            const edgeLength = Math.sqrt(edgeVector.x ** 2 + edgeVector.y ** 2);
            const edgeNormal = { x: -edgeVector.y / edgeLength, y: edgeVector.x / edgeLength };

            // Determine push direction (away from corner point)
            const toPoint = {
              x: corner.x - bestClosestPoint.x,
              y: corner.y - bestClosestPoint.y,
            };
            const dotProduct = toPoint.x * edgeNormal.x + toPoint.y * edgeNormal.y;
            const pushDirection = dotProduct > 0 ? 1 : -1;

            const offset = {
              x: edgeNormal.x * bestDistance * pushDirection,
              y: edgeNormal.y * bestDistance * pushDirection,
            };

            targetPolygon.points[bestEdgeIndex] = {
              x: edgeStart.x + offset.x,
              y: edgeStart.y + offset.y,
            };

            targetPolygon.points[(bestEdgeIndex + 1) % targetPolygon.length] = {
              x: edgeEnd.x + offset.x,
              y: edgeEnd.y + offset.y,
            };
          }
        }
      }
    }

    return adjustedPolygons;
  }

  /**
   * Adjust polygon edges based on line prompts (batch processor).
   * For each line prompt, casts perpendicular rays to find the closest contiguous
   * edge chain, then moves that chain to align with the prompt line.
   * Chain endpoint vertices slide along their neighbor edges to maintain connectivity.
   * @param polygons Polygons in world space (cm)
   * @param raySampleCount Number of sample points along the prompt line for ray casting
   * @param maxRayDistanceCm Maximum perpendicular distance to consider for edge hits
   * @param pullModeRatio If the prompt projection is shorter than this ratio of the chain span, use pull-out mode instead of snapping the entire chain (0-1)
   */
  private adjustEdgesForLinePrompts(polygons: Polygon[], raySampleCount = 20, maxRayDistanceCm = 50, mergeThresholdCm = 3, pullModeRatio = 0.7, debugFlags?: LinePromptDebugFlags): Polygon[] {
    const result = adjustEdgesForLinePromptsImpl(
      {
        promptPoints: this.promptPoints,
        pixelsToCm: (px) => this.pixelsToCm(px),
        cmToPixels: (cm) => this.cmToPixels(cm),
        rayIntersectsEdge: (origin, dir, edgeStart, edgeEnd) => this.rayIntersectsEdge(origin, dir, edgeStart, edgeEnd),
        infiniteLineIntersection: (ptA, dirA, ptB, dirB) => this.infiniteLineIntersection(ptA, dirA, ptB, dirB),
      },
      polygons,
      raySampleCount,
      maxRayDistanceCm,
      mergeThresholdCm,
      pullModeRatio,
      debugFlags,
    );
    this.linePromptDebug = result.linePromptDebug;
    return result.polygons;
  }

  /**
   * Apply boolean operations for box prompts
   * Processes prompts in order: union with positive boxes, difference with negative boxes
   * @param polygons Input polygons
   * @returns Modified polygons after boolean operations
   */
  private applyBooleanOperationsForPrompts(polygons: Polygon[]): Polygon[] {
    if (!this.promptPoints || this.promptPoints.length === 0) {
      return polygons;
    }

    // Filter only box prompts, excluding label=2 (remove_points)
    // Label 2 is handled separately by removePointsInBoxes pass
    const boxPrompts = this.promptPoints.filter((p) => p.type === 'box' && p.box?.worldCorners && p.label !== 2);

    if (boxPrompts.length === 0) {
      return polygons;
    }

    // Process each polygon
    const resultPolygons: Polygon[] = [];

    for (const polygon of polygons) {
      let currentPolygons: Polygon[] = [polygon];

      // Process prompts in order
      for (const prompt of boxPrompts) {
        if (!prompt.box?.worldCorners) continue;

        // Convert box corners from pixel space to world space (cm)
        const boxCorners = prompt.box.worldCorners.map((c) => ({
          x: this.pixelsToCm(c.x),
          y: this.pixelsToCm(c.y),
        }));

        // Skip boxes that are smaller than 2x the caching grid size in any dimension
        // Using 2x ensures opposite edges won't snap to the same grid cell
        // Grid size is already in world space (cm) - same units as the processed polygons
        const minBoxSize = this.pipeline.getPointCacheGridSize() * 2;
        const box = BoxClipper.cornersToBox(boxCorners);
        const width = box.maxX - box.minX;
        const height = box.maxY - box.minY;
        if (width < minBoxSize || height < minBoxSize) {
          continue;
        }

        const nextPolygons: Polygon[] = [];

        for (const currentPoly of currentPolygons) {
          if (prompt.label === 1) {
            // Positive prompt: Union operation
            const unionResult = BoxClipper.union(currentPoly, boxCorners);
            nextPolygons.push(...unionResult);
          } else {
            // Negative prompt: Difference operation (subtract box from polygon)
            const diffResult = BoxClipper.difference(currentPoly, boxCorners);
            nextPolygons.push(...diffResult);
          }
        }

        currentPolygons = nextPolygons;
      }

      // Add all resulting polygons
      resultPolygons.push(...currentPolygons);
    }

    return resultPolygons;
  }

  /**
   * Remove points from polygons that fall inside boxes with label=2 (remove_points type)
   * This pass filters out vertices that are inside remove_points boxes
   * @param polygons Input polygons
   * @returns Modified polygons with points removed
   */
  private removePointsInBoxes(polygons: Polygon[]): Polygon[] {
    if (!this.promptPoints || this.promptPoints.length === 0) {
      return polygons;
    }

    // Filter only box prompts with label=2 (remove_points)
    const removeBoxes = this.promptPoints.filter((p) => p.type === 'box' && p.label === 2 && p.box?.worldCorners);

    if (removeBoxes.length === 0) {
      return polygons;
    }

    // Convert all remove boxes to world space (cm) and create bounding boxes
    const boxes = removeBoxes
      .map((prompt) => {
        if (!prompt.box?.worldCorners) return null;
        const worldCorners = prompt.box.worldCorners.map((c) => ({
          x: this.pixelsToCm(c.x),
          y: this.pixelsToCm(c.y),
        }));
        return BoxClipper.cornersToBox(worldCorners);
      })
      .filter((box): box is NonNullable<typeof box> => box !== null);

    if (boxes.length === 0) {
      return polygons;
    }

    // Process each polygon
    const resultPolygons: Polygon[] = [];

    for (const polygon of polygons) {
      // Filter points that are NOT inside any remove box
      const filteredPoints = polygon.points.filter((point) => {
        // Check if point is inside any remove box
        for (const box of boxes) {
          const EPSILON = 1e-10;
          if (point.x >= box.minX - EPSILON && point.x <= box.maxX + EPSILON && point.y >= box.minY - EPSILON && point.y <= box.maxY + EPSILON) {
            return false; // Point is inside a remove box, exclude it
          }
        }
        return true; // Point is not inside any remove box, keep it
      });

      // Only keep polygon if it still has at least 3 points
      if (filteredPoints.length >= 3) {
        const newPolygon = { ...polygon, points: filteredPoints };

        // Recursively process children (holes)
        if (polygon.children && polygon.children.length > 0) {
          newPolygon.children = this.removePointsInBoxes(polygon.children);
        }

        resultPolygons.push(newPolygon);
      }
    }

    return resultPolygons;
  }

  /**
   * Enforce vertex prompts by ensuring polygon vertices exist within each vertex prompt's radius.
   * For each vertex prompt, checks if at least one polygon vertex exists within its radius.
   * If not, searches within radius + extraSearchCm for a nearby vertex and moves it to the radius border.
   * If still no vertex found, inserts a new vertex at the prompt position on the closest edge.
   *
   * @param polygons Input polygons in world space (cm)
   * @param kwargs.extraSearchCm Extra distance (cm) to search beyond radius for existing vertices to move (default: 5)
   * @returns Modified polygons with enforced vertices
   */
  private enforceVertexPrompts(polygons: Polygon[], kwargs?: { extraSearchCm?: number }): Polygon[] {
    const extraSearchCm = kwargs?.extraSearchCm ?? 5;

    if (!this.promptPoints || this.promptPoints.length === 0) {
      return polygons;
    }

    // Filter only vertex prompts with positive label
    const vertexPrompts = this.promptPoints.filter((p) => p.type === 'vertex' && p.vertex && p.label === 1);

    if (vertexPrompts.length === 0) {
      return polygons;
    }

    // Clone all polygons
    const resultPolygons: Polygon[] = polygons.map((p) => {
      const cloned = createPolygon(p.points.slice(), p.isHole);
      if (p.children && p.children.length > 0) {
        cloned.children = p.children.map((child) => createPolygon(child.points.slice(), child.isHole));
      }
      return cloned;
    });

    // Flatten polygons for processing
    const flatPolygons: { polygon: Polygon; parentIndex: number; isChild: boolean; childIndex: number }[] = [];
    for (let i = 0; i < resultPolygons.length; i++) {
      flatPolygons.push({ polygon: resultPolygons[i], parentIndex: i, isChild: false, childIndex: -1 });
      const children = resultPolygons[i].children;
      if (children) {
        for (let j = 0; j < children.length; j++) {
          flatPolygons.push({ polygon: children[j], parentIndex: i, isChild: true, childIndex: j });
        }
      }
    }

    // Helper to calculate distance from point to polygon (minimum distance to any edge)
    const distanceToPolygon = (px: number, py: number, points: Point2D[]): number => {
      let minDist = Infinity;
      for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];
        const edgeVec = { x: p2.x - p1.x, y: p2.y - p1.y };
        const edgeLen = Math.sqrt(edgeVec.x ** 2 + edgeVec.y ** 2);
        if (edgeLen < 1e-10) continue;
        const edgeDir = { x: edgeVec.x / edgeLen, y: edgeVec.y / edgeLen };
        const toPoint = { x: px - p1.x, y: py - p1.y };
        const t = Math.max(0, Math.min(edgeLen, toPoint.x * edgeDir.x + toPoint.y * edgeDir.y));
        const pointOnEdge = { x: p1.x + t * edgeDir.x, y: p1.y + t * edgeDir.y };
        const dist = Math.sqrt((pointOnEdge.x - px) ** 2 + (pointOnEdge.y - py) ** 2);
        if (dist < minDist) minDist = dist;
      }
      return minDist;
    };

    // Process each vertex prompt - only affect the closest polygon
    for (const prompt of vertexPrompts) {
      if (!prompt.vertex) continue;

      // Convert prompt position to world space (cm)
      const promptX = this.pixelsToCm(prompt.x);
      const promptY = this.pixelsToCm(prompt.y);
      const radiusCm = this.pixelsToCm(prompt.vertex.radius);

      // Find the closest polygon to the prompt
      let closestPolyIndex = -1;
      let closestPolyDistance = Infinity;
      for (let i = 0; i < flatPolygons.length; i++) {
        const points = flatPolygons[i].polygon.points;
        if (points.length < 3) continue;
        const dist = distanceToPolygon(promptX, promptY, points);
        if (dist < closestPolyDistance) {
          closestPolyDistance = dist;
          closestPolyIndex = i;
        }
      }

      // Only process the closest polygon
      if (closestPolyIndex < 0) continue;

      const flatPoly = flatPolygons[closestPolyIndex];
      const points = flatPoly.polygon.points;

      // Check if any existing vertex is within radius
      let hasVertexInRange = false;
      for (const point of points) {
        const dist = Math.sqrt((point.x - promptX) ** 2 + (point.y - promptY) ** 2);
        if (dist <= radiusCm) {
          hasVertexInRange = true;
          break;
        }
      }

      // If no vertex in range, check extended range (radius + extraSearchCm)
      if (!hasVertexInRange) {
        const extendedRadius = radiusCm + extraSearchCm;
        let closestVertexIndex = -1;
        let closestVertexDistance = Infinity;

        // Find closest vertex within extended radius
        for (let i = 0; i < points.length; i++) {
          const point = points[i];
          const dist = Math.sqrt((point.x - promptX) ** 2 + (point.y - promptY) ** 2);
          if (dist <= extendedRadius && dist < closestVertexDistance) {
            closestVertexDistance = dist;
            closestVertexIndex = i;
          }
        }

        if (closestVertexIndex >= 0) {
          // Move the vertex to the radius border (or prompt position if radius is 0)
          const newPoints = [...points];
          if (radiusCm <= 0) {
            // If radius is 0, move vertex directly to prompt position
            newPoints[closestVertexIndex] = { x: promptX, y: promptY };
          } else {
            // Move vertex to the radius border along the line from prompt to vertex
            const vertex = points[closestVertexIndex];
            const dx = vertex.x - promptX;
            const dy = vertex.y - promptY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 0) {
              // Normalize and scale to radius
              newPoints[closestVertexIndex] = {
                x: promptX + (dx / dist) * radiusCm,
                y: promptY + (dy / dist) * radiusCm,
              };
            } else {
              // Vertex is exactly at prompt, leave it
              newPoints[closestVertexIndex] = { x: promptX, y: promptY };
            }
          }
          flatPoly.polygon.points = newPoints;

          // Update result polygons
          if (flatPoly.isChild) {
            const parentChildren = resultPolygons[flatPoly.parentIndex].children;
            if (parentChildren) {
              parentChildren[flatPoly.childIndex].points = newPoints;
            }
          } else {
            resultPolygons[flatPoly.parentIndex].points = newPoints;
          }
        } else {
          // No vertex in extended range, find closest edge and insert a vertex
          let closestEdgeIndex = -1;
          let closestDistance = Infinity;

          // Find the closest edge to the prompt position
          for (let i = 0; i < points.length; i++) {
            const p1 = points[i];
            const p2 = points[(i + 1) % points.length];

            // Calculate closest point on edge to prompt
            const edgeVec = { x: p2.x - p1.x, y: p2.y - p1.y };
            const edgeLen = Math.sqrt(edgeVec.x ** 2 + edgeVec.y ** 2);
            if (edgeLen < 1e-10) continue;

            // Normalize edge vector
            const edgeDir = { x: edgeVec.x / edgeLen, y: edgeVec.y / edgeLen };

            // Vector from p1 to prompt
            const toPrompt = { x: promptX - p1.x, y: promptY - p1.y };

            // Project onto edge
            const t = Math.max(0, Math.min(edgeLen, toPrompt.x * edgeDir.x + toPrompt.y * edgeDir.y));

            // Closest point on edge
            const pointOnEdge = { x: p1.x + t * edgeDir.x, y: p1.y + t * edgeDir.y };

            // Distance from prompt to point on edge
            const dist = Math.sqrt((pointOnEdge.x - promptX) ** 2 + (pointOnEdge.y - promptY) ** 2);

            if (dist < closestDistance) {
              closestDistance = dist;
              closestEdgeIndex = i;
            }
          }

          // Insert vertex at prompt position
          if (closestEdgeIndex >= 0) {
            const newPoints = [...points];
            newPoints.splice(closestEdgeIndex + 1, 0, { x: promptX, y: promptY });
            flatPoly.polygon.points = newPoints;

            // Update result polygons
            if (flatPoly.isChild) {
              const parentChildren = resultPolygons[flatPoly.parentIndex].children;
              if (parentChildren) {
                parentChildren[flatPoly.childIndex].points = newPoints;
              }
            } else {
              resultPolygons[flatPoly.parentIndex].points = newPoints;
            }
          }
        }
      }
    }

    return resultPolygons;
  }

  /**
   * Ray-edge intersection test
   * Returns distance and hit point if ray intersects edge, null otherwise
   */
  private rayIntersectsEdge(origin: Point2D, direction: Point2D, edgeStart: Point2D, edgeEnd: Point2D): { distance: number; point: Point2D } | null {
    const dx = edgeEnd.x - edgeStart.x;
    const dy = edgeEnd.y - edgeStart.y;
    const det = direction.x * dy - direction.y * dx;

    if (Math.abs(det) < 1e-10) return null; // Parallel

    const t = ((edgeStart.x - origin.x) * dy - (edgeStart.y - origin.y) * dx) / det;
    const u = ((edgeStart.x - origin.x) * direction.y - (edgeStart.y - origin.y) * direction.x) / det;

    if (t > 0 && u >= 0 && u <= 1) {
      const hitPoint = { x: origin.x + t * direction.x, y: origin.y + t * direction.y };
      const distance = Math.sqrt((hitPoint.x - origin.x) ** 2 + (hitPoint.y - origin.y) ** 2);
      return { distance, point: hitPoint };
    }

    return null;
  }

  /**
   * Check if a point is inside a polygon using ray casting algorithm
   */
  private isPointInPolygon(point: Point2D, polygon: Polygon): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x,
        yi = polygon[i].y;
      const xj = polygon[j].x,
        yj = polygon[j].y;

      const intersect = (yi > point.y) !== (yj > point.y) && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  /**
   * Check if a point is inside a triangle using barycentric coordinates
   */
  private isPointInTriangle(point: Point2D, v0: Point2D, v1: Point2D, v2: Point2D): boolean {
    const sign = (p1: Point2D, p2: Point2D, p3: Point2D): number => {
      return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
    };

    const d1 = sign(point, v0, v1);
    const d2 = sign(point, v1, v2);
    const d3 = sign(point, v2, v0);

    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;

    return !(hasNeg && hasPos);
  }

  /**
   * Check if a triangle overlaps with a polygon (box)
   * Two polygons overlap if:
   * 1. Any vertex of one is inside the other
   * 2. Any edges of the two polygons intersect
   */
  private triangleOverlapsPolygon(triangle: Point2D[], polygon: Point2D[]): boolean {
    // Check if any triangle vertex is inside the polygon
    const boxPoly = createPolygon(polygon, false);

    for (const vertex of triangle) {
      if (this.isPointInPolygon(vertex, boxPoly)) {
        return true;
      }
    }

    // Check if any polygon vertex is inside the triangle
    for (const vertex of polygon) {
      if (this.isPointInTriangle(vertex, triangle[0], triangle[1], triangle[2])) {
        return true;
      }
    }

    // Check if any edges intersect
    const triangleEdges = [
      [triangle[0], triangle[1]],
      [triangle[1], triangle[2]],
      [triangle[2], triangle[0]],
    ];

    const polygonEdges: Point2D[][] = [];
    for (let i = 0; i < polygon.length; i++) {
      polygonEdges.push([polygon[i], polygon[(i + 1) % polygon.length]]);
    }

    for (const [t1, t2] of triangleEdges) {
      for (const [p1, p2] of polygonEdges) {
        if (this.lineSegmentsIntersect(t1, t2, p1, p2)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Intersect two infinite lines, each defined by a point and direction vector.
   * Returns the intersection point, or null if lines are parallel.
   */
  private infiniteLineIntersection(pointA: Point2D, dirA: Point2D, pointB: Point2D, dirB: Point2D): Point2D | null {
    const det = dirA.x * dirB.y - dirA.y * dirB.x;
    if (Math.abs(det) < 1e-10) return null;
    const dx = pointB.x - pointA.x;
    const dy = pointB.y - pointA.y;
    const t = (dx * dirB.y - dy * dirB.x) / det;
    return { x: pointA.x + t * dirA.x, y: pointA.y + t * dirA.y };
  }

  /**
   * Find the nearest edge in polygon to a point
   */
  private findNearestEdge(point: Point2D, polygon: Polygon): { edgeIndex: number; closestPoint: Point2D; distance: number } {
    let minDistance = Infinity;
    let nearestEdgeIndex = 0;
    let nearestPoint = polygon[0];

    for (let i = 0; i < polygon.length; i++) {
      const edgeStart = polygon[i];
      const edgeEnd = polygon[(i + 1) % polygon.length];

      const { closestPoint, distance } = this.pointToLineSegmentDistance(point, edgeStart, edgeEnd);

      if (distance < minDistance) {
        minDistance = distance;
        nearestEdgeIndex = i;
        nearestPoint = closestPoint;
      }
    }

    return { edgeIndex: nearestEdgeIndex, closestPoint: nearestPoint, distance: minDistance };
  }

  /**
   * Calculate distance from point to line segment and closest point on segment
   */
  private pointToLineSegmentDistance(point: Point2D, lineStart: Point2D, lineEnd: Point2D): { closestPoint: Point2D; distance: number } {
    const lineVector = { x: lineEnd.x - lineStart.x, y: lineEnd.y - lineStart.y };
    const pointVector = { x: point.x - lineStart.x, y: point.y - lineStart.y };

    const lineLengthSquared = lineVector.x ** 2 + lineVector.y ** 2;

    if (lineLengthSquared === 0) {
      // Line segment is a point
      const distance = Math.sqrt(pointVector.x ** 2 + pointVector.y ** 2);
      return { closestPoint: lineStart, distance };
    }

    // Project point onto line (clamped to segment)
    const t = Math.max(0, Math.min(1, (pointVector.x * lineVector.x + pointVector.y * lineVector.y) / lineLengthSquared));

    const closestPoint = {
      x: lineStart.x + t * lineVector.x,
      y: lineStart.y + t * lineVector.y,
    };

    const distance = Math.sqrt((point.x - closestPoint.x) ** 2 + (point.y - closestPoint.y) ** 2);

    return { closestPoint, distance };
  }

  /**
   * Remove collinear points (vertices that form almost straight angles)
   * Runs iteratively until no more points are removed
   * @param polygon The polygon to process
   * @param angleThresholdDegrees Maximum deviation from 180° to consider a point collinear (default: 2°)
   * @param lengthScaleFactor How much segment length affects threshold. Higher = more aggressive removal of short segments (default: 0.1)
   */
  private removeCollinearPoints(polygon: Polygon, angleThresholdDegrees = 2.0, lengthScaleFactor = 0.1): Polygon {
    if (polygon.length < 3) return polygon;

    const baseAngleThresholdRad = (angleThresholdDegrees * Math.PI) / 180;
    let current = polygon;
    let iteration = 0;
    const maxIterations = 100; // Safety limit

    while (iteration < maxIterations) {
      // First pass: mark which points are collinear candidates for removal
      const isCollinear: boolean[] = new Array(current.length).fill(false);

      for (let i = 0; i < current.length; i++) {
        const prev = current[(i - 1 + current.length) % current.length];
        const curr = current[i];
        const next = current[(i + 1) % current.length];

        // Calculate vectors from current point
        const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
        const v2 = { x: next.x - curr.x, y: next.y - curr.y };

        // Calculate vector lengths
        const v1Len = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
        const v2Len = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

        if (v1Len < 1e-10 || v2Len < 1e-10) {
          continue;
        }

        // Use a fixed angle threshold (no length-based scaling)
        // Only remove points that are truly collinear
        const angleThresholdRad = baseAngleThresholdRad;
        const maxAngleRad = Math.PI - angleThresholdRad; // 180° - threshold

        // Dot product and angle
        const dotProduct = v1.x * v2.x + v1.y * v2.y;
        const cosAngle = dotProduct / (v1Len * v2Len);
        const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle))); // Clamp to [-1, 1] for numerical stability

        // Mark as collinear if angle is almost straight (>= 180° - threshold)
        if (angle >= maxAngleRad) {
          isCollinear[i] = true;
        }
      }

      // Second pass: skip removal if an adjacent point is also marked for removal.
      // Removing adjacent collinear points in the same pass can destroy corners
      // (e.g. two nearly-straight segments that together form a 90° turn).
      // Strategy: in a chain of consecutive collinear points, remove every other one
      // to make progress while preserving corner safety.
      const filtered: Point2D[] = [];
      for (let i = 0; i < current.length; i++) {
        if (isCollinear[i]) {
          const prevIdx = (i - 1 + current.length) % current.length;
          const nextIdx = (i + 1) % current.length;
          if (isCollinear[prevIdx] || isCollinear[nextIdx]) {
            // Adjacent point also collinear — we're in a chain.
            // Only keep every other point in the chain to make progress.
            // Keep this point if the previous collinear point was removed (i.e., prevIdx is not collinear
            // OR prevIdx was collinear but we kept it, which happens when prevIdx's prev was not collinear).
            // Simpler approach: keep if previous is NOT collinear (start of chain) or previous was kept.
            // We can achieve "remove every other" by checking if prevIdx is collinear:
            // - If prevIdx is NOT collinear, this is the first in a chain → keep it
            // - If prevIdx IS collinear, remove this one (it will be handled in next iteration)
            if (!isCollinear[prevIdx]) {
              // First point of the collinear chain — keep to preserve potential corner
              filtered.push(current[i]);
            }
            // Otherwise: skip this point (remove it), next iteration will handle the rest
            continue;
          }
        }

        if (!isCollinear[i]) {
          filtered.push(current[i]);
        }
      }

      // Check if we removed any points
      if (filtered.length === current.length || filtered.length < 3) {
        // No change or too few points, stop
        break;
      }

      current = createPolygon(filtered, current.isHole);
      iteration++;
    }

    return current;
  }

  /**
   * Snap small segments to align with or be perpendicular to neighboring longer edges
   * Uses a simpler approach: if a small segment is nearly parallel or perpendicular to a neighbor,
   * make it exactly parallel or perpendicular by adjusting the shared vertex
   * @param polygon Polygon in world space (cm)
   * @param minSegmentRatio Minimum length ratio (short/long) to consider snapping (default: 0.5)
   * @param angleSnapThreshold Angle threshold in degrees for snapping (default: 10 degrees)
   */
  private snapSmallSegments(polygon: Polygon, minSegmentRatio = 0.5, angleSnapThreshold = 10.0, maxEdgeLengthCm = 1000): Polygon {
    if (polygon.length < 4) return polygon;

    const angleThresholdRad = (angleSnapThreshold * Math.PI) / 180;
    const points = polygon.points.slice();
    const n = points.length;

    const maxIterations = 5;
    let iteration = 0;
    let totalSnapped = 0;

    while (iteration < maxIterations) {
      let snappedThisIteration = 0;

      // Find edges that need snapping and calculate their new positions
      const adjustments: Array<{ vertexIdx: number; newPos: Point2D } | null> = new Array(n).fill(null);

      for (let i = 0; i < n; i++) {
        const v0 = points[(i - 1 + n) % n];
        const v1 = points[i];
        const v2 = points[(i + 1) % n];
        const v3 = points[(i + 2) % n];

        // Edge from v1 to v2 (current edge)
        const edge = { x: v2.x - v1.x, y: v2.y - v1.y };
        const edgeLen = Math.sqrt(edge.x * edge.x + edge.y * edge.y);

        if (edgeLen < 1e-10 || edgeLen > maxEdgeLengthCm) continue;

        // Previous edge (v0 to v1)
        const prevEdge = { x: v1.x - v0.x, y: v1.y - v0.y };
        const prevLen = Math.sqrt(prevEdge.x * prevEdge.x + prevEdge.y * prevEdge.y);

        // Next edge (v2 to v3)
        const nextEdge = { x: v3.x - v2.x, y: v3.y - v2.y };
        const nextLen = Math.sqrt(nextEdge.x * nextEdge.x + nextEdge.y * nextEdge.y);

        if (prevLen < 1e-10 || nextLen < 1e-10) continue;

        // Check if current edge is small compared to neighbors
        if (edgeLen >= prevLen * minSegmentRatio && edgeLen >= nextLen * minSegmentRatio) continue;

        // Normalize
        const edgeDir = { x: edge.x / edgeLen, y: edge.y / edgeLen };
        const prevDir = { x: prevEdge.x / prevLen, y: prevEdge.y / prevLen };
        const nextDir = { x: nextEdge.x / nextLen, y: nextEdge.y / nextLen };

        // Check angle between current edge and neighbors
        const dotPrev = edgeDir.x * prevDir.x + edgeDir.y * prevDir.y;
        const dotNext = edgeDir.x * nextDir.x + edgeDir.y * nextDir.y;

        const angleToPrev = Math.acos(Math.max(-1, Math.min(1, Math.abs(dotPrev))));
        const angleToNext = Math.acos(Math.max(-1, Math.min(1, Math.abs(dotNext))));

        // Determine if we should snap to previous or next edge
        let snapToPrev = false;
        let snapToNext = false;
        let makeParallel = false;

        if (edgeLen < prevLen * minSegmentRatio) {
          if (angleToPrev < angleThresholdRad) {
            // Nearly parallel - make exactly parallel
            snapToPrev = true;
            makeParallel = true;
          } else if (Math.abs(angleToPrev - Math.PI / 2) < angleThresholdRad) {
            // Nearly perpendicular - make exactly perpendicular
            snapToPrev = true;
            makeParallel = false;
          }
        }

        if (!snapToPrev && edgeLen < nextLen * minSegmentRatio) {
          if (angleToNext < angleThresholdRad) {
            snapToNext = true;
            makeParallel = true;
          } else if (Math.abs(angleToNext - Math.PI / 2) < angleThresholdRad) {
            snapToNext = true;
            makeParallel = false;
          }
        }

        if (!snapToPrev && !snapToNext) continue;

        // Calculate the target direction for the current edge
        let targetDir: Point2D;
        if (snapToPrev) {
          if (makeParallel) {
            targetDir = prevDir;
          } else {
            // Perpendicular to prev
            targetDir = { x: -prevDir.y, y: prevDir.x };
          }
        } else {
          if (makeParallel) {
            targetDir = nextDir;
          } else {
            // Perpendicular to next
            targetDir = { x: -nextDir.y, y: nextDir.x };
          }
        }

        // Adjust v2 to make the edge parallel to targetDir
        // Keep v1 fixed, move v2 along the nextEdge direction
        // Find intersection of: line through v1 with direction targetDir
        //                  and: line through v3 with direction -nextDir

        // Line 1: v1 + t * targetDir
        // Line 2: v3 - s * nextDir
        // Solve: v1 + t * targetDir = v3 - s * nextDir

        // This gives us:
        // v1.x + t * targetDir.x = v3.x - s * nextDir.x
        // v1.y + t * targetDir.y = v3.y - s * nextDir.y

        // Rearrange:
        // t * targetDir.x + s * nextDir.x = v3.x - v1.x
        // t * targetDir.y + s * nextDir.y = v3.y - v1.y

        const dx = v3.x - v1.x;
        const dy = v3.y - v1.y;

        // Solve 2x2 system using Cramer's rule
        const det = targetDir.x * nextDir.y - targetDir.y * nextDir.x;

        if (Math.abs(det) < 1e-10) continue; // Lines are parallel, can't solve

        const t = (dx * nextDir.y - dy * nextDir.x) / det;

        const newV2 = {
          x: v1.x + t * targetDir.x,
          y: v1.y + t * targetDir.y,
        };

        // Check if the new position is reasonable (not too far from original)
        const movement = Math.sqrt((newV2.x - v2.x) ** 2 + (newV2.y - v2.y) ** 2);
        if (movement > edgeLen * 4) continue; // Don't make huge changes

        adjustments[i + (1 % n)] = { vertexIdx: (i + 1) % n, newPos: newV2 };
        snappedThisIteration++;
      }

      // Apply adjustments
      for (let i = 0; i < n; i++) {
        const adj = adjustments[i];
        if (adj) {
          points[adj.vertexIdx] = adj.newPos;
        }
      }

      if (snappedThisIteration === 0) break;

      totalSnapped += snappedThisIteration;
      iteration++;
    }

    if (totalSnapped > 0) {
      return createPolygon(points, polygon.isHole);
    }

    return polygon;
  }

  /**
   * Remove features whose AREA is too small to be real detail — thin spikes and
   * deep narrow notches that deviation-based simplifiers keep (their tip sits
   * far from the neighbour chord, but the pocket they enclose is tiny).
   *
   * Two operations compete each iteration, and the cheaper (in swept area) is
   * applied:
   *
   * 1. VERTEX PRUNE (Visvalingam–Whyatt): drop the vertex whose effective
   *    triangle (prev, curr, next) is smallest — the vertex collapses onto the
   *    chord between its neighbours. Eats notch tips and spikes.
   * 2. EDGE DISSOLVE: drop a whole short edge by EXTENDING its two neighbour
   *    edges to their intersection — the corner the small edge was chamfering
   *    off is rebuilt exactly. A prune here would collapse one endpoint onto a
   *    chord and skew the corner; the dissolve keeps both walls' lines. Only
   *    proper corners qualify (the intersection must lie beyond both edges);
   *    near-parallel neighbours push the intersection far away, which inflates
   *    the swept area past the threshold and the dissolve loses naturally.
   *
   * Repeats until every remaining candidate sweeps at least `minAreaCm2`. A
   * multi-vertex pocket is consumed progressively: each removal shrinks the
   * neighbours' effective areas, so the whole pocket collapses onto the rim.
   * Real corners (two long edges) sweep large areas and survive.
   *
   * @param polygon - Polygon to prune
   * @param minAreaCm2 - Features sweeping less than this area are removed (default 150 cm²)
   * @param maxIterations - Safety cap on removal operations (default 100)
   */
  private removeSmallFeatures(polygon: Polygon, minAreaCm2 = 150, maxIterations = 100): Polygon {
    if (polygon.length <= 4) return polygon;

    const points = polygon.points.slice();

    const effectiveArea = (a: Point2D, b: Point2D, c: Point2D): number => Math.abs((b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y)) / 2;

    for (let iter = 0; iter < maxIterations && points.length > 4; iter++) {
      const nPts = points.length;

      // Candidate 1: cheapest vertex prune.
      let bestVertexIdx = -1;
      let bestVertexArea = Infinity;
      for (let i = 0; i < nPts; i++) {
        const area = effectiveArea(points[(i - 1 + nPts) % nPts], points[i], points[(i + 1) % nPts]);
        if (area < bestVertexArea) {
          bestVertexArea = area;
          bestVertexIdx = i;
        }
      }

      // Candidate 2: cheapest edge dissolve — replace edge (b→c) with the
      // intersection X of the extended neighbour edges (a→b) and (d→c).
      // The swept area is the triangle (b, c, X).
      let bestEdgeIdx = -1;
      let bestEdgeArea = Infinity;
      let bestEdgeX: Point2D | null = null;
      for (let i = 0; i < nPts; i++) {
        const a = points[(i - 1 + nPts) % nPts];
        const b = points[i];
        const c = points[(i + 1) % nPts];
        const d = points[(i + 2) % nPts];
        const dirAB = { x: b.x - a.x, y: b.y - a.y };
        const dirDC = { x: c.x - d.x, y: c.y - d.y };
        const denom = dirAB.x * dirDC.y - dirAB.y * dirDC.x;
        if (Math.abs(denom) < 1e-9) continue;
        // Solve b + t·dirAB = c + u·dirDC; both extensions must go FORWARD
        // (t, u > 0) or there is no corner to rebuild, only a fold.
        const rx = c.x - b.x;
        const ry = c.y - b.y;
        const t = (rx * dirDC.y - ry * dirDC.x) / denom;
        const u = (rx * dirAB.y - ry * dirAB.x) / denom;
        if (t <= 0 || u <= 0) continue;
        const X = { x: b.x + dirAB.x * t, y: b.y + dirAB.y * t };
        const area = effectiveArea(b, c, X);
        if (area < bestEdgeArea) {
          bestEdgeArea = area;
          bestEdgeIdx = i;
          bestEdgeX = X;
        }
      }

      // Apply the cheaper operation, if it stays under the threshold.
      const dissolveWins = bestEdgeX !== null && bestEdgeArea < bestVertexArea && nPts > 4;
      if (dissolveWins && bestEdgeArea < minAreaCm2 && bestEdgeX) {
        if (bestEdgeIdx + 1 < nPts) {
          points.splice(bestEdgeIdx, 2, bestEdgeX);
        } else {
          // Edge wraps (last → first): replace the last point, drop the first.
          points.splice(bestEdgeIdx, 1, bestEdgeX);
          points.splice(0, 1);
        }
      } else if (bestVertexIdx !== -1 && bestVertexArea < minAreaCm2) {
        points.splice(bestVertexIdx, 1);
      } else {
        break;
      }
    }

    if (points.length < 3 || points.length === polygon.length) return polygon;
    return createPolygon(points, polygon.isHole);
  }

  /**
   * Remove polygon segments that are NOT supported by the underlying pixel
   * data, and rebuild the shape from the surrounding well-supported lines.
   *
   * Uses the same probe signal as pixel alignment: each edge is sampled along
   * its length and scanned perpendicular for a density peak. An edge whose hit
   * ratio is below `minProbeRatio` does not lie on a clear line — it is an
   * artifact (segmentation notch, spike, chamfer), not a wall.
   *
   * Each run of consecutive unsupported edges is replaced by extending the two
   * bordering supported edges (which DO sit on clear lines) to their
   * intersection — rebuilding the corner the artifact obscured. When those
   * neighbours are near-collinear (a notch cut into a straight rim) there is no
   * corner to rebuild: the run collapses to a straight bridge along the rim.
   *
   * An edge only counts as an ANCHOR (a line the shape is rebuilt from) when it
   * is on a clear line AND consistent with the rest of the shape: long enough
   * to be a real wall, and not an isolated sliver (e.g. a door leaf) whose
   * neighbours on both sides are themselves unsupported — those are removed
   * together with the run they interrupt.
   *
   * @param polygon - Polygon in world space (cm)
   * @param searchRangeCm - Perpendicular scan distance per probe (default 3 cm, same as pixel alignment)
   * @param minProbeRatio - Minimum fraction of probes with a density peak for an edge to count as "on a line" (default 0.7)
   * @param maxSpanCm - Longest gap (straight-line distance between the bordering anchors) to rebuild; wider gaps are genuinely unclear and left alone (default 300 cm)
   * @param paddingCm - Sampling padding at edge ends, mirrors pixel alignment (default 7 cm)
   * @param maxExtendCm - Cap on how far an anchor edge may be extended to the rebuilt corner (default 150 cm)
   * @param minAnchorCm - Edges shorter than this never anchor a rebuild, even when on a line (default 25 cm)
   * @param isolatedAnchorCm - On-line edges shorter than this are demoted when both neighbours are unsupported (default 120 cm)
   * @param minIntensity - Minimum density-mask brightness (0-255) a probe pixel must reach to count as line evidence. Deliberately much stricter than pixel alignment's floor of 30: alignment SEARCHES for faint lines to snap to, while this pass makes a destructive judgment and must only trust strong, unambiguous lines (default 150)
   * @param maxAreaChangeM2 - Cap on how much area (m²) a single run's fixup may add or remove; a run whose bridge/corner would change more is left unfixed rather than amputating real geometry (default 2)
   * @param debug - Log per-edge support and per-run decisions to the console (default false)
   */
  private removeUnsupportedSegments(
    polygon: Polygon,
    searchRangeCm = 3.0,
    minProbeRatio = 0.7,
    maxSpanCm = 300,
    paddingCm = 7.0,
    maxExtendCm = 150,
    minAnchorCm = 25,
    isolatedAnchorCm = 120,
    minIntensity = 150,
    maxAreaChangeM2 = 2,
    debug = false,
  ): Polygon {
    if (!this.densityMask || polygon.length <= 4) {
      if (debug) console.warn('[remove_unsupported_segments] skipped:', !this.densityMask ? 'no density mask' : `only ${polygon.length} points`);
      return polygon;
    }
    const densityMask = this.densityMask;

    const pixelPolygon = this.worldPolygonToPixelPolygon(polygon);
    const pts = pixelPolygon.points;
    const n = pts.length;

    const searchRangePx = Math.max(1, this.cmToPixels(searchRangeCm));
    const paddingPx = this.cmToPixels(paddingCm);
    const maxExtendPx = this.cmToPixels(maxExtendCm);
    const maxSpanPx = this.cmToPixels(maxSpanCm);
    const minAnchorPx = this.cmToPixels(minAnchorCm);
    const isolatedAnchorPx = this.cmToPixels(isolatedAnchorCm);

    // A probe counts only when a pixel along its perpendicular scan reaches
    // minIntensity. Deliberately NOT findDensityPeakAlongScanLine: that
    // accepts any smoothed value above a hardcoded 30, so faint point
    // speckle (furniture, clutter) reads as a "line". Wall lines in the
    // density mask are bright; speckle is dim — a strict brightness floor
    // separates them.
    const probeHitsLine = (samplePoint: Point2D, perp: Point2D): boolean => {
      for (let offset = -searchRangePx; offset <= searchRangePx; offset++) {
        const px = Math.round(samplePoint.x + perp.x * offset);
        const py = Math.round(samplePoint.y + perp.y * offset);
        if (px < 0 || px >= densityMask.width || py < 0 || py >= densityMask.height) continue;
        const idx = (py * densityMask.width + px) * 4;
        const intensity = (densityMask.data[idx] + densityMask.data[idx + 1] + densityMask.data[idx + 2]) / 3;
        if (intensity >= minIntensity) return true;
      }
      return false;
    };

    // Probe an edge like pixel alignment's phase 1: perpendicular scans at
    // regular samples along the edge. Returns the hit ratio.
    const edgeSupport = (p1: Point2D, p2: Point2D): number => {
      const ex = p2.x - p1.x;
      const ey = p2.y - p1.y;
      const len = Math.sqrt(ex * ex + ey * ey);
      // Too short to verify against the mask — not evidence of a clear line.
      if (len < 2) return 0;
      const perp = { x: -ey / len, y: ex / len };
      const pad = Math.min(paddingPx, len * 0.15);
      const samplingLength = len - pad;
      if (samplingLength <= 0) return 0;
      const numSamples = Math.max(5, Math.floor(samplingLength));
      let hits = 0;
      for (let j = 0; j < numSamples; j++) {
        const d = pad / 2 + (j / (numSamples - 1)) * samplingLength;
        const t = d / len;
        const samplePoint = { x: p1.x + ex * t, y: p1.y + ey * t };
        if (probeHitsLine(samplePoint, perp)) hits++;
      }
      return hits / numSamples;
    };

    const edgeLengths: number[] = [];
    const supportRatios: number[] = [];
    const onLine: boolean[] = [];
    for (let i = 0; i < n; i++) {
      const p1 = pts[i];
      const p2 = pts[(i + 1) % n];
      edgeLengths.push(Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2));
      supportRatios.push(edgeSupport(p1, p2));
      onLine.push(supportRatios[i] >= minProbeRatio);
    }

    // An anchor must be on a line AND long enough to plausibly be a real wall.
    // Isolated slivers (door leaves) are handled later, evidence-based: they
    // are only demoted when a merged rebuild across them validates over the
    // mask — structurally demoting them here proved to eat real walls that
    // merely have jagged neighbours.
    const anchor: boolean[] = [];
    for (let i = 0; i < n; i++) {
      anchor.push(onLine[i] && edgeLengths[i] >= minAnchorPx);
    }

    if (debug) {
      console.log(`[remove_unsupported_segments] mask ${densityMask.width}x${densityMask.height}, polygon ${n} edges, 1cm = ${this.cmToPixels(1).toFixed(2)}px`);
      console.table(
        pts.map((p, idx) => ({
          edge: idx,
          from: `(${Math.round(this.pixelsToCm(p.x))},${Math.round(this.pixelsToCm(p.y))})cm`,
          lengthCm: Math.round(this.pixelsToCm(edgeLengths[idx])),
          support: supportRatios[idx].toFixed(2),
          onLine: onLine[idx],
          anchor: anchor[idx],
        })),
      );
    }

    // No anchors to rebuild from, or nothing to remove.
    if (!anchor.includes(true) || !anchor.includes(false)) {
      if (debug) console.warn(`[remove_unsupported_segments] nothing to do: ${anchor.includes(true) ? 'every edge is an anchor' : 'no anchor edges at all'}`);
      return polygon;
    }

    // Collect circular runs of consecutive non-anchor edges. Edge i spans
    // vertices i → i+1, so a run of edges [a..b] owns vertices a..b+1 (the
    // endpoints are shared with the bordering anchor edges).
    const runs: Array<{ start: number; length: number }> = [];
    let i = 0;
    // Start scanning at an anchor edge so no run wraps past index 0 unseen.
    while (!anchor[i]) i++;
    for (let scanned = 0, idx = i; scanned < n; ) {
      if (anchor[idx % n]) {
        scanned++;
        idx++;
        continue;
      }
      let length = 0;
      while (!anchor[(idx + length) % n]) length++;
      runs.push({ start: idx % n, length });
      scanned += length;
      idx += length;
    }

    const intersectLines = (p: Point2D, r: Point2D, q: Point2D, s: Point2D): Point2D | null => {
      const denom = r.x * s.y - r.y * s.x;
      const rLen = Math.sqrt(r.x * r.x + r.y * r.y);
      const sLen = Math.sqrt(s.x * s.x + s.y * s.y);
      if (rLen < 1e-10 || sLen < 1e-10) return null;
      // Near-collinear neighbours (< ~15°) have no meaningful corner.
      if (Math.abs(denom) / (rLen * sLen) < 0.26) return null;
      const t = ((q.x - p.x) * s.y - (q.y - p.y) * s.x) / denom;
      return { x: p.x + r.x * t, y: p.y + r.y * t };
    };

    // Support ratio of an arbitrary segment (no end padding — used for
    // extensions and pushed bridge candidates, which are often short).
    const segmentSupport = (p1: Point2D, p2: Point2D): number => {
      const ex = p2.x - p1.x;
      const ey = p2.y - p1.y;
      const len = Math.sqrt(ex * ex + ey * ey);
      if (len < 3) return 1;
      const perp = { x: -ey / len, y: ex / len };
      const numSamples = Math.max(5, Math.floor(len));
      let hits = 0;
      for (let j = 0; j < numSamples; j++) {
        const t = j / (numSamples - 1);
        if (probeHitsLine({ x: p1.x + ex * t, y: p1.y + ey * t }, perp)) hits++;
      }
      return hits / numSamples;
    };

    // A neighbour may only GROW OVER A LINE: the extension from its endpoint
    // to the rebuilt corner is probed against the mask like any edge, and the
    // corner is rebuilt only when the mask actually continues the line there.
    const extensionOnLine = (p1: Point2D, p2: Point2D): boolean => segmentSupport(p1, p2) >= minProbeRatio;

    interface ResolvedRun {
      start: number;
      length: number;
      spanPx: number;
      spanOk: boolean;
      corner: Point2D | null;
      /** Intersection exists and is within reach — enough to rebuild the corner. */
      cornerOk: boolean;
      /** cornerOk AND both extensions grow over mask lines — required to demote an isolated anchor. */
      cornerConfirmed: boolean;
      /** Bridge pushed outward onto a found line: the run's endpoints are replaced by these. */
      pushedStart: Point2D | null;
      pushedEnd: Point2D | null;
    }

    // Resolve what closing a run of non-anchor edges [start..start+length-1]
    // would look like. Preference order when applying: rebuild the corner
    // whenever the anchors geometrically intersect within reach; otherwise
    // PUSH the closing edge outward (toward where the removed chain was)
    // looking for a real line to land on — most of the time there is one —
    // and connect the endpoints directly only when neither works.
    const resolveRun = (start: number, length: number): ResolvedRun => {
      const vStart = start;
      const vEnd = (start + length) % n;
      const spanPx = Math.sqrt((pts[vEnd].x - pts[vStart].x) ** 2 + (pts[vEnd].y - pts[vStart].y) ** 2);
      const spanOk = spanPx <= maxSpanPx;
      let corner: Point2D | null = null;
      let cornerOk = false;
      let cornerConfirmed = false;
      let pushedStart: Point2D | null = null;
      let pushedEnd: Point2D | null = null;
      if (spanOk) {
        const prevEdgeFrom = pts[(start - 1 + n) % n];
        const prevEdgeTo = pts[vStart];
        const nextEdgeFrom = pts[vEnd];
        const nextEdgeTo = pts[(vEnd + 1) % n];
        corner = intersectLines(prevEdgeFrom, { x: prevEdgeTo.x - prevEdgeFrom.x, y: prevEdgeTo.y - prevEdgeFrom.y }, nextEdgeFrom, {
          x: nextEdgeTo.x - nextEdgeFrom.x,
          y: nextEdgeTo.y - nextEdgeFrom.y,
        });
        cornerOk =
          corner !== null &&
          Math.sqrt((corner.x - prevEdgeTo.x) ** 2 + (corner.y - prevEdgeTo.y) ** 2) <= maxExtendPx &&
          Math.sqrt((corner.x - nextEdgeFrom.x) ** 2 + (corner.y - nextEdgeFrom.y) ** 2) <= maxExtendPx;
        cornerConfirmed = cornerOk && corner !== null && extensionOnLine(prevEdgeTo, corner) && extensionOnLine(corner, nextEdgeFrom);

        // No corner to rebuild — push the closing edge OUT of the
        // segmentation area and look for a real line to land on (most of
        // the time there is one). From each sample along the direct bridge,
        // scan outward toward the removed chain's side until the first
        // strong mask pixel, then fit a line through the found points —
        // this finds the wall even when it isn't parallel to the bridge.
        // The anchors then extend along their OWN lines to meet the landed
        // edge, so the neighbours never change shape.
        if (!cornerOk && spanPx > 2) {
          const bridgeDx = (nextEdgeFrom.x - prevEdgeTo.x) / spanPx;
          const bridgeDy = (nextEdgeFrom.y - prevEdgeTo.y) / spanPx;
          // Side of the removed chain relative to the bridge = push direction.
          let centroidX = 0;
          let centroidY = 0;
          for (let k = 0; k <= length; k++) {
            centroidX += pts[(start + k) % n].x;
            centroidY += pts[(start + k) % n].y;
          }
          centroidX /= length + 1;
          centroidY /= length + 1;
          const side = -bridgeDy * (centroidX - prevEdgeTo.x) + bridgeDx * (centroidY - prevEdgeTo.y) >= 0 ? 1 : -1;
          const pushX = -bridgeDy * side;
          const pushY = bridgeDx * side;

          const mask = this.densityMask;
          const intensityAt = (px: number, py: number): number => {
            if (!mask || px < 0 || px >= mask.width || py < 0 || py >= mask.height) return 0;
            const idx = (py * mask.width + px) * 4;
            return (mask.data[idx] + mask.data[idx + 1] + mask.data[idx + 2]) / 3;
          };

          const numProbes = Math.max(7, Math.floor(spanPx / Math.max(1, this.cmToPixels(5))));
          const hits: Point2D[] = [];
          for (let j = 0; j < numProbes; j++) {
            const t = j / (numProbes - 1);
            const baseX = prevEdgeTo.x + (nextEdgeFrom.x - prevEdgeTo.x) * t;
            const baseY = prevEdgeTo.y + (nextEdgeFrom.y - prevEdgeTo.y) * t;
            for (let off = 0; off <= maxExtendPx; off++) {
              const sx = Math.round(baseX + pushX * off);
              const sy = Math.round(baseY + pushY * off);
              if (intensityAt(sx, sy) >= minIntensity) {
                hits.push({ x: baseX + pushX * off, y: baseY + pushY * off });
                break;
              }
            }
          }

          if (hits.length / numProbes >= minProbeRatio) {
            // Least-squares line through the hit points (principal axis),
            // with one round of outlier rejection so a clutter hit on the
            // way out doesn't bend the landing line.
            const fitLine = (points: Point2D[]): { px: number; py: number; dx: number; dy: number } | null => {
              if (points.length < 3) return null;
              let mx = 0;
              let my = 0;
              for (const p of points) {
                mx += p.x;
                my += p.y;
              }
              mx /= points.length;
              my /= points.length;
              let sxx = 0;
              let sxy = 0;
              let syy = 0;
              for (const p of points) {
                sxx += (p.x - mx) ** 2;
                sxy += (p.x - mx) * (p.y - my);
                syy += (p.y - my) ** 2;
              }
              const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
              return { px: mx, py: my, dx: Math.cos(theta), dy: Math.sin(theta) };
            };

            let landing = fitLine(hits);
            if (landing) {
              const line = landing;
              const residual = (p: Point2D): number => Math.abs((p.x - line.px) * line.dy - (p.y - line.py) * line.dx);
              const residuals = hits.map(residual).sort((a, b) => a - b);
              const tolerance = Math.max(2, residuals[Math.floor(residuals.length / 2)] * 2);
              const inliers = hits.filter((p) => residual(p) <= tolerance);
              if (inliers.length >= hits.length * 0.6) {
                landing = fitLine(inliers) ?? landing;
              }
            }

            if (landing) {
              const newStart = intersectLines(prevEdgeFrom, { x: prevEdgeTo.x - prevEdgeFrom.x, y: prevEdgeTo.y - prevEdgeFrom.y }, { x: landing.px, y: landing.py }, { x: landing.dx, y: landing.dy });
              const newEnd = intersectLines(nextEdgeFrom, { x: nextEdgeTo.x - nextEdgeFrom.x, y: nextEdgeTo.y - nextEdgeFrom.y }, { x: landing.px, y: landing.py }, { x: landing.dx, y: landing.dy });
              if (
                newStart &&
                newEnd &&
                Math.sqrt((newStart.x - prevEdgeTo.x) ** 2 + (newStart.y - prevEdgeTo.y) ** 2) <= maxExtendPx &&
                Math.sqrt((newEnd.x - nextEdgeFrom.x) ** 2 + (newEnd.y - nextEdgeFrom.y) ** 2) <= maxExtendPx
              ) {
                pushedStart = newStart;
                pushedEnd = newEnd;
              }
            }
          }
        }
      }
      return { start, length, spanPx, spanOk, corner, cornerOk, cornerConfirmed, pushedStart, pushedEnd };
    };

    // Evidence-based demotion of isolated anchors: a short on-line edge (a
    // door leaf crossing the gap) sandwiched between two non-anchor runs is
    // demoted ONLY when merging those runs across it resolves to a confirmed
    // corner rebuild — i.e. the mask proves the shape continues without it.
    // A real wall with jagged surroundings has no such confirmed corner and
    // keeps anchoring its runs.
    const runIndexByStart = new Map<number, number>();
    const runIndexByEnd = new Map<number, number>();
    runs.forEach((r, idx) => {
      runIndexByStart.set(r.start, idx);
      runIndexByEnd.set((r.start + r.length - 1) % n, idx);
    });
    const consumed = new Set<number>();
    const actions: ResolvedRun[] = [];
    for (let e = 0; e < n; e++) {
      if (!anchor[e] || edgeLengths[e] >= isolatedAnchorPx) continue;
      const prevRunIdx = runIndexByEnd.get((e - 1 + n) % n);
      const nextRunIdx = runIndexByStart.get((e + 1) % n);
      if (prevRunIdx === undefined || nextRunIdx === undefined || prevRunIdx === nextRunIdx) continue;
      if (consumed.has(prevRunIdx) || consumed.has(nextRunIdx)) continue;
      const prevRun = runs[prevRunIdx];
      const merged = resolveRun(prevRun.start, prevRun.length + 1 + runs[nextRunIdx].length);
      // Demotion destroys real evidence (an on-line edge), so it needs the
      // strong form: the merged corner's extensions must grow over mask lines.
      if (!merged.cornerConfirmed) continue;
      if (debug) console.log(`[remove_unsupported_segments] demoting isolated anchor edge ${e} (${Math.round(this.pixelsToCm(edgeLengths[e]))}cm) — merged rebuild confirmed by mask`);
      consumed.add(prevRunIdx);
      consumed.add(nextRunIdx);
      actions.push(merged);
    }
    runs.forEach((r, idx) => {
      if (!consumed.has(idx)) actions.push(resolveRun(r.start, r.length));
    });

    const shoelace = (list: Point2D[]): number => {
      let area = 0;
      for (let k = 0; k < list.length; k++) {
        const a = list[k];
        const b = list[(k + 1) % list.length];
        area += a.x * b.y - b.x * a.y;
      }
      return area / 2;
    };

    // For each vertex: keep as-is, drop, or replace with a rebuilt corner.
    const maxAreaChangePx2 = maxAreaChangeM2 * this.cmToPixels(100) ** 2;
    const replacement: (Point2D | null | undefined)[] = new Array(n).fill(undefined); // undefined = keep
    for (const action of actions) {
      const first = action.start; // first non-anchor edge → run vertices first..last+1
      const last = (action.start + action.length - 1) % n;
      const vStart = first; // shared with the preceding anchor edge
      const vEnd = (last + 1) % n; // shared with the following anchor edge

      // The gap the anchors must close. Wider than maxSpanPx = genuinely
      // unclear geometry — leave it for a human.
      if (!action.spanOk) {
        if (debug) console.warn(`[remove_unsupported_segments] run edges ${first}..${last} skipped: span ${Math.round(this.pixelsToCm(action.spanPx))}cm > maxSpanCm ${maxSpanCm}`);
        continue;
      }

      // A fixup may only change the shape by an artifact's worth of area.
      // The loop of the replaced chain plus the new path (corner point, or
      // the direct closing segment) encloses exactly the area this action
      // adds or removes — if that exceeds the cap, replacing the run would
      // amputate (or invent) real geometry, so leave it unfixed instead.
      const changeLoop: Point2D[] = [];
      for (let k = 0; k <= action.length; k++) {
        changeLoop.push(pts[(action.start + k) % n]);
      }
      if (action.cornerOk && action.corner) {
        changeLoop.push(action.corner);
      } else if (action.pushedStart && action.pushedEnd) {
        changeLoop.push(action.pushedEnd, action.pushedStart);
      }
      const changeAreaPx2 = Math.abs(shoelace(changeLoop));
      if (changeAreaPx2 > maxAreaChangePx2) {
        if (debug) {
          console.warn(
            `[remove_unsupported_segments] run edges ${first}..${last} skipped: would change ${(changeAreaPx2 / this.cmToPixels(100) ** 2).toFixed(1)}m² > maxAreaChangeM2 ${maxAreaChangeM2}`,
          );
        }
        continue;
      }

      if (debug) {
        const cornerStr = action.corner ? `(${Math.round(this.pixelsToCm(action.corner.x))},${Math.round(this.pixelsToCm(action.corner.y))})cm` : 'none (near-collinear neighbours)';
        const outcome = action.cornerOk
          ? 'REBUILD CORNER'
          : action.pushedStart && action.pushedEnd
            ? 'PUSH OUT to found line'
            : action.length > 1
              ? 'CONNECT DIRECTLY (drop interior)'
              : 'no-op (single edge, no corner)';
        console.log(`[remove_unsupported_segments] run edges ${first}..${last}: corner ${cornerStr}${action.corner && !action.cornerOk ? ' (too far out)' : ''} → ${outcome}`);
      }

      // Interior vertices always go. The run endpoints merge into the rebuilt
      // corner when one exists, or move onto the pushed-out landed edge; only
      // as a last resort do they stay (straight bridge). In every case the
      // neighbour edges keep their lines — they are only extended.
      for (let k = 1; k < action.length; k++) {
        replacement[(first + k) % n] = null;
      }
      if (action.cornerOk && action.corner) {
        replacement[vStart] = action.corner;
        replacement[vEnd] = null;
      } else if (action.pushedStart && action.pushedEnd) {
        replacement[vStart] = action.pushedStart;
        replacement[vEnd] = action.pushedEnd;
      }
    }

    const newPoints: Point2D[] = [];
    for (let k = 0; k < n; k++) {
      const r = replacement[k];
      if (r === null) continue;
      newPoints.push(r === undefined ? pts[k] : r);
    }

    if (newPoints.length < 4 || newPoints.length === n) {
      if (debug) console.warn(`[remove_unsupported_segments] result kept original: ${newPoints.length === n ? 'no vertices removed' : 'would leave < 4 vertices'}`);
      return polygon;
    }

    // Safety: reject destructive rewrites (mirrors pixel alignment's guard).
    const originalArea = shoelace(pts);
    const newArea = shoelace(newPoints);
    if ((originalArea > 0) !== (newArea > 0) || Math.abs(newArea) < Math.abs(originalArea) * 0.5) {
      if (debug) console.warn('[remove_unsupported_segments] result rejected by area guard (winding flip or >50% area loss)');
      return polygon;
    }

    if (debug) console.log(`[remove_unsupported_segments] removed ${n - newPoints.length} vertices (${n} → ${newPoints.length})`);
    return this.pixelPolygonToWorldPolygon(createPolygon(newPoints, polygon.isHole));
  }

  /**
   * Remove detail that is small relative to the polygon's overall size.
   *
   * For a 50×50m room, a 10cm bump is irrelevant — but for a 2×2m closet the
   * same 10cm bump is significant.  This pass computes the polygon perimeter and
   * iteratively removes vertices whose perpendicular deviation from the line
   * between their neighbours is below a fraction of that perimeter.
   * Short edges (relative to perimeter) are also collapsed.
   *
   * The perimeter is recalculated after each iteration so the thresholds
   * naturally tighten as the polygon shrinks, preventing over-simplification.
   *
   * @param polygon - Polygon to simplify
   * @param deviationThreshold - Max perpendicular deviation as fraction of perimeter (default 0.002 = 0.2%)
   * @param minEdgeFraction - Edges shorter than this fraction of perimeter are collapse candidates (default 0.005 = 0.5%)
   * @param maxIterations - Safety cap on iterations (default 50)
   */
  private removeRelativeDetail(polygon: Polygon, deviationThreshold = 0.002, minEdgeFraction = 0.005, maxIterations = 50): Polygon {
    if (polygon.length <= 4) return polygon;

    const points = polygon.points.slice();

    const calcPerimeter = (pts: Point2D[]): number => {
      let perim = 0;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        perim += Math.sqrt(dx * dx + dy * dy);
      }
      return perim;
    };

    const perpDistance = (prev: Point2D, curr: Point2D, next: Point2D): number => {
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 1e-10) return 0;
      // |cross product| / length = perpendicular distance
      return Math.abs((curr.x - prev.x) * dy - (curr.y - prev.y) * dx) / len;
    };

    for (let iter = 0; iter < maxIterations; iter++) {
      if (points.length <= 4) break;

      const perimeter = calcPerimeter(points);
      if (perimeter < 1e-10) break;

      const maxDeviation = perimeter * deviationThreshold;
      const minEdgeLen = perimeter * minEdgeFraction;
      const n = points.length;

      // Score each vertex: how "removable" it is.
      // Lower score = more removable (smaller detail relative to polygon).
      // We remove the single most removable vertex per iteration so that the
      // perimeter is recalculated and thresholds adapt.
      let bestIdx = -1;
      let bestScore = Infinity;

      for (let i = 0; i < n; i++) {
        const prev = points[(i - 1 + n) % n];
        const curr = points[i];
        const next = points[(i + 1) % n];

        const dist = perpDistance(prev, curr, next);

        // Only consider vertices with deviation below the threshold
        if (dist >= maxDeviation) continue;

        // Also check that at least one adjacent edge is short
        const ePrev = Math.sqrt((curr.x - prev.x) ** 2 + (curr.y - prev.y) ** 2);
        const eNext = Math.sqrt((next.x - curr.x) ** 2 + (next.y - curr.y) ** 2);
        const shortEdge = Math.min(ePrev, eNext);

        if (shortEdge >= minEdgeLen && dist >= maxDeviation * 0.5) continue;

        // Score: deviation normalized by perimeter — smaller = more removable
        const score = dist / perimeter;
        if (score < bestScore) {
          bestScore = score;
          bestIdx = i;
        }
      }

      // Nothing left to remove
      if (bestIdx === -1) break;

      points.splice(bestIdx, 1);
    }

    if (points.length < 3) return polygon;
    if (points.length === polygon.length) return polygon;

    return createPolygon(points, polygon.isHole);
  }

  /**
   * Remove edges that are shorter than a minimum length threshold (in cm)
   * Works by removing vertices that create short edges
   */
  private removeSmallEdges(polygon: Polygon, minEdgeLength = 2.0): Polygon {
    if (polygon.length < 4) return polygon;

    const points = polygon.points.slice();
    const filteredPoints: Point2D[] = [];
    let removedCount = 0;

    for (let i = 0; i < points.length; i++) {
      const current = points[i];
      const next = points[(i + 1) % points.length];

      const dx = next.x - current.x;
      const dy = next.y - current.y;
      const edgeLength = Math.sqrt(dx * dx + dy * dy);

      // Keep the current point if the edge to the next point is long enough
      if (edgeLength >= minEdgeLength) {
        filteredPoints.push(current);
      } else {
        removedCount++;
      }
    }

    // Ensure we keep at least 3 points for a valid polygon
    if (filteredPoints.length < 3) {
      return polygon;
    }

    if (removedCount > 0) {
      return createPolygon(filteredPoints, polygon.isHole);
    }

    return polygon;
  }

  /**
   * Snap vertices to a regular grid for simplification
   * This is the "special hashing algorithm" - vertices within the same grid cell are merged
   *
   * @param polygon - Polygon to simplify
   * @param gridSizeCm - Size of grid cells in centimeters (default 0.5cm)
   * @param snapToGrid - If true, snap to grid centers; if false, use original positions (default false)
   * @param minimizeAreaDeviation - If true, when multiple vertices snap to the same grid cell, pick the one
   *                                that minimizes the polygon's area deviation (neither shrinking nor growing)
   * @param removeCollinear - If true, iteratively remove collinear points (default true)
   *
   * Grid-based vertex snapping works by:
   * 1. Snap each vertex to a grid cell (for grouping/selection)
   * 2. Remove consecutive duplicates (vertices in the same grid cell)
   *    - When minimizeAreaDeviation=true, picks the vertex that minimizes area change (loss or gain)
   *    - When minimizeAreaDeviation=false (default), keeps the first vertex in each group
   * 3. Remove collinear points iteratively
   * 4. Return either snapped positions OR original positions based on snapToGrid flag
   *
   * Example: gridSizeCm = 0.5 means vertices within 0.5cm are grouped together
   * Smaller values = more precision, less simplification
   * Larger values = less precision, more simplification
   */
  private gridSnapVertices(polygon: Polygon, gridSizeCm = 0.5, snapToGrid = false, minimizeAreaDeviation = false, removeCollinear = true): Polygon {
    if (polygon.length < 4 || gridSizeCm <= 0) return polygon;

    const originalPoints = polygon.points.slice();
    const snappedPoints: Point2D[] = [];
    const originalIndices: number[] = []; // Track which original point each snapped point came from

    // Snap each vertex to the grid (for selection purposes only)
    for (let i = 0; i < originalPoints.length; i++) {
      const point = originalPoints[i];
      // Calculate grid cell coordinates
      const gridX = Math.round(point.x / gridSizeCm) * gridSizeCm;
      const gridY = Math.round(point.y / gridSizeCm) * gridSizeCm;

      snappedPoints.push({ x: gridX, y: gridY });
      originalIndices.push(i);
    }

    // Group consecutive points that snap to the same grid cell
    // When minimizeAreaDeviation is enabled, pick the point that minimizes area change
    const deduplicatedPoints: Point2D[] = [];

    // First pass: identify groups of consecutive points in the same grid cell
    // Each group is an array of original indices
    const groups: number[][] = [];
    let currentGroup: number[] = [0];

    for (let i = 1; i < snappedPoints.length; i++) {
      const prev = snappedPoints[i - 1];
      const curr = snappedPoints[i];
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= 0.001) {
        // Same grid cell as previous - add to current group
        currentGroup.push(i);
      } else {
        // Different grid cell - start new group
        groups.push(currentGroup);
        currentGroup = [i];
      }
    }
    // Don't forget the last group
    groups.push(currentGroup);

    // Handle wrap-around: check if first and last groups should be merged
    if (groups.length > 1) {
      const firstSnapped = snappedPoints[groups[0][0]];
      const lastSnapped = snappedPoints[groups[groups.length - 1][0]];
      const dx = firstSnapped.x - lastSnapped.x;
      const dy = firstSnapped.y - lastSnapped.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= 0.001) {
        // Merge first group into last group
        const lastGroup = groups.pop() ?? [];
        const firstGroup = groups.shift() ?? [];
        groups.push([...lastGroup, ...firstGroup]);
      }
    }

    // Second pass: for each group, store the snapped point and keep all original indices
    // Area optimization will pick the best original point after collinear collapse
    const deduplicatedGroups: number[][] = []; // All original indices for each deduplicated point
    for (const group of groups) {
      // Use the snapped position of the first point (they all snap to the same cell)
      deduplicatedPoints.push(snappedPoints[group[0]]);
      // Keep all original indices from this group for area optimization later
      deduplicatedGroups.push(group.map((i) => originalIndices[i]));
    }

    // Remove spike points (where polygon goes somewhere and comes back)
    // This includes:
    // 1. When prev and next points are the same (A -> B -> A)
    // 2. When edges go in opposite directions (backtracking) - even if not exactly 180 degrees
    // Must be done before collinear removal as they don't work well together
    let currentPoints = deduplicatedPoints.slice();
    let currentGroups = deduplicatedGroups.slice();

    let removedSpikes = true;
    while (removedSpikes && currentPoints.length >= 3) {
      removedSpikes = false;
      const nextPoints: Point2D[] = [];
      const nextGroups: number[][] = [];
      const n = currentPoints.length;

      for (let i = 0; i < n; i++) {
        const prev = currentPoints[(i - 1 + n) % n];
        const current = currentPoints[i];
        const next = currentPoints[(i + 1) % n];

        // Check if prev and next are the same point (spike type 1: A -> B -> A)
        const dxPrevNext = next.x - prev.x;
        const dyPrevNext = next.y - prev.y;
        const distPrevNext = Math.sqrt(dxPrevNext * dxPrevNext + dyPrevNext * dyPrevNext);

        if (distPrevNext <= 0.001) {
          // Prev and next are the same - this is a spike, remove it
          removedSpikes = true;
          continue;
        }

        // Check for backtracking spike (type 2): edges go in opposite directions
        // Vector from prev to current
        const v1x = current.x - prev.x;
        const v1y = current.y - prev.y;
        const len1 = Math.sqrt(v1x * v1x + v1y * v1y);

        // Vector from current to next
        const v2x = next.x - current.x;
        const v2y = next.y - current.y;
        const len2 = Math.sqrt(v2x * v2x + v2y * v2y);

        if (len1 > 0.001 && len2 > 0.001) {
          // Normalize vectors
          const n1x = v1x / len1;
          const n1y = v1y / len1;
          const n2x = v2x / len2;
          const n2y = v2y / len2;

          // Dot product: negative means going backwards (more than 90 degree turn)
          const dot = n1x * n2x + n1y * n2y;

          // Cross product to check collinearity
          const cross = n1x * n2y - n1y * n2x;

          // If collinear (cross ~= 0) and going backwards (dot < 0), it's a spike
          if (Math.abs(cross) < 0.1 && dot < -0.5) {
            removedSpikes = true;
            continue;
          }
        }

        // Not a spike, keep the point
        nextPoints.push(current);
        nextGroups.push(currentGroups[i]);
      }

      currentPoints = nextPoints;
      currentGroups = nextGroups;
    }

    if (currentPoints.length < 3) {
      return polygon; // Not enough points for a valid polygon
    }

    // Remove collinear points iteratively (using snapped coordinates for geometry test)
    // Need to iterate because removing one point can create new collinear configurations
    if (removeCollinear) {
      let removedAny = true;
      const maxIterations = 10;
      let iteration = 0;

      while (removedAny && iteration < maxIterations) {
        removedAny = false;
        const n = currentPoints.length;

        // First pass: mark collinear candidates
        const isCollinear: boolean[] = new Array(n).fill(false);
        for (let i = 0; i < n; i++) {
          const prev = currentPoints[(i - 1 + n) % n];
          const current = currentPoints[i];
          const next = currentPoints[(i + 1) % n];

          const v1 = { x: current.x - prev.x, y: current.y - prev.y };
          const v2 = { x: next.x - current.x, y: next.y - current.y };
          const cross = v1.x * v2.y - v1.y * v2.x;

          if (Math.abs(cross) <= 0.01) {
            isCollinear[i] = true;
          }
        }

        // Second pass: only remove if no adjacent point is also collinear
        // In a chain of consecutive collinear points, remove every other one to make progress
        const nextPoints: Point2D[] = [];
        const nextGroups: number[][] = [];
        for (let i = 0; i < n; i++) {
          if (isCollinear[i]) {
            const prevIdx = (i - 1 + n) % n;
            const nextIdx = (i + 1) % n;
            if (isCollinear[prevIdx] || isCollinear[nextIdx]) {
              // Adjacent point also collinear — we're in a chain.
              // Only keep if this is the first point of the chain (prev is not collinear)
              if (!isCollinear[prevIdx]) {
                nextPoints.push(currentPoints[i]);
                nextGroups.push(currentGroups[i]);
              } else {
                removedAny = true;
              }
              continue;
            }
            removedAny = true;
          } else {
            nextPoints.push(currentPoints[i]);
            nextGroups.push(currentGroups[i]);
          }
        }

        // Update for next iteration
        currentPoints = nextPoints;
        currentGroups = nextGroups;
        iteration++;

        // Safety check - ensure we have enough points
        if (currentPoints.length < 3) {
          return polygon;
        }
      }
    }

    const finalSnappedPoints = currentPoints;
    const finalGroups = currentGroups;

    // If not snapping to grid, use original points (no area optimization needed)
    if (!snapToGrid) {
      const finalPoints = finalGroups.map((group) => originalPoints[group[0]]);
      return createPolygon(finalPoints, polygon.isHole);
    }

    // Area minimization: for each vertex, pick the original point from its group
    // that minimizes local area deviation (triangle formed with neighbors)
    // Neighbors are always the first original point from their respective groups
    if (minimizeAreaDeviation && finalGroups.length >= 3) {
      const n = finalGroups.length;

      // Get the first original point from each group (these are fixed neighbors)
      const firstOriginalPoints: Point2D[] = finalGroups.map((group) => originalPoints[group[0]]);

      // Helper to calculate signed triangle area (for a vertex and its neighbors)
      const triangleArea = (prev: Point2D, curr: Point2D, next: Point2D): number => {
        return 0.5 * ((curr.x - prev.x) * (next.y - prev.y) - (next.x - prev.x) * (curr.y - prev.y));
      };

      const selectedPoints: Point2D[] = [];

      for (let i = 0; i < n; i++) {
        const group = finalGroups[i];

        if (group.length === 1) {
          // Only one candidate, use it
          selectedPoints.push(originalPoints[group[0]]);
          continue;
        }

        // Use first original points from neighboring groups as fixed neighbors
        const prevIdx = (i - 1 + n) % n;
        const nextIdx = (i + 1) % n;
        const prev = firstOriginalPoints[prevIdx];
        const next = firstOriginalPoints[nextIdx];

        // Calculate the target area using the snapped position
        const snappedArea = triangleArea(prev, finalSnappedPoints[i], next);

        let bestPoint = originalPoints[group[0]];
        let bestAreaDiff = Infinity;

        for (const candidateIdx of group) {
          const candidatePoint = originalPoints[candidateIdx];
          const candidateArea = triangleArea(prev, candidatePoint, next);
          const areaDiff = Math.abs(candidateArea - snappedArea);

          // Prefer candidates closer to the snapped triangle area, with tie-breaker for first point
          if (areaDiff < bestAreaDiff - 0.0001) {
            bestAreaDiff = areaDiff;
            bestPoint = candidatePoint;
          }
        }

        selectedPoints.push(bestPoint);
      }

      return createPolygon(selectedPoints, polygon.isHole);
    }

    // snapToGrid is true but minimizeAreaDeviation is false: use snapped positions
    return createPolygon(finalSnappedPoints, polygon.isHole);
  }

  /**
   * Snap vertices to a hexagonal grid for simplification.
   * Uses a hexagonal spatial hash — vertices within the same hexagon cell are merged
   * into one representative point (closest to the hexagon center).
   *
   * Hexagonal grids provide more uniform neighbor distances than square grids,
   * which can produce more natural-looking simplification for organic shapes.
   *
   * @param polygon - Polygon to simplify
   * @param hexSizeCm - Hexagon size in centimeters (center-to-vertex distance, i.e. circumradius)
   * @param snapToCenter - If true, snap merged point to the hexagon cell center; if false, use the
   *                       original point closest to the center (default false)
   * @param removeCollinear - If true, iteratively remove collinear points (default true)
   */
  private hexagonSnapVertices(polygon: Polygon, hexSizeCm = 0.5, snapToCenter = false, removeCollinear = true): Polygon {
    if (polygon.length < 4 || hexSizeCm <= 0) return polygon;

    const originalPoints = polygon.points.slice();

    // Hexagonal grid constants
    // For a flat-top hexagon with circumradius R (center-to-vertex):
    //   width  = 2 * R
    //   height = sqrt(3) * R
    // Column spacing = 3/2 * R, row spacing = sqrt(3) * R
    // Odd columns are offset vertically by sqrt(3)/2 * R
    const R = hexSizeCm;
    const colSpacing = 1.5 * R;
    const rowSpacing = Math.sqrt(3) * R;
    const halfRowSpacing = rowSpacing / 2;

    /**
     * Convert a 2D point to its hexagonal cell coordinates (col, row).
     * Uses axial coordinate conversion for flat-top hexagons.
     */
    const pointToHexCell = (p: Point2D): { col: number; row: number } => {
      // Approximate column
      const col = p.x / colSpacing;
      // Odd columns are shifted down by half a row
      const isOddCol = Math.round(col) % 2 !== 0;
      const rowOffset = isOddCol ? halfRowSpacing : 0;
      const row = (p.y - rowOffset) / rowSpacing;

      // We need to check the nearest 3 candidate hex centers to find the true closest one,
      // because the rectangular approximation above can be off near hex edges.
      const colRound = Math.round(col);
      const rowRound = Math.round(row);

      let bestCol = colRound;
      let bestRow = rowRound;
      let bestDist = Infinity;

      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          const cc = colRound + dc;
          const rr = rowRound + dr;
          const oddC = cc % 2 !== 0;
          const cx = cc * colSpacing;
          const cy = rr * rowSpacing + (oddC ? halfRowSpacing : 0);
          const dx = p.x - cx;
          const dy = p.y - cy;
          const dist = dx * dx + dy * dy;
          if (dist < bestDist) {
            bestDist = dist;
            bestCol = cc;
            bestRow = rr;
          }
        }
      }

      return { col: bestCol, row: bestRow };
    };

    /** Get the world-space center of a hexagonal cell */
    const hexCellCenter = (col: number, row: number): Point2D => {
      const isOdd = col % 2 !== 0;
      return {
        x: col * colSpacing,
        y: row * rowSpacing + (isOdd ? halfRowSpacing : 0),
      };
    };

    // Map each vertex to its hex cell
    const cellKeys: string[] = [];
    const cellCoords: { col: number; row: number }[] = [];

    for (let i = 0; i < originalPoints.length; i++) {
      const cell = pointToHexCell(originalPoints[i]);
      cellKeys.push(`${cell.col},${cell.row}`);
      cellCoords.push(cell);
    }

    // Group consecutive points that fall into the same hex cell
    const groups: number[][] = [];
    let currentGroup: number[] = [0];

    for (let i = 1; i < originalPoints.length; i++) {
      if (cellKeys[i] === cellKeys[i - 1]) {
        currentGroup.push(i);
      } else {
        groups.push(currentGroup);
        currentGroup = [i];
      }
    }
    groups.push(currentGroup);

    // Handle wrap-around: check if first and last groups share the same hex cell
    if (groups.length > 1 && cellKeys[groups[0][0]] === cellKeys[groups[groups.length - 1][0]]) {
      const lastGroup = groups.pop() ?? [];
      const firstGroup = groups.shift() ?? [];
      groups.push([...lastGroup, ...firstGroup]);
    }

    // For each group, pick the representative point:
    // - The original point closest to the hexagon center
    const deduplicatedPoints: Point2D[] = [];
    const deduplicatedGroups: number[][] = [];

    for (const group of groups) {
      const cell = cellCoords[group[0]];
      const center = hexCellCenter(cell.col, cell.row);

      // Find the original point closest to the hex center
      let closestIdx = group[0];
      let closestDist = Infinity;
      for (const idx of group) {
        const p = originalPoints[idx];
        const dx = p.x - center.x;
        const dy = p.y - center.y;
        const dist = dx * dx + dy * dy;
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = idx;
        }
      }

      if (snapToCenter) {
        deduplicatedPoints.push(center);
      } else {
        deduplicatedPoints.push(originalPoints[closestIdx]);
      }
      deduplicatedGroups.push(group);
    }

    // Remove spike points (same logic as gridSnapVertices)
    let currentPoints = deduplicatedPoints.slice();
    let currentGroups = deduplicatedGroups.slice();

    let removedSpikes = true;
    while (removedSpikes && currentPoints.length >= 3) {
      removedSpikes = false;
      const nextPoints: Point2D[] = [];
      const nextGroups: number[][] = [];
      const n = currentPoints.length;

      for (let i = 0; i < n; i++) {
        const prev = currentPoints[(i - 1 + n) % n];
        const current = currentPoints[i];
        const next = currentPoints[(i + 1) % n];

        // Check if prev and next are the same point (spike: A -> B -> A)
        const dxPN = next.x - prev.x;
        const dyPN = next.y - prev.y;
        if (Math.sqrt(dxPN * dxPN + dyPN * dyPN) <= 0.001) {
          removedSpikes = true;
          continue;
        }

        // Check for backtracking spike
        const v1x = current.x - prev.x;
        const v1y = current.y - prev.y;
        const len1 = Math.sqrt(v1x * v1x + v1y * v1y);
        const v2x = next.x - current.x;
        const v2y = next.y - current.y;
        const len2 = Math.sqrt(v2x * v2x + v2y * v2y);

        if (len1 > 0.001 && len2 > 0.001) {
          const dot = (v1x / len1) * (v2x / len2) + (v1y / len1) * (v2y / len2);
          const cross = (v1x / len1) * (v2y / len2) - (v1y / len1) * (v2x / len2);
          if (Math.abs(cross) < 0.1 && dot < -0.5) {
            removedSpikes = true;
            continue;
          }
        }

        nextPoints.push(current);
        nextGroups.push(currentGroups[i]);
      }

      currentPoints = nextPoints;
      currentGroups = nextGroups;
    }

    if (currentPoints.length < 3) {
      return polygon;
    }

    // Remove collinear points iteratively
    if (removeCollinear) {
      let removedAny = true;
      const maxIterations = 10;
      let iteration = 0;

      while (removedAny && iteration < maxIterations) {
        removedAny = false;
        const n = currentPoints.length;

        // First pass: mark collinear candidates
        const isCollinear: boolean[] = new Array(n).fill(false);
        for (let i = 0; i < n; i++) {
          const prev = currentPoints[(i - 1 + n) % n];
          const current = currentPoints[i];
          const next = currentPoints[(i + 1) % n];

          const v1 = { x: current.x - prev.x, y: current.y - prev.y };
          const v2 = { x: next.x - current.x, y: next.y - current.y };
          const cross = v1.x * v2.y - v1.y * v2.x;

          if (Math.abs(cross) <= 0.01) {
            isCollinear[i] = true;
          }
        }

        // Second pass: only remove if no adjacent point is also collinear
        // In a chain of consecutive collinear points, remove every other one to make progress
        const nextPoints: Point2D[] = [];
        const nextGroups: number[][] = [];
        for (let i = 0; i < n; i++) {
          if (isCollinear[i]) {
            const prevIdx = (i - 1 + n) % n;
            const nextIdx = (i + 1) % n;
            if (isCollinear[prevIdx] || isCollinear[nextIdx]) {
              // Adjacent point also collinear — we're in a chain.
              // Only keep if this is the first point of the chain (prev is not collinear)
              if (!isCollinear[prevIdx]) {
                nextPoints.push(currentPoints[i]);
                nextGroups.push(currentGroups[i]);
              } else {
                removedAny = true;
              }
              continue;
            }
            removedAny = true;
          } else {
            nextPoints.push(currentPoints[i]);
            nextGroups.push(currentGroups[i]);
          }
        }

        currentPoints = nextPoints;
        currentGroups = nextGroups;
        iteration++;

        if (currentPoints.length < 3) {
          return polygon;
        }
      }
    }

    return createPolygon(currentPoints, polygon.isHole);
  }

  /**
   * Merge multiple polygons into one by tracing the outer boundary
   * Preserves exact polygon edges without using convex hull
   */
  private mergePolygons(polygons: Polygon[]): Polygon[] {
    if (polygons.length === 0) return [];
    if (polygons.length === 1) return polygons;

    // Collect all edges from all polygons with polygon ID
    const edges: Array<{ start: Point2D; end: Point2D; polyId: number }> = [];

    for (let polyId = 0; polyId < polygons.length; polyId++) {
      const polygon = polygons[polyId];
      for (let i = 0; i < polygon.length; i++) {
        const start = polygon[i];
        const end = polygon[(i + 1) % polygon.length];
        edges.push({ start, end, polyId });
      }
    }

    // Create edge keys for comparison
    const makeEdgeKey = (p1: Point2D, p2: Point2D) => {
      const x1 = p1.x.toFixed(6);
      const y1 = p1.y.toFixed(6);
      const x2 = p2.x.toFixed(6);
      const y2 = p2.y.toFixed(6);
      return `${x1},${y1}->${x2},${y2}`;
    };

    // Count how many times each edge appears (forward and reverse)
    const edgeCounts = new Map<string, number>();
    edges.forEach((edge) => {
      const fwdKey = makeEdgeKey(edge.start, edge.end);
      const revKey = makeEdgeKey(edge.end, edge.start);
      edgeCounts.set(fwdKey, (edgeCounts.get(fwdKey) || 0) + 1);
      edgeCounts.set(revKey, (edgeCounts.get(revKey) || 0) + 1);
    });

    // An edge is on the outer boundary if its reverse edge appears less frequently
    // (meaning it's not fully shared with another polygon)
    const outerEdges: Array<{ start: Point2D; end: Point2D; idx: number }> = [];

    edges.forEach((edge, idx) => {
      const fwdKey = makeEdgeKey(edge.start, edge.end);
      const revKey = makeEdgeKey(edge.end, edge.start);
      const fwdCount = edgeCounts.get(fwdKey) || 0;
      const revCount = edgeCounts.get(revKey) || 0;

      // Edge is outer if it appears more times forward than reverse
      // (or if reverse doesn't exist)
      if (fwdCount > revCount) {
        outerEdges.push({ start: edge.start, end: edge.end, idx });
      }
    });

    if (outerEdges.length === 0) {
      // Find edges that are close and going in opposite directions from different polygons
      // These should be removed and the polygons connected at those points
      const edgesToRemove = new Set<number>();
      const threshold = 2.0; // Distance threshold for "close" edges in pixels

      for (let i = 0; i < edges.length; i++) {
        if (edgesToRemove.has(i)) continue;
        const edge1 = edges[i];

        for (let j = i + 1; j < edges.length; j++) {
          if (edgesToRemove.has(j)) continue;
          const edge2 = edges[j];

          // Skip if same polygon
          if (edge1.polyId === edge2.polyId) continue;

          // Check if edges are parallel and opposite direction
          const dir1 = {
            x: edge1.end.x - edge1.start.x,
            y: edge1.end.y - edge1.start.y,
          };
          const dir2 = {
            x: edge2.end.x - edge2.start.x,
            y: edge2.end.y - edge2.start.y,
          };

          const len1 = Math.sqrt(dir1.x * dir1.x + dir1.y * dir1.y);
          const len2 = Math.sqrt(dir2.x * dir2.x + dir2.y * dir2.y);

          if (len1 < 1e-6 || len2 < 1e-6) continue;

          // Normalize
          dir1.x /= len1;
          dir1.y /= len1;
          dir2.x /= len2;
          dir2.y /= len2;

          // Check if opposite direction (dot product ~ -1)
          const dot = dir1.x * dir2.x + dir1.y * dir2.y;
          if (dot > -0.9) continue; // Not opposite enough

          // Check if edges are close (check average distance between endpoints)
          const d1 = Math.sqrt((edge1.start.x - edge2.start.x) ** 2 + (edge1.start.y - edge2.start.y) ** 2);
          const d2 = Math.sqrt((edge1.end.x - edge2.end.x) ** 2 + (edge1.end.y - edge2.end.y) ** 2);
          const d3 = Math.sqrt((edge1.start.x - edge2.end.x) ** 2 + (edge1.start.y - edge2.end.y) ** 2);
          const d4 = Math.sqrt((edge1.end.x - edge2.start.x) ** 2 + (edge1.end.y - edge2.start.y) ** 2);

          const minDist = Math.min(d1, d2, d3, d4);
          if (minDist < threshold) {
            edgesToRemove.add(i);
            edgesToRemove.add(j);
          }
        }
      }

      // Count edges per polygon
      const edgesPerPoly = new Map<number, number>();
      edges.forEach((edge) => {
        edgesPerPoly.set(edge.polyId, (edgesPerPoly.get(edge.polyId) || 0) + 1);
      });

      const removedPerPoly = new Map<number, number>();
      edgesToRemove.forEach((idx) => {
        const polyId = edges[idx].polyId;
        removedPerPoly.set(polyId, (removedPerPoly.get(polyId) || 0) + 1);
      });

      // Build new edge list without removed edges
      const filteredEdges: Array<{ start: Point2D; end: Point2D; polyId: number; idx: number }> = [];
      edges.forEach((edge, idx) => {
        if (!edgesToRemove.has(idx)) {
          filteredEdges.push({ start: edge.start, end: edge.end, polyId: edge.polyId, idx: filteredEdges.length });
        }
      });

      if (filteredEdges.length === 0) {
        console.warn('   ⚠️  All edges removed, returning first polygon');
        return [polygons[0]];
      }

      // Now trace the outer boundary with filtered edges
      return [this.traceOuterBoundary(filteredEdges)];
    }

    // Trace the outer boundary by connecting outer edges
    const used = new Set<number>();
    const mergedPoints: Point2D[] = [];

    // Start with the leftmost point (guaranteed to be on outer boundary)
    let current = outerEdges.reduce((min, e) => (e.start.x < min.start.x || (e.start.x === min.start.x && e.start.y < min.start.y) ? e : min), outerEdges[0]);

    const startPoint = current.start;
    mergedPoints.push({ ...current.start });
    used.add(current.idx);

    let iterations = 0;
    const maxIterations = outerEdges.length * 2;

    while (iterations < maxIterations) {
      iterations++;

      // Find next outer edge that starts where current edge ends
      const currentEndKey = `${current.end.x.toFixed(6)},${current.end.y.toFixed(6)}`;
      const nextEdge = outerEdges.find((e) => {
        if (used.has(e.idx)) return false;
        const startKey = `${e.start.x.toFixed(6)},${e.start.y.toFixed(6)}`;
        return startKey === currentEndKey;
      });

      if (!nextEdge) {
        // Try to close the loop
        const distToStart = Math.sqrt((current.end.x - startPoint.x) ** 2 + (current.end.y - startPoint.y) ** 2);
        if (distToStart < 1e-3) {
          // Successfully closed
          break;
        }
        console.warn('   ⚠️  Could not find next edge, breaking');
        break;
      }

      mergedPoints.push({ ...nextEdge.start });
      used.add(nextEdge.idx);
      current = nextEdge;

      // Check if we've returned to start
      const distToStart = Math.sqrt((current.end.x - startPoint.x) ** 2 + (current.end.y - startPoint.y) ** 2);
      if (distToStart < 1e-3) {
        break;
      }
    }

    if (mergedPoints.length < 3) {
      console.warn('   ⚠️  Merged polygon has < 3 points, returning first polygon');
      return [polygons[0]];
    }
    return [createPolygon(mergedPoints, false)];
  }

  /**
   * Trace outer boundary from a list of edges
   * Handles multiple connected components
   */
  private traceOuterBoundary(edges: Array<{ start: Point2D; end: Point2D; idx: number }>): Polygon {
    if (edges.length === 0) return createPolygon([], false);

    // Build adjacency map
    const adjacency = new Map<string, typeof edges>();
    edges.forEach((edge) => {
      const key = `${edge.start.x.toFixed(6)},${edge.start.y.toFixed(6)}`;
      if (!adjacency.has(key)) adjacency.set(key, []);
      adjacency.get(key)?.push(edge);
    });

    const globalUsed = new Set<number>();
    const components: Point2D[][] = [];

    // Trace all connected components
    for (const startEdge of edges) {
      if (globalUsed.has(startEdge.idx)) continue;

      const component: Point2D[] = [];
      const startKey = `${startEdge.start.x.toFixed(6)},${startEdge.start.y.toFixed(6)}`;
      let currentKey = startKey;
      let prevAngle = Math.PI;
      const maxIter = edges.length;
      let iterations = 0;

      do {
        const outEdges = adjacency.get(currentKey) || [];
        if (outEdges.length === 0) break;

        // Take rightmost turn
        let bestEdge = null;
        let smallestTurn = Infinity;

        for (const edge of outEdges) {
          if (globalUsed.has(edge.idx) && iterations > 0) continue;

          const angle = Math.atan2(edge.end.y - edge.start.y, edge.end.x - edge.start.x);
          let turn = prevAngle - angle;
          while (turn < 0) turn += 2 * Math.PI;
          while (turn >= 2 * Math.PI) turn -= 2 * Math.PI;

          if (turn < smallestTurn) {
            smallestTurn = turn;
            bestEdge = edge;
          }
        }

        if (!bestEdge) break;

        component.push({ x: bestEdge.start.x, y: bestEdge.start.y });
        globalUsed.add(bestEdge.idx);

        prevAngle = Math.atan2(bestEdge.end.y - bestEdge.start.y, bestEdge.end.x - bestEdge.start.x);
        currentKey = `${bestEdge.end.x.toFixed(6)},${bestEdge.end.y.toFixed(6)}`;
        iterations++;

        if (currentKey === startKey && component.length > 2) break;
      } while (iterations < maxIter);

      if (component.length >= 3) {
        components.push(component);
      }
    }

    if (components.length === 0) {
      return createPolygon([], false);
    }

    if (components.length === 1) {
      return createPolygon(components[0], false);
    }

    // Multiple components - connect them with bridges
    let merged = [...components[0]];

    for (let i = 1; i < components.length; i++) {
      const comp = components[i];

      // Find closest edge pair (not just points)
      let minDist = Infinity;
      let bestMergedEdge = { start: 0, end: 1 };
      let bestCompEdge = { start: 0, end: 1 };

      for (let m = 0; m < merged.length; m++) {
        const m2 = (m + 1) % merged.length;
        const mergedEdgeMid = {
          x: (merged[m].x + merged[m2].x) / 2,
          y: (merged[m].y + merged[m2].y) / 2,
        };

        for (let c = 0; c < comp.length; c++) {
          const c2 = (c + 1) % comp.length;
          const compEdgeMid = {
            x: (comp[c].x + comp[c2].x) / 2,
            y: (comp[c].y + comp[c2].y) / 2,
          };

          const d = Math.sqrt((mergedEdgeMid.x - compEdgeMid.x) ** 2 + (mergedEdgeMid.y - compEdgeMid.y) ** 2);

          if (d < minDist) {
            minDist = d;
            bestMergedEdge = { start: m, end: m2 };
            bestCompEdge = { start: c, end: c2 };
          }
        }
      }

      // Remove the bridged edges and connect the open vertices
      // Merged edge: A -> B (remove this edge)
      // Comp edge: C -> D (remove this edge)
      // Result: ...A -> D -> (traverse comp) -> C -> B...

      const result: Point2D[] = [];

      // Add merged points up to A (bestMergedEdge.start)
      for (let j = 0; j <= bestMergedEdge.start; j++) {
        result.push(merged[j]);
      }

      // Connect A to D (component edge end)
      result.push({ ...comp[bestCompEdge.end] });

      // Traverse component from D+1 to C (excluding D and C becomes the last point)
      for (let j = 1; j < comp.length - 1; j++) {
        const idx = (bestCompEdge.end + j) % comp.length;
        result.push({ ...comp[idx] });
      }

      // Add C (component edge start)
      result.push({ ...comp[bestCompEdge.start] });

      // Continue with B and rest of merged (from bestMergedEdge.end onwards)
      for (let j = bestMergedEdge.end; j < merged.length; j++) {
        result.push(merged[j]);
      }

      merged = result;
    }

    return createPolygon(merged, false);
  }

  /**
   * Compute outer contour of multiple polygons by following actual edges
   * For separate polygons, connects them with bridge edges
   */
  private connectSeparatePolygons(polygons: Polygon[]): Polygon {
    if (polygons.length === 0) return createPolygon([], false);
    if (polygons.length === 1) return polygons[0];

    // For separate polygons, we need to connect them with bridges
    // Strategy: trace each polygon's outer boundary and connect with shortest bridges

    interface Edge {
      start: Point2D;
      end: Point2D;
      polyId: number;
    }

    // Collect all edges with their polygon ID
    const edges: Edge[] = [];
    for (let polyId = 0; polyId < polygons.length; polyId++) {
      const poly = polygons[polyId];
      for (let i = 0; i < poly.points.length; i++) {
        edges.push({
          start: poly.points[i],
          end: poly.points[(i + 1) % poly.points.length],
          polyId,
        });
      }
    }

    // Build adjacency: point -> outgoing edges
    const adjacency = new Map<string, Edge[]>();
    edges.forEach((edge) => {
      const key = `${edge.start.x.toFixed(6)},${edge.start.y.toFixed(6)}`;
      if (!adjacency.has(key)) adjacency.set(key, []);
      adjacency.get(key)?.push(edge);
    });

    // Find all separate components by tracing
    const usedEdges = new Set<number>();
    const components: Point2D[][] = [];

    for (let startEdgeIdx = 0; startEdgeIdx < edges.length; startEdgeIdx++) {
      if (usedEdges.has(startEdgeIdx)) continue;

      const startEdge = edges[startEdgeIdx];
      const component: Point2D[] = [];
      let currentKey = `${startEdge.start.x.toFixed(6)},${startEdge.start.y.toFixed(6)}`;
      const startKey = currentKey;
      let prevAngle = Math.PI;
      let iterations = 0;
      const maxIter = edges.length;

      do {
        const outEdges = adjacency.get(currentKey) || [];
        if (outEdges.length === 0) break;

        // Take rightmost turn
        let bestEdge: Edge | null = null;
        let smallestAngle = Infinity;

        for (const edge of outEdges) {
          const edgeIdx = edges.indexOf(edge);
          if (usedEdges.has(edgeIdx) && iterations > 0) continue;

          const angle = Math.atan2(edge.end.y - edge.start.y, edge.end.x - edge.start.x);
          let turnAngle = prevAngle - angle;
          while (turnAngle < 0) turnAngle += 2 * Math.PI;
          while (turnAngle >= 2 * Math.PI) turnAngle -= 2 * Math.PI;

          if (turnAngle < smallestAngle) {
            smallestAngle = turnAngle;
            bestEdge = edge;
          }
        }

        if (!bestEdge) break;

        component.push({ x: bestEdge.start.x, y: bestEdge.start.y });
        usedEdges.add(edges.indexOf(bestEdge));

        prevAngle = Math.atan2(bestEdge.end.y - bestEdge.start.y, bestEdge.end.x - bestEdge.start.x);
        currentKey = `${bestEdge.end.x.toFixed(6)},${bestEdge.end.y.toFixed(6)}`;
        iterations++;

        if (currentKey === startKey && component.length > 2) break;
      } while (iterations < maxIter);

      if (component.length >= 3) {
        components.push(component);
      }
    }

    if (components.length === 0) {
      console.warn('   ⚠️  No components found, returning first polygon');
      return polygons[0];
    }

    if (components.length === 1) {
      return createPolygon(components[0], false);
    }

    // Connect components with bridges (similar to before but with traced boundaries)
    let merged = [...components[0]];

    for (let i = 1; i < components.length; i++) {
      const component = components[i];

      // Find closest points
      let minDist = Infinity;
      let mergedIdx = 0;
      let compIdx = 0;

      for (let mi = 0; mi < merged.length; mi++) {
        for (let ci = 0; ci < component.length; ci++) {
          const dist = Math.sqrt((merged[mi].x - component[ci].x) ** 2 + (merged[mi].y - component[ci].y) ** 2);
          if (dist < minDist) {
            minDist = dist;
            mergedIdx = mi;
            compIdx = ci;
          }
        }
      }

      // Insert component with bridge
      const result: Point2D[] = [];
      for (let j = 0; j <= mergedIdx; j++) result.push(merged[j]);
      result.push({ ...component[compIdx] });
      for (let j = 1; j < component.length; j++) {
        result.push(component[(compIdx + j) % component.length]);
      }
      result.push({ ...component[compIdx] });
      result.push({ ...merged[mergedIdx] });
      for (let j = mergedIdx + 1; j < merged.length; j++) result.push(merged[j]);

      merged = result;
    }

    return createPolygon(merged, false);
  }

  /**
   * Remove polygons that are smaller than the minimum area threshold.
   * Only removes outer polygons (isHole=false), not holes.
   *
   * The threshold scales relative to the largest polygon:
   * - For a 10m² largest polygon, threshold is 0.5m² (5%)
   * - For a 1m² largest polygon, threshold is 0.05m² (5%)
   * - etc.
   *
   * @param polygons Array of polygons to filter
   * @param kwargs Configuration { minAreaM2: number } - base minimum area for 10m² reference
   * @returns Filtered array of polygons
   */
  private removeSmallPolygons(polygons: Polygon[], kwargs?: { minAreaM2?: number }): Polygon[] {
    const baseMinAreaM2 = kwargs?.minAreaM2 ?? 0.5; // Base threshold for 10m² reference
    const referenceAreaM2 = 10.0; // Reference area for base threshold

    // Calculate signed area using shoelace formula (returns area in m²)
    const calculateAreaM2 = (pts: Point2D[]): number => {
      if (pts.length < 3) return 0;
      let areaCm2 = 0;
      for (let k = 0; k < pts.length; k++) {
        const next = (k + 1) % pts.length;
        areaCm2 += pts[k].x * pts[next].y - pts[next].x * pts[k].y;
      }
      areaCm2 = Math.abs(areaCm2 / 2);
      return areaCm2 / 10000; // Convert cm² to m²
    };

    // Find the largest non-hole polygon area
    let largestAreaM2 = 0;
    for (const polygon of polygons) {
      if (!polygon.isHole) {
        const areaM2 = calculateAreaM2(polygon.points);
        if (areaM2 > largestAreaM2) {
          largestAreaM2 = areaM2;
        }
      }
    }

    // Scale threshold relative to largest polygon
    // ratio = baseMinAreaM2 / referenceAreaM2 = 0.5 / 10 = 0.05 (5%)
    const thresholdRatio = baseMinAreaM2 / referenceAreaM2;
    const scaledMinAreaM2 = largestAreaM2 * thresholdRatio;

    return polygons.filter((polygon) => {
      // Only filter out non-holes (outer polygons)
      if (polygon.isHole) {
        return true; // Keep all holes
      }

      const areaM2 = calculateAreaM2(polygon.points);
      return areaM2 >= scaledMinAreaM2;
    });
  }

  /**
   * Fast cavity detection and removal pass
   *
   * Detects and removes cavities by finding pairs of vertices in the polygon
   * that are close together but have other vertices "trapped" between them.
   * These vertices are "welded" together, effectively cutting off the cavity.
   *
   * For cavities larger than the area threshold (default 1 m²), they are extracted
   * as separate polygons instead of being removed.
   *
   * Algorithm:
   * 1. For each pair of non-adjacent vertices (i, j) where j > i + 1
   * 2. Check if they're within weldDistance
   * 3. Calculate area of both options: keeping i->j path vs keeping j->i path
   * 4. If smaller area > threshold, extract it as a separate polygon
   * 5. Otherwise, keep the path that produces larger polygon area
   *
   * @param polygon Input polygon
   * @param kwargs Configuration { weldDistance: number, areaThresholdM2: number }
   * @returns Array of polygons (main polygon plus any extracted large cavities)
   */
  static removeCavities(polygon: Polygon, kwargs?: { weldDistance?: number; areaThresholdM2?: number; maxAreaThresholdM2?: number }): Polygon[] {
    const weldDistance = kwargs?.weldDistance ?? 3.0; // cm in world space
    const areaThresholdM2 = kwargs?.areaThresholdM2 ?? 1.0; // m² - cavities larger than this are kept open

    const points = [...polygon.points];
    const n = points.length;

    if (n < 4) {
      return [polygon];
    }

    const distance = (p1: Point2D, p2: Point2D): number => {
      const dx = p1.x - p2.x;
      const dy = p1.y - p2.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Calculate signed area using shoelace formula (returns area in m²)
    const calculateArea = (pts: Point2D[]): number => {
      if (pts.length < 3) return 0;
      let areaCm2 = 0;
      for (let k = 0; k < pts.length; k++) {
        const next = (k + 1) % pts.length;
        areaCm2 += pts[k].x * pts[next].y - pts[next].x * pts[k].y;
      }
      areaCm2 = Math.abs(areaCm2 / 2);
      return areaCm2 / 10000; // Convert cm² to m²
    };

    let modified = true;
    let iterations = 0;
    const maxIterations = 100; // Prevent infinite loops
    let currentPoints = points;

    while (modified && iterations < maxIterations) {
      modified = false;
      iterations++;
      const len = currentPoints.length;

      if (len < 4) break;

      // Scan for close vertex pairs with vertices between them
      for (let i = 0; i < len; i++) {
        let foundCavity = false;

        // Check all non-adjacent vertices
        for (let j = i + 2; j < len; j++) {
          // Skip if j is the last vertex and i is first (they're adjacent via wrap)
          if (i === 0 && j === len - 1) continue;

          const dist = distance(currentPoints[i], currentPoints[j]);

          if (dist <= weldDistance) {
            // Found a cavity! Need to decide which side to keep
            // Weld point: midpoint of the two close vertices
            const weldPoint: Point2D = {
              x: (currentPoints[i].x + currentPoints[j].x) / 2,
              y: (currentPoints[i].y + currentPoints[j].y) / 2,
            };

            // Option 1: Keep path from 0->weld->j+1->len (remove cavity between i and j)
            const option1: Point2D[] = [];
            for (let k = 0; k < i; k++) option1.push(currentPoints[k]);
            option1.push(weldPoint);
            for (let k = j + 1; k < len; k++) option1.push(currentPoints[k]);

            // Option 2: Keep path going through the cavity (i+1 to j-1, closed by weld)
            const option2: Point2D[] = [];
            option2.push(weldPoint);
            for (let k = i + 1; k < j; k++) option2.push(currentPoints[k]);

            const area1 = calculateArea(option1);
            const area2 = calculateArea(option2);

            // Determine which is the main polygon and which is the cavity
            const isOption1Main = area1 >= area2;
            const mainPolygon = isOption1Main ? option1 : option2;
            const cavityArea = isOption1Main ? area2 : area1;

            // Check cavity size and decide what to do
            if (cavityArea < areaThresholdM2) {
              // Cavity is small enough - close it by welding the points

              currentPoints = mainPolygon;
            } else {
              // Cavity is too large - keep it open (don't weld)

              continue; // Skip this cavity, don't close it
            }

            modified = true;
            foundCavity = true;
            break;
          }
        }

        if (foundCavity) break; // Restart scan with new geometry
      }
    }

    if (iterations >= maxIterations) {
      console.warn(`   ⚠️  Max iterations reached (${maxIterations})`);
    }

    // Return only the main polygon (cavities above threshold are kept open)
    return [createPolygon(currentPoints, polygon.isHole)];
  }

  /**
   * Remove single-point deviations from collinear point chains
   *
   * First finds sequences of at least 3 collinear points. Then within those sequences,
   * removes single points that deviate from the line. Multi-point deviations are kept.
   *
   * @param polygon Input polygon (closed)
   * @param kwargs Configuration with maxDeviationCm (cm), minChainLength
   * @returns Processed polygon
   */
  private removeSinglePointDeviations(polygon: Polygon, kwargs: { maxDeviationCm?: number; minChainLength?: number } = {}): Polygon {
    const maxDeviationCm = kwargs.maxDeviationCm ?? 3.0; // cm - max distance from fitted line
    const minChainLength = kwargs.minChainLength ?? 3; // minimum points to consider a collinear chain

    if (polygon.length < minChainLength + 1) return polygon;

    const points = [...polygon.points];
    const n = points.length;

    // Helper: Calculate distance from point to line defined by two other points
    const distanceToLine = (point: Point2D, lineP1: Point2D, lineP2: Point2D): number => {
      const dx = lineP2.x - lineP1.x;
      const dy = lineP2.y - lineP1.y;
      const lengthSq = dx * dx + dy * dy;

      if (lengthSq < 1e-10) {
        return Math.sqrt((point.x - lineP1.x) ** 2 + (point.y - lineP1.y) ** 2);
      }

      const t = ((point.x - lineP1.x) * dx + (point.y - lineP1.y) * dy) / lengthSq;
      const projX = lineP1.x + t * dx;
      const projY = lineP1.y + t * dy;

      return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
    };

    // Step 1: Find all collinear chains by fitting lines to consecutive point sequences
    const collinearChains: number[][] = [];
    const processed = new Set<number>();

    // Try every possible starting point
    for (let start = 0; start < n; start++) {
      if (processed.has(start)) continue;

      // Try to build a chain starting from this point
      // Use a more flexible approach: find the longest sequence where MOST points are collinear
      const chain = [start];

      // Look ahead to find collinear points, allowing some deviations
      for (let i = 1; i < n && chain.length < n; i++) {
        const nextIdx = (start + i) % n;
        if (processed.has(nextIdx)) break;

        // Fit a line through the current chain endpoints
        const first = points[chain[0]];

        // Check if adding this point keeps most points collinear
        const testChain = [...chain, nextIdx];

        // Count how many points in the test chain are within tolerance
        let withinTolerance = 0;
        for (const idx of testChain) {
          const dist = distanceToLine(points[idx], first, points[nextIdx]);
          if (dist <= maxDeviationCm) {
            withinTolerance++;
          }
        }

        // If at least 70% of points are collinear, keep extending
        if (withinTolerance / testChain.length >= 0.7) {
          chain.push(nextIdx);
        } else {
          // Stop extending this chain
          break;
        }
      }

      // Only keep chains with at least minChainLength points
      if (chain.length >= minChainLength) {
        // Verify the chain is actually reasonably collinear
        const first = points[chain[0]];
        const last = points[chain[chain.length - 1]];

        let onLine = 0;
        for (const idx of chain) {
          if (distanceToLine(points[idx], first, last) <= maxDeviationCm) {
            onLine++;
          }
        }

        // At least 70% should be on the line
        if (onLine / chain.length >= 0.7) {
          collinearChains.push(chain);
          chain.forEach((idx) => processed.add(idx));
        }
      }
    }

    if (collinearChains.length === 0) {
      return polygon;
    }

    // Step 2: Within each chain, find and remove single-point deviations
    const verticesToRemove = new Set<number>();

    for (const chain of collinearChains) {
      const first = points[chain[0]];
      const last = points[chain[chain.length - 1]];

      // Check each point in the chain
      for (let i = 0; i < chain.length; i++) {
        const idx = chain[i];
        const dist = distanceToLine(points[idx], first, last);

        // If this point deviates from the line
        if (dist > maxDeviationCm) {
          // Check if it's an isolated deviation (neighbors are on the line)
          const prevIdx = i > 0 ? chain[i - 1] : chain[chain.length - 1];
          const nextIdx = i < chain.length - 1 ? chain[i + 1] : chain[0];

          const prevDist = distanceToLine(points[prevIdx], first, last);
          const nextDist = distanceToLine(points[nextIdx], first, last);

          // Isolated deviation: both neighbors are on the line
          if (prevDist <= maxDeviationCm && nextDist <= maxDeviationCm) {
            verticesToRemove.add(idx);
          }
        }
      }
    }

    // Remove marked vertices
    const filtered = points.filter((_, idx) => !verticesToRemove.has(idx));

    if (filtered.length < 3) {
      return polygon;
    }

    return createPolygon(filtered, polygon.isHole);
  }

  /**
   * Remove spike artifacts by detecting when edge segments 1 and 4 are aligned
   *
   * Pattern: [before, prev, center, next, after]
   * Segments: seg1=(before->prev), seg2=(prev->center), seg3=(center->next), seg4=(next->after)
   * If seg1 and seg4 are nearly collinear, remove prev, center, next
   *
   * @param polygon Input polygon (closed)
   * @param kwargs Configuration with angleThresholdDegrees
   * @returns Processed polygon with spikes removed
   */
  private removeThreePointDeviations(polygon: Polygon, kwargs: { angleThresholdDegrees?: number; distanceThresholdCm?: number } = {}): Polygon {
    const angleThreshold = kwargs.angleThresholdDegrees ?? 10.0; // degrees - max angle between seg1 and seg4
    const distanceThreshold = kwargs.distanceThresholdCm ?? 5.0; // cm - max distance from line for collinearity

    if (polygon.length < 6) return polygon;

    const points = [...polygon.points];
    const n = points.length;

    // Helper: Calculate distance from point to line
    const distanceToLine = (point: Point2D, lineP1: Point2D, lineP2: Point2D): number => {
      const dx = lineP2.x - lineP1.x;
      const dy = lineP2.y - lineP1.y;
      const lengthSq = dx * dx + dy * dy;

      if (lengthSq < 1e-10) {
        return Math.sqrt((point.x - lineP1.x) ** 2 + (point.y - lineP1.y) ** 2);
      }

      const t = ((point.x - lineP1.x) * dx + (point.y - lineP1.y) * dy) / lengthSq;
      const projX = lineP1.x + t * dx;
      const projY = lineP1.y + t * dy;

      return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
    };

    const verticesToRemove = new Set<number>();

    // Check each point as a potential center of a spike pattern
    for (let centerIdx = 0; centerIdx < n; centerIdx++) {
      if (verticesToRemove.has(centerIdx)) continue;

      // Get the 5-point pattern: [before, prev, center, next, after]
      const beforeIdx = (centerIdx - 2 + n) % n;
      const prevIdx = (centerIdx - 1 + n) % n;
      const nextIdx = (centerIdx + 1) % n;
      const afterIdx = (centerIdx + 2) % n;

      // Skip if any are already marked
      if (verticesToRemove.has(beforeIdx) || verticesToRemove.has(prevIdx) || verticesToRemove.has(nextIdx) || verticesToRemove.has(afterIdx)) {
        continue;
      }

      const before = points[beforeIdx];
      const prev = points[prevIdx];
      const next = points[nextIdx];
      const after = points[afterIdx];

      // Calculate segment vectors
      // Segment 1: before -> prev
      const seg1 = { x: prev.x - before.x, y: prev.y - before.y };
      const seg1Length = Math.sqrt(seg1.x * seg1.x + seg1.y * seg1.y);

      // Segment 4: next -> after
      const seg4 = { x: after.x - next.x, y: after.y - next.y };
      const seg4Length = Math.sqrt(seg4.x * seg4.x + seg4.y * seg4.y);

      // Skip if either segment is too short
      if (seg1Length < 1.0 || seg4Length < 1.0) continue;

      // Normalize the segment vectors
      const seg1Norm = { x: seg1.x / seg1Length, y: seg1.y / seg1Length };
      const seg4Norm = { x: seg4.x / seg4Length, y: seg4.y / seg4Length };

      // Calculate the angle between the two segments using dot product
      const dotProduct = seg1Norm.x * seg4Norm.x + seg1Norm.y * seg4Norm.y;
      const angleRad = Math.acos(Math.max(-1, Math.min(1, dotProduct)));
      const angleDeg = (angleRad * 180) / Math.PI;

      // Check if the segments are nearly aligned (angle close to 0 or 180)
      const isAligned = angleDeg < angleThreshold || angleDeg > 180 - angleThreshold;

      if (isAligned) {
        // Additional check: ensure the segments are collinear (not just parallel)
        // Check both directions and use the minimum distance

        // Direction 1: Check if 'next' and 'after' are close to line through 'before->prev'
        const distNext1 = distanceToLine(next, before, prev);
        const distAfter1 = distanceToLine(after, before, prev);
        const maxDist1 = Math.max(distNext1, distAfter1);

        // Direction 2: Check if 'before' and 'prev' are close to line through 'next->after'
        const distBefore2 = distanceToLine(before, next, after);
        const distPrev2 = distanceToLine(prev, next, after);
        const maxDist2 = Math.max(distBefore2, distPrev2);

        // Use the direction that gives the smaller maximum distance
        const minMaxDist = Math.min(maxDist1, maxDist2);

        const isCollinear = minMaxDist <= distanceThreshold;

        if (isCollinear) {
          // Before removing, check if any prompt point is inside the triangle
          // or if a positive box prompt overlaps it
          const center = points[centerIdx];
          const trianglePoints = [prev, center, next];

          // Check point prompts (positive, label=1) inside the triangle
          // Skip box, vertex, and line prompts - they are handled separately or are visual-only
          const hasPromptInside = this.promptPoints.some((prompt) => {
            if (prompt.type === 'box' || prompt.type === 'vertex' || prompt.label !== 1) return false;
            // Convert prompt from pixel space to world space (cm)
            const promptWorld = {
              x: this.pixelsToCm(prompt.x),
              y: this.pixelsToCm(prompt.y),
            };
            return this.isPointInTriangle(promptWorld, trianglePoints[0], trianglePoints[1], trianglePoints[2]);
          });

          if (hasPromptInside) {
            continue; // Skip removal - prompt point is inside the triangle
          }

          // Check positive box prompts (label=1) for overlap with the triangle
          const hasBoxOverlap = this.promptPoints.some((prompt) => {
            if (prompt.type !== 'box' || prompt.label !== 1 || !prompt.box?.worldCorners) return false;
            // Convert box corners from pixel space to world space (cm)
            const boxCornersWorld = prompt.box.worldCorners.map((c) => ({
              x: this.pixelsToCm(c.x),
              y: this.pixelsToCm(c.y),
            }));
            return this.triangleOverlapsPolygon(trianglePoints, boxCornersWorld);
          });

          if (hasBoxOverlap) {
            continue; // Skip removal - positive box prompt overlaps the triangle
          }

          // Found collinear segments! Mark the 3 middle points for removal
          verticesToRemove.add(prevIdx);
          verticesToRemove.add(centerIdx);
          verticesToRemove.add(nextIdx);
        }
      }
    }

    // Remove marked vertices
    const filtered = points.filter((_, idx) => !verticesToRemove.has(idx));

    if (filtered.length < 3) {
      return polygon;
    }

    return createPolygon(filtered, polygon.isHole);
  }

  /**
   * Remove vertices where a small segment connects to a larger segment at a non-90-degree angle
   *
   * If a small edge is connected to a larger edge and the angle between them is not close to 90 degrees,
   * remove the connecting vertex to simplify the geometry.
   *
   * @param polygon Input polygon (closed)
   * @param kwargs Configuration with smallEdgeThreshold (cm), largeEdgeRatio, angleToleranceDegrees
   * @returns Processed polygon
   */
  private removeNon90SmallEdges(polygon: Polygon, kwargs: { smallEdgeThreshold?: number; largeEdgeRatio?: number; angleToleranceDegrees?: number } = {}): Polygon {
    const smallEdgeThreshold = kwargs.smallEdgeThreshold ?? 20; // cm
    const largeEdgeRatio = kwargs.largeEdgeRatio ?? 2.0; // Large edge must be at least 2x the small edge
    const angleToleranceDegrees = kwargs.angleToleranceDegrees ?? 15; // degrees

    if (polygon.length < 3) return polygon;

    const points = [...polygon.points];
    const n = points.length;
    const angleToleranceRad = (angleToleranceDegrees * Math.PI) / 180;
    const targetAngle = Math.PI / 2; // 90 degrees

    // Helper: Calculate edge length
    const getEdgeLength = (i: number): number => {
      const p1 = points[i];
      const p2 = points[(i + 1) % n];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Helper: Calculate angle at vertex (in radians)
    const getAngleAtVertex = (prev: Point2D, curr: Point2D, next: Point2D): number => {
      const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
      const v2 = { x: next.x - curr.x, y: next.y - curr.y };
      const dot = v1.x * v2.x + v1.y * v2.y;
      const cross = v1.x * v2.y - v1.y * v2.x;
      return Math.atan2(cross, dot);
    };

    const verticesToRemove = new Set<number>();

    // Identify vertices to remove
    for (let i = 0; i < n; i++) {
      const prevIdx = (i - 1 + n) % n;
      const nextIdx = (i + 1) % n;

      const prevEdgeLen = getEdgeLength(prevIdx); // Edge from prevIdx to i
      const nextEdgeLen = getEdgeLength(i); // Edge from i to nextIdx

      // Check if one edge is small and the other is large
      const isSmallPrevLargeNext = prevEdgeLen < smallEdgeThreshold && nextEdgeLen >= prevEdgeLen * largeEdgeRatio;
      const isSmallNextLargePrev = nextEdgeLen < smallEdgeThreshold && prevEdgeLen >= nextEdgeLen * largeEdgeRatio;

      if (isSmallPrevLargeNext || isSmallNextLargePrev) {
        // Calculate angle at this vertex
        const prev = points[prevIdx];
        const curr = points[i];
        const next = points[nextIdx];
        const angle = Math.abs(getAngleAtVertex(prev, curr, next));

        // Check if angle is NOT close to 90 degrees
        const angleDiff = Math.abs(angle - targetAngle);
        const isNot90Degrees = angleDiff > angleToleranceRad;

        if (isNot90Degrees) {
          verticesToRemove.add(i);
        }
      }
    }

    // Remove marked vertices
    const filtered = points.filter((_, idx) => !verticesToRemove.has(idx));

    if (filtered.length < 3) {
      // Don't remove too many vertices
      return polygon;
    }

    return createPolygon(filtered, polygon.isHole);
  }

  /**
   * Remove short edges and rectify geometry by intersecting or creating 90-degree connections
   *
   * Step 1: Identify and remove edges below threshold, mark remainder edges
   * Step 2: For each marked edge, either intersect neighboring edges or slide vertices to create 90-degree connections
   *
   * @param polygon Input polygon (closed)
   * @param kwargs Configuration with edgeLengthThreshold (cm), intersectionDistanceThreshold (cm)
   * @returns Processed polygon
   */
  private removeShortEdgesAndRectify(polygon: Polygon, kwargs: { edgeLengthThreshold?: number; intersectionDistanceThreshold?: number; angleThreshold?: number } = {}): Polygon {
    const edgeLengthThreshold = kwargs.edgeLengthThreshold ?? 10; // cm
    const intersectionDistanceThreshold = kwargs.intersectionDistanceThreshold ?? 10; // cm
    const angleThreshold = kwargs.angleThreshold ?? 15; // degrees

    if (polygon.length < 3) return polygon;

    const points = [...polygon.points];
    const n = points.length;

    // Helper: Calculate edge length
    const getEdgeLength = (i: number, pts: Point2D[]): number => {
      const p1 = pts[i];
      const p2 = pts[(i + 1) % pts.length];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Helper: Calculate line-line intersection
    const lineIntersection = (p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D): Point2D | null => {
      const x1 = p1.x,
        y1 = p1.y;
      const x2 = p2.x,
        y2 = p2.y;
      const x3 = p3.x,
        y3 = p3.y;
      const x4 = p4.x,
        y4 = p4.y;

      const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
      if (Math.abs(denom) < 1e-10) return null; // Parallel lines

      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

      // We don't require intersection to be within segments for now
      // Just return the mathematical intersection point
      return {
        x: x1 + t * (x2 - x1),
        y: y1 + t * (y2 - y1),
      };
    };

    // Helper: Distance between two points
    const distance = (p1: Point2D, p2: Point2D): number => {
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // STEP 1: Identify short edges and mark vertices for removal
    interface EdgeInfo {
      index: number;
      length: number;
      isShort: boolean;
    }

    const edges: EdgeInfo[] = [];
    for (let i = 0; i < n; i++) {
      const length = getEdgeLength(i, points);
      edges.push({
        index: i,
        length,
        isShort: length < edgeLengthThreshold,
      });
    }

    // Determine which vertices belong to long edges or form ~90 degree angles (cannot be removed)
    const belongsToLongEdge = new Set<number>();
    const preservedByAngle = new Set<number>(); // Track vertices preserved due to angle check

    for (let i = 0; i < n; i++) {
      if (!edges[i].isShort) {
        belongsToLongEdge.add(i);
        belongsToLongEdge.add((i + 1) % n);
      }
    }

    // Check for edges that are almost perpendicular - preserve vertices forming such angles
    for (let i = 0; i < n; i++) {
      const prevIdx = (i - 1 + n) % n;
      const nextIdx = (i + 1) % n;

      // Get the two edges meeting at this vertex
      const edge1 = { x: points[i].x - points[prevIdx].x, y: points[i].y - points[prevIdx].y };
      const edge2 = { x: points[nextIdx].x - points[i].x, y: points[nextIdx].y - points[i].y };

      // Normalize the edge vectors
      const len1 = Math.sqrt(edge1.x * edge1.x + edge1.y * edge1.y);
      const len2 = Math.sqrt(edge2.x * edge2.x + edge2.y * edge2.y);

      if (len1 < 1e-10 || len2 < 1e-10) {
        continue; // Skip degenerate edges
      }

      edge1.x /= len1;
      edge1.y /= len1;
      edge2.x /= len2;
      edge2.y /= len2;

      // Calculate the dot product (cosine of angle between edges)
      const dotProduct = edge1.x * edge2.x + edge1.y * edge2.y;

      // Calculate the angle between the edges in degrees
      const angleBetweenEdges = (Math.acos(Math.max(-1, Math.min(1, dotProduct))) * 180) / Math.PI;

      // Check if edges are perpendicular (angle ≈ 90°)
      const perpDiff = Math.abs(angleBetweenEdges - 90);

      if (perpDiff <= angleThreshold) {
        belongsToLongEdge.add(i);
        preservedByAngle.add(i);
      }
    }

    // Remove vertices that don't belong to any long edge
    interface MarkedEdge {
      startIdx: number; // Index in newPoints
      endIdx: number; // Index in newPoints
      wasShortEdge: boolean; // True if this edge replaced a removed short edge
      preservedByAngle: boolean; // True if vertices were preserved due to angle threshold
    }

    const newPoints: Point2D[] = [];
    const oldToNewIndex = new Map<number, number>();
    const markedEdges: MarkedEdge[] = [];

    for (let i = 0; i < n; i++) {
      if (belongsToLongEdge.has(i)) {
        oldToNewIndex.set(i, newPoints.length);
        newPoints.push({ ...points[i] });
      }
    }

    // Build marked edges list
    if (newPoints.length < 3) {
      // If we removed too many vertices, return original polygon
      return polygon;
    }

    // Identify marked edges (edges created after removal or short edges that couldn't be removed)
    for (let i = 0; i < n; i++) {
      if (!belongsToLongEdge.has(i)) continue; // Skip removed vertices

      // Find the next preserved vertex
      let nextIdx = (i + 1) % n;
      while (!belongsToLongEdge.has(nextIdx)) {
        nextIdx = (nextIdx + 1) % n;
      }

      const newI = oldToNewIndex.get(i);
      const newNext = oldToNewIndex.get(nextIdx);
      if (newI === undefined || newNext === undefined) continue;

      // Check if this edge spans across removed vertices or is a short edge
      const originalEdgeIdx = i;
      const spansRemovedVertices = nextIdx !== (i + 1) % n;
      const wasShortEdge = edges[originalEdgeIdx].isShort;

      // Check if both vertices were preserved by angle threshold
      const bothPreservedByAngle = preservedByAngle.has(i) && preservedByAngle.has(nextIdx);

      // Mark edges for rectification if they span removed vertices or are short edges
      // Vertices preserved by angle will be rectified but not removed
      if (spansRemovedVertices || wasShortEdge) {
        markedEdges.push({
          startIdx: newI,
          endIdx: newNext,
          wasShortEdge: wasShortEdge || spansRemovedVertices,
          preservedByAngle: bothPreservedByAngle,
        });
      }
    }

    // STEP 2: Process marked edges - try intersection or create 90-degree connections
    const processedPoints = [...newPoints];
    const m = processedPoints.length;

    for (const markedEdge of markedEdges) {
      const { startIdx, endIdx, preservedByAngle } = markedEdge;

      // Handle case where marked edge connects non-adjacent vertices
      // We need to check if this is an adjacent edge or spans multiple vertices
      const isAdjacent = endIdx === (startIdx + 1) % m;

      if (!isAdjacent) {
        // Skip non-adjacent marked edges for now (complex case)
        continue;
      }

      // Get neighboring edges
      const prevIdx = (startIdx - 1 + m) % m;
      const nextNextIdx = (endIdx + 1) % m;

      // Previous edge: prevIdx -> startIdx
      // Current (marked) edge: startIdx -> endIdx
      // Next edge: endIdx -> nextNextIdx

      const prevPt = processedPoints[prevIdx];
      const startPt = processedPoints[startIdx];
      const endPt = processedPoints[endIdx];
      const nextPt = processedPoints[nextNextIdx];

      // Special handling for edges where vertices were preserved by angle threshold
      // In this case, the short edge should be aligned perpendicular (90°) to the larger neighbors
      if (preservedByAngle) {
        // Apply perpendicular alignment instead of normal rectification
        // This is handled by the existing Case B logic below, so let normal flow continue
        // but skip the intersection case which might weld vertices together
      }

      // Try Case A: Intersection (skip if preservedByAngle to avoid welding vertices)
      if (!preservedByAngle) {
        const intersection = lineIntersection(prevPt, startPt, endPt, nextPt);

        if (intersection) {
          // Check if intersection is close enough to the marked edge endpoints
          const distToStart = distance(intersection, startPt);
          const distToEnd = distance(intersection, endPt);

          if (distToStart < intersectionDistanceThreshold && distToEnd < intersectionDistanceThreshold) {
            // Weld vertices to intersection point
            processedPoints[startIdx] = { ...intersection };
            processedPoints[endIdx] = { ...intersection };
            continue;
          }
        }
      }

      // Case B: Slide vertices along edges to create 90-degree connections relative to neighbors
      // Goal: Make the marked edge perpendicular to its neighboring edges

      // Calculate the center point of the marked edge (should remain roughly fixed)
      const centerPt = {
        x: (startPt.x + endPt.x) / 2,
        y: (startPt.y + endPt.y) / 2,
      };

      // Get the directions of the neighboring edges
      // Previous edge: prevPt -> startPt
      const prevEdgeDir = {
        x: startPt.x - prevPt.x,
        y: startPt.y - prevPt.y,
      };
      const prevEdgeLen = Math.sqrt(prevEdgeDir.x * prevEdgeDir.x + prevEdgeDir.y * prevEdgeDir.y);

      // Next edge: endPt -> nextPt
      const nextEdgeDir = {
        x: nextPt.x - endPt.x,
        y: nextPt.y - endPt.y,
      };
      const nextEdgeLen = Math.sqrt(nextEdgeDir.x * nextEdgeDir.x + nextEdgeDir.y * nextEdgeDir.y);

      if (prevEdgeLen > 1 && nextEdgeLen > 1) {
        // Normalize edge directions
        const prevDir = { x: prevEdgeDir.x / prevEdgeLen, y: prevEdgeDir.y / prevEdgeLen };
        const nextDir = { x: nextEdgeDir.x / nextEdgeLen, y: nextEdgeDir.y / nextEdgeLen };

        // Calculate both perpendicular directions (90° rotations)
        // For a vector (x, y), perpendicular is (-y, x) or (y, -x)
        const prevPerp1 = { x: -prevDir.y, y: prevDir.x };
        const prevPerp2 = { x: prevDir.y, y: -prevDir.x };
        const nextPerp1 = { x: -nextDir.y, y: nextDir.x };
        const nextPerp2 = { x: nextDir.y, y: -nextDir.x };

        // Get current edge direction to choose the perpendicular that's closest
        const currentEdgeDir = {
          x: endPt.x - startPt.x,
          y: endPt.y - startPt.y,
        };
        const currentEdgeLen = Math.sqrt(currentEdgeDir.x * currentEdgeDir.x + currentEdgeDir.y * currentEdgeDir.y);
        const currentDir = { x: currentEdgeDir.x / currentEdgeLen, y: currentEdgeDir.y / currentEdgeLen };

        // Try all 4 combinations and pick the one with optimal direction closest to current edge
        const combinations = [
          { prevPerp: prevPerp1, nextPerp: nextPerp1 },
          { prevPerp: prevPerp1, nextPerp: nextPerp2 },
          { prevPerp: prevPerp2, nextPerp: nextPerp1 },
          { prevPerp: prevPerp2, nextPerp: nextPerp2 },
        ];

        let bestOptimalDir = null;
        let bestDotProduct = -Infinity;

        for (const combo of combinations) {
          const optimalDir = {
            x: combo.prevPerp.x + combo.nextPerp.x,
            y: combo.prevPerp.y + combo.nextPerp.y,
          };
          const optimalLen = Math.sqrt(optimalDir.x * optimalDir.x + optimalDir.y * optimalDir.y);

          if (optimalLen < 0.1) {
            // Perpendiculars are opposite, use just one
            const testDir = combo.prevPerp;
            const dot = testDir.x * currentDir.x + testDir.y * currentDir.y;
            if (dot > bestDotProduct) {
              bestDotProduct = dot;
              bestOptimalDir = testDir;
            }
          } else {
            // Normalize and test
            const normalized = { x: optimalDir.x / optimalLen, y: optimalDir.y / optimalLen };
            const dot = normalized.x * currentDir.x + normalized.y * currentDir.y;
            if (dot > bestDotProduct) {
              bestDotProduct = dot;
              bestOptimalDir = normalized;
            }
          }
        }

        if (!bestOptimalDir) {
          continue;
        }

        const normalizedOptimal = bestOptimalDir;

        // Create a line through centerPt in the optimal direction
        // Line equation: P = centerPt + s * normalizedOptimal

        // Find intersection of this line with the previous edge (prevPt -> startPt extended as infinite line)
        // prevEdge line: P = prevPt + t1 * prevDir
        const intersectionStart = lineIntersection(centerPt, { x: centerPt.x + normalizedOptimal.x, y: centerPt.y + normalizedOptimal.y }, prevPt, startPt);

        // Find intersection with the next edge (endPt -> nextPt extended as infinite line)
        // nextEdge line: P = endPt + t2 * nextDir
        const intersectionEnd = lineIntersection(centerPt, { x: centerPt.x + normalizedOptimal.x, y: centerPt.y + normalizedOptimal.y }, endPt, nextPt);

        if (intersectionStart && intersectionEnd) {
          // Use the intersections directly (allow sliding beyond segment boundaries along the edge line)
          const newStartPt = intersectionStart;
          const newEndPt = intersectionEnd;

          // Check if the solution is reasonable (not too far, not degenerate)
          const distMoved1 = distance(startPt, newStartPt);
          const distMoved2 = distance(endPt, newEndPt);
          const newEdgeLen = distance(newStartPt, newEndPt);

          // More lenient constraint: allow reasonable movement along the edge lines
          const maxMovement = Math.max(prevEdgeLen, nextEdgeLen) * 2; // Allow sliding up to 2x the edge length

          if (distMoved1 < maxMovement && distMoved2 < maxMovement && newEdgeLen > 0.5) {
            processedPoints[startIdx] = newStartPt;
            processedPoints[endIdx] = newEndPt;
          }
        }
      }
    }

    return createPolygon(processedPoints, polygon.isHole);
  }
}
